import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import LaserAreaPicker from "./booking/LaserAreaPicker";
import { ProviderToggle, TimeOfDayFilter, DayStrip, OtherClinicsCard, todBucket, dateMatchesProvider } from "./booking/DayTimePicker";
import { EarliestAnywhereToggle, EarliestAnywherePanel } from "./booking/EarliestAnywhere";
import Any3AreasPicker from "./booking/Any3AreasPicker";
import { isPromoService, groupByMenu, menuCategory, azCompare, sortPromos, splitName, cleanName, KARINE_INJECTABLE_KEYWORDS, TANYA_SERVICE_KEYWORDS, QUICK_FILTERS, isLegacyHidden, loadTreatmentCodes, codeOf, matchesCode, matchesService, providerOf } from "../lib/serviceMenu";
import { MapPin, Calendar, Clock, User, Check, ChevronLeft, ChevronRight, X, Loader2, Search, Sparkles, Flame, TrendingUp, Star, RefreshCw, Zap, Crown, Timer, RotateCcw, Mail, MessageCircle, ExternalLink, Gift, Plus, ChevronDown, ChevronUp, Bell, Droplets, HelpCircle } from "lucide-react";
import DateFallbackHelp from "@/components/DateFallbackHelp";
import BookingHelpStrip from "@/components/BookingHelpStrip";
import SupportEmailButton from "@/components/SupportEmailButton";
import { resolveTanyaVariation } from "@/data/tanyaServiceVariations";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import CrownPointsBanner from "@/components/CrownPointsBanner";
import AddonRecommendations from "@/components/AddonRecommendations";
import NearestSlotsCard from "@/components/NearestSlotsCard";
import LindaCrossSellCard from "@/components/LindaCrossSellCard";
import SaveMySlotButton from "@/components/SaveMySlotButton";
import axios from "axios";
import { toast } from "sonner";
import { errMsg } from "@/utils/errMsg";
import { openContactModal } from "@/components/ContactModal";
import { logBookingError } from "@/lib/bookingErrorTelemetry";
import { providerFromSelectedServices, providerFromServiceName } from "@/lib/providerFromServiceName";
import { menuCategoryInfo } from "@/components/menu/MenuThumb";
import { eminencePhotoForVariation } from "@/lib/eminencePhotos";
import { gaEvent } from "@/utils/ga";
import { reportLanguagePreference } from "@/utils/languagePref";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// Oct 25 2026 · a mistyped phone made Square reject the customer → every
// slot showed "taken". Accept 10 digits, or 11 starting with 1.
const phoneDigitsOk = (v) => {
  const d = (v || "").replace(/\D/g, "");
  return d.length === 10 || (d.length === 11 && d.startsWith("1"));
};

// Cache duration: 2 minutes
const CACHE_TTL_MS = 2 * 60 * 1000;
// Oct 28 2026 · Square (Ottawa · Tanya) — $149 HydraFacial promo + $49 Dermaplaning add-on
const HYDRA_PROMO_VID = "CBJPUSIUVOCTRLX3C7K7YXGN";
const DERMA_ADDON_VID = "OU5POL2HQIHOSACA4YW2LK75";
const MAKEOVER_ADDONS = [
  { label: "Lip Flip", fr: "Lip Flip" },
  { label: "Brow Lift", fr: "Lifting Sourcils" },
  { label: "Smile Lift", fr: "Lifting Sourire" },
];
// Number of days to preload in background
const PRELOAD_DAYS = 7;
// Never hard-reload the page while the visitor is busy somewhere else
// (typing, or inside Lumi / another modal) — it wiped their input.
const userIsBusyElsewhere = () => {
  try {
    const tag = document.activeElement?.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable) return true;
    return !!document.querySelector('[data-testid="lumi-fab-modal"], [role="dialog"][data-state="open"]');
  } catch { return false; }
};

// ── Aggressive localStorage cache for available dates ──────────────────
// Why: backend returns 94 dates in <1s on warm cache, but on cold mobile
// page-load the round trip can stall for 10–20s, leaving users staring at
// skeleton loaders. We persist the last successful date list per location
// (60 min TTL) so on every subsequent visit the dates pop INSTANTLY
// (stale-while-revalidate) and a fresh fetch updates them in the background.
const DATES_LS_PREFIX = "kl_dates_v1_";
const DATES_LS_TTL_MS = 60 * 60 * 1000; // 60 minutes
// ITER 108 (Jul 2026) — include provider in the localStorage cache key
// so Tanya's Tue–Sat Ottawa dates never share storage with Karine's
// Mon+Fri Ottawa dates. Defaults to "karine" so pre-existing cache
// entries (written before this fix) still resolve. Provider is optional
// on the read/write to keep the older call sites (non-Tanya paths) safe.
const datesKey = (locationId, provider) =>
  `${DATES_LS_PREFIX}${(provider || "karine").toLowerCase()}_${locationId}`;
const readCachedDates = (locationId, provider) => {
  try {
    const raw = localStorage.getItem(datesKey(locationId, provider));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.dates) || !parsed.savedAt) return null;
    if (Date.now() - parsed.savedAt > DATES_LS_TTL_MS) return null;
    return parsed.dates;
  } catch { return null; }
};
const writeCachedDates = (locationId, dates, provider) => {
  try {
    if (!locationId || !Array.isArray(dates) || dates.length === 0) return;
    localStorage.setItem(
      datesKey(locationId, provider),
      JSON.stringify({ dates, savedAt: Date.now() })
    );
  } catch { /* localStorage unavailable (private mode / quota) — silent */ }
};

// Same idea for individual-date TIME SLOTS — keeps step 4 (time picker)
// instant on every revisit. Tight 60-second TTL because slots churn fast
// (clients book/cancel by the minute); Square always validates conflicts
// at confirm-time so a slightly stale list never produces a double-booking.
// CRITICAL: empty-slot responses are NEVER cached so a moment of no-
// availability from Square doesn't keep showing forever.
const SLOTS_LS_PREFIX = "kl_slots_v1_";
const SLOTS_LS_TTL_MS = 60 * 1000; // 60 seconds (was 10 minutes)
// ITER 107 — include provider in the cache key so Tanya's empty/karine
// slot list never poisons her booking funnel. Defaults to "karine" so
// existing cached entries (created before this fix) still resolve.
const slotsKey = (locationId, date, duration, provider) =>
  `${SLOTS_LS_PREFIX}${(provider || "karine").toLowerCase()}_${locationId}_${date}_${duration || 0}`;
const readCachedSlots = (locationId, date, duration, provider) => {
  try {
    const raw = localStorage.getItem(slotsKey(locationId, date, duration, provider));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.slots) || !parsed.savedAt) return null;
    if (Date.now() - parsed.savedAt > SLOTS_LS_TTL_MS) return null;
    return parsed.slots;
  } catch { return null; }
};
const writeCachedSlots = (locationId, date, duration, slots, provider) => {
  try {
    if (!locationId || !date || !Array.isArray(slots)) return;
    // CRITICAL: never persist empty-slot responses. A single moment of
    // Square returning [] (due to in-flight bookings, calendar gaps that
    // close, etc.) would otherwise lock the cache to "no slots" for the
    // entire TTL window and prevent the user from ever seeing later
    // openings on that date.
    if (slots.length === 0) return;
    localStorage.setItem(
      slotsKey(locationId, date, duration, provider),
      JSON.stringify({ slots, savedAt: Date.now() })
    );
  } catch { /* localStorage unavailable (private mode / quota) — silent */ }
};

// Skeleton loading components — shapes match the EXACT geometry of their
// real counterparts (same padding, rounded corners, icon space + text
// width) so the visual transition from "loading" to "loaded" is
// imperceptible. Apple-style polish.
const SkeletonTimeSlot = () => (
  <div className="px-3 py-2.5 rounded-lg bg-zinc-800/70 animate-pulse flex items-center justify-center gap-1.5">
    <div className="w-3.5 h-3.5 rounded-full bg-zinc-700/80" />
    <div className="h-3 w-12 bg-zinc-700/80 rounded" />
  </div>
);

const SkeletonDateButton = () => (
  <div className="px-3 py-2.5 rounded-lg bg-zinc-800/70 animate-pulse flex items-center gap-1.5">
    <div className="w-3.5 h-3.5 rounded-full bg-zinc-700/80 flex-shrink-0" />
    <div className="h-3 w-20 bg-zinc-700/80 rounded" />
  </div>
);

const SkeletonServiceItem = () => (
  <div className="flex items-center gap-3 p-2.5 rounded-lg border border-zinc-700/50 bg-zinc-800/30 animate-pulse">
    <div className="flex-1 min-w-0">
      <div className="h-3.5 w-40 bg-zinc-700/80 rounded mb-1.5" />
      <div className="flex items-center gap-3">
        <div className="h-2.5 w-14 bg-zinc-700/60 rounded" />
        <div className="h-2.5 w-10 bg-zinc-700/60 rounded" />
      </div>
    </div>
  </div>
);

const LOCATION_IMAGES = {
  "1": "https://customer-assets.emergentagent.com/job_lauzon-care/artifacts/f3ty3lwt_p2%20sept2022.jpg",
  "2": "https://customer-assets.emergentagent.com/job_8776705b-3965-48ce-aa62-68a5febfba36/artifacts/axpoecd5_image.png",
  "3": "https://customer-assets.emergentagent.com/job_karine-rn-clinic/artifacts/4yp5hs3j_ottawa%20reno%204%202026.jpeg",
};

const LOCATION_ADDRESSES = {
  "1": "163 Bridge Street, Downtown, Carleton Place",
  "2": "320 March Road, Sixth Floor, Clinic 603, Kanata",
  "3": "1390 Prince of Wales Drive, Main Floor, Ottawa",
};

const STEPS = ["location", "service", "date", "time", "info", "review", "confirm"];

// ─────────────────────────────────────────────────────────────────
// ProviderReviewCard — Mar 4 2026 · Karine ask
// Big, friendly "You're booking with" panel at the top of the Review
// step so clients see the provider's name, role, and scope of services
// BEFORE they tap Confirm. Prevents the "I thought Linda did Laser
// Genesis" mix-up that led to Sarah McDonald's misrouted appointment.
// ─────────────────────────────────────────────────────────────────
const PROVIDER_INFO = {
  karine: {
    name: "Karine Lauzon RN",
    initials: "KL",
    role_en: "Registered Nurse · Studio Owner",
    role_fr: "Infirmière autorisée · Propriétaire",
    tone: {
      badge: "bg-amber-500 text-zinc-900",
      ring: "ring-amber-500/40",
      accent: "text-amber-300",
      chip: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    },
    scope_en: [
      "Neuromodulator / Botox",
      "Dermal Fillers (lips · cheeks · jawline · Sculptra)",
      "Skin Boosters + PRP / Exosomes",
      "Full injectable consultations",
    ],
    scope_fr: [
      "Neuromodulateur / Botox",
      "Fillers dermiques (lèvres · joues · mâchoire · Sculptra)",
      "Boosters cutanés + PRP / Exosomes",
      "Consultations injections",
    ],
    schedule_en: "Ottawa · Kanata · Carleton Place",
    schedule_fr: "Ottawa · Kanata · Carleton Place",
  },
  linda: {
    name: "Linda Lafrance NP",
    initials: "LL",
    role_en: "Nurse Practitioner · Ottawa · Thursdays only",
    role_fr: "Infirmière praticienne · Ottawa · les jeudis uniquement",
    tone: {
      badge: "bg-teal-500 text-zinc-900",
      ring: "ring-teal-500/40",
      accent: "text-teal-300",
      chip: "bg-teal-500/15 text-teal-200 border-teal-500/30",
    },
    scope_en: [
      "Botox / Neuromodulator",
      "Dermal Fillers",
      "Sculptra",
      "Injectable Consultations",
    ],
    scope_fr: [
      "Botox / Neuromodulateur",
      "Fillers dermiques",
      "Sculptra",
      "Consultations d'injections",
    ],
    schedule_en: "Ottawa only · Thursdays",
    schedule_fr: "Ottawa seulement · les jeudis",
  },
  tanya: {
    name: "Tanya Jackson",
    initials: "TJ",
    role_en: "Medical Aesthetician · Ottawa · Mondays & Fridays only",
    role_fr: "Esthéticienne médicale · Ottawa · lundis & vendredis uniquement",
    tone: {
      badge: "bg-rose-500 text-zinc-900",
      ring: "ring-rose-500/40",
      accent: "text-rose-300",
      chip: "bg-rose-500/15 text-rose-200 border-rose-500/30",
    },
    scope_en: [
      "Laser Genesis + Lymphatic Facial Drainage",
      "HydraFacial + Éminence Facials",
      "Laser Hair Removal · IPL Photofacial",
      "Chemical Peels · Dermaplaning",
      "Lash Lift & Tint · Brow Lamination",
    ],
    scope_fr: [
      "Laser Genesis + Drainage lymphatique facial",
      "HydraFacial + Soins Éminence",
      "Épilation laser · Photofaciale IPL",
      "Peelings chimiques · Dermaplaning",
      "Rehaussement des cils · Laminage des sourcils",
    ],
    schedule_en: "Ottawa only · Mondays + Fridays",
    schedule_fr: "Ottawa seulement · lundis + vendredis",
  },
  aesthetician: {
    name: "Medical Aesthetician",
    initials: "MA",
    role_en: "Aesthetician team · Ottawa · Mondays & Fridays",
    role_fr: "Équipe d'esthétique · Ottawa · lundis & vendredis",
    tone: {
      badge: "bg-rose-500 text-zinc-900",
      ring: "ring-rose-500/40",
      accent: "text-rose-300",
      chip: "bg-rose-500/15 text-rose-200 border-rose-500/30",
    },
    scope_en: [
      "Laser Genesis + Lymphatic Facial Drainage",
      "HydraFacial + Éminence Facials",
      "Laser Hair Removal · IPL Photofacial",
      "Chemical Peels · Dermaplaning",
      "Lash Lift & Tint · Brow Lamination",
    ],
    scope_fr: [
      "Laser Genesis + Drainage lymphatique facial",
      "HydraFacial + Soins Éminence",
      "Épilation laser · Photofaciale IPL",
      "Peelings chimiques · Dermaplaning",
      "Rehaussement des cils · Laminage des sourcils",
    ],
    schedule_en: "Ottawa only · Mondays + Fridays",
    schedule_fr: "Ottawa seulement · lundis + vendredis",
  },
};

function ProviderReviewCard({ providerSlug, isFr }) {
  const key = (providerSlug || "karine").toLowerCase();
  const info = PROVIDER_INFO[key] || PROVIDER_INFO.karine;
  const tone = info.tone;
  const scope = isFr ? info.scope_fr : info.scope_en;
  const role = isFr ? info.role_fr : info.role_en;
  const schedule = isFr ? info.schedule_fr : info.schedule_en;
  return (
    <div className="p-4 border-b border-zinc-700/50 bg-gradient-to-r from-zinc-900/40 to-zinc-900/10" data-testid="review-provider-card">
      <p className="text-zinc-500 text-[10px] uppercase tracking-[2px] font-black mb-2">
        {isFr ? "Vous réservez avec" : "You're booking with"}
      </p>
      <div className="flex items-start gap-3">
        <div
          className={`w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0 text-lg font-black ring-4 ${tone.badge} ${tone.ring}`}
        >
          {info.initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-[15px] font-black leading-tight">{info.name}</p>
          <p className={`text-xs font-semibold mt-0.5 ${tone.accent}`}>{role}</p>
          <p className="text-zinc-500 text-[11px] mt-0.5">{schedule}</p>
        </div>
      </div>
      <div className="mt-3">
        <p className="text-zinc-400 text-[10px] uppercase tracking-widest font-black mb-1.5">
          {isFr ? "Ce que je fais" : "What I do"}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {scope.map((item) => (
            <span
              key={item}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10.5px] font-semibold border ${tone.chip}`}
            >
              {item}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// Oct 28 2026 · ticket #294006 — the fuzzy matchers in the prefill effect take
// the first service whose name contains the card's words and never check
// price, so "Forehead + 11's · $349" landed on Makeover 6 ($499) at Kanata and
// Makeover 1 ($299) at Ottawa / Carleton Place. When the fuzzy pick's price
// differs from the card's, switch to a service at the card's exact price that
// shares a treatment word with the card (name + areas). If none exists, keep
// the original pick — combo cards intentionally book a single Square service.
const _PRICE_REPICK_GENERIC = new Set([
  "the", "and", "with", "for", "or", "ou", "et", "avec", "makeover", "metamorphose", "métamorphose",
  "botox", "package", "forfait", "combo", "promo", "special", "pricing", "free", "gratuit",
  "lift", "lifting", "face", "visage", "full", "complet", "areas", "area", "zones", "zone",
  "most", "requested", "tx", "plus", "new", "popular", "best", "value", "works", "total",
]);
const _priceNum = (s) => {
  const n = parseFloat(String((s && s.price) ?? "").replace(/[^0-9.]/g, ""));
  return Number.isFinite(n) ? n : null;
};
const _treatmentWords = (...txts) => new Set(
  txts.join(" ").toLowerCase().replace(/['’]/g, "").replace(/[^a-z0-9àâçéèêëîïôûùüÿñæœ ]+/g, " ")
    .split(/\s+/).filter((w) => w.length >= 3 && !/^\d+$/.test(w) && !_PRICE_REPICK_GENERIC.has(w)),
);
function repickByCardPrice(matched, services, pkg) {
  const cardPrice = Number(pkg && pkg.priceOverride);
  if (!matched || !(cardPrice > 0)) return matched;
  const pickedPrice = _priceNum(matched);
  if (pickedPrice == null || pickedPrice === cardPrice) return matched;
  const want = _treatmentWords(pkg.name, pkg.category, pkg.exact_name);
  if (!want.size) return matched;
  let best = null; let bestScore = 0;
  for (const s of services || []) {
    if ((s.name || "").trim().startsWith("-") || _priceNum(s) !== cardPrice) continue;
    const have = _treatmentWords(s.name);
    const score = [...want].filter((w) => have.has(w)).length;
    if (score > bestScore || (score === bestScore && best && (s.name || "").length < (best.name || "").length)) { best = s; bestScore = score; }
  }
  return bestScore >= 1 ? best : matched;
}

const InlineBookingFlow = ({ isOpen, onClose, prefillEmail, prefillLocationId, prefillPackage, prefillProvider, prefillBookingType, prefillSearch, lockLocationId, prefillAddonSlug, prefillPrizeCode, prefillPrizeTitle, prefillQualifyingTreatments }) => {
  const { language } = useLanguage();
  const isFr = language === "fr";

  const [step, setStep] = useState(0);
  // `selectedProvider` is "karine" by default. When booking is launched from
  // Jenny's landing pages or the /jenny route, prefillProvider="jenny" flips
  // this so the cross-provider suggestion engine (and ultimately Jenny's
  // own Square team_member calendar, once wired) knows which calendar to
  // check first.
  //
  // Iter 442 · Sep 21 2026 · Karine — Facebook/email promo links land at
  // `/book?provider=tanya&location=3&service=...` (no parent-provided
  // prefillProvider prop), so we also fall back to the URL query. Without
  // this, /promo/laser-genesis-lymphatic promotions defaulted to Karine
  // and returned "no availability" because Karine's TM isn't assigned to
  // the aesthetician-only catalog variations.
  const urlProvider = useMemo(() => {
    if (typeof window === "undefined") return "";
    try {
      const v = new URLSearchParams(window.location.search).get("provider");
      return (v || "").trim().toLowerCase();
    } catch { return ""; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const effectiveProvider = prefillProvider || urlProvider || "";
  const [selectedProvider, setSelectedProvider] = useState(effectiveProvider || "karine");
  // Tanya (Medical Esthetician) works out of the OTTAWA clinic only — when
  // the flow opens from "Book with Tanya", the location step is restricted
  // to Ottawa (id "3") and a provider pill shows in the header.
  const isTanya = effectiveProvider === "tanya";
  // Iter 468 · Sep 17 2026 · Karine ask — Linda works Ottawa Thursdays
  // only. Mirror the Tanya location-lock so the booking flow collapses
  // the 3-city picker to the single Ottawa button when the client is
  // booking with Linda (from `/book?provider=linda` or the studio
  // page's "Book with Linda" CTA). Same downstream effect as
  // `isLocationLocked`.
  const isLinda = effectiveProvider === "linda";
  // Treatment-driven location lock. HydraFacial, RF Microneedling, Crown
  // Laser and Skincare are only offered at our Ottawa clinic, so pages like
  // /laser-and-skin open the flow with `lockLocationId="3"`, restricting the
  // picker to Ottawa exactly like the Tanya flow. Lives next to `isTanya` so
  // every downstream check stays in one place.
  //
  // Iter 435 · Sep 11 2026 · Karine — when a client arrives from the
  // Facebook/email promo (`/book?location=3&service=...`) but no lock prop
  // was passed by the parent, treat the URL param as an equivalent lock
  // signal. Same effect: collapses the "Book Direct — Square" banner to
  // the single Ottawa button, hides the 2 other clinics from the picker,
  // and lets us auto-advance past the location step. Otherwise clients
  // land on step 1 and see all 3 clinics for an Ottawa-only promo.
  const urlLocationLock = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = new URLSearchParams(window.location.search).get("location");
      const id = raw ? String(raw) : null;
      return (id && ["1", "2", "3"].includes(id)) ? id : null;
    } catch { return null; }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const isLocationLocked = Boolean(lockLocationId) || Boolean(urlLocationLock) || isLinda;
  const lockedLocationId = lockLocationId || urlLocationLock || (isTanya || isLinda ? "3" : null);
  // Re-sync when the modal opens with a freshly-changed prefillProvider —
  // useState initializer only runs on mount, so if /book?provider=jenny
  // sets the prop after the component mounted (it always does, since the
  // modal lives at the top of the tree), we'd otherwise be stuck at the
  // initial value.
  useEffect(() => {
    // Sync `selectedProvider` state whenever the modal opens with a
    // freshly-changed prefillProvider prop. This must fire for EVERY
    // provider (tanya, karine, jenny, …) so the provider-scoped date
    // cache (`kl_dates_v1_<provider>_<loc>`) + snapshot lookups
    // (`window.__KL_BOOKING_DATA.providers.tanya.locations[...]`) route
    // to the correct calendar. Previously only `karine` was synced,
    // which stranded the Tanya funnel on Karine's Mon+Fri Ottawa cache.
    //
    // Iter 442 · Sep 21 2026 · fall back to the `?provider=` URL param
    // when the parent didn't pass a prefillProvider prop — this is how
    // /promo/laser-genesis-lymphatic reaches the booking flow.
    const p = prefillProvider || urlProvider;
    if (p) {
      setSelectedProvider(p);
    }
  }, [prefillProvider, urlProvider, isOpen]);

  // Global flag so unrelated overlays (WelcomeRewardsPopup, Crown Reserve
  // signup nudges, etc.) can suppress themselves while the booking modal
  // is open. Prevents them from covering the Fast Track chooser mid-flow.
  // See RCA note in `WelcomeRewardsPopup.jsx` (Jul 3, 2026).
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isOpen) {
      window.__klrnBookingOpen = true;
    } else {
      window.__klrnBookingOpen = false;
    }
    return () => {
      window.__klrnBookingOpen = false;
    };
  }, [isOpen]);
  // ── "Booking with Tanya · Ottawa" flash banner ─────────────────────
  // When the modal opens from one of Tanya's landing pages (Laser & Skin,
  // RF Microneedling, HydraFacial, etc.), a soft rose banner slides in
  // at the top of the modal body for ~5 seconds to reassure the client
  // they're in the right provider's flow. Auto-dismisses afterwards so
  // it doesn't crowd the steps. Added Jun 29 2026 per Karine's request.
  const [showAnnaIntro, setShowAnnaIntro] = useState(false);
  const [showKarineIntro, setShowKarineIntro] = useState(false);
  useEffect(() => {
    if (isOpen && isTanya) {
      setShowAnnaIntro(true);
      const t = setTimeout(() => setShowAnnaIntro(false), 5000);
      return () => clearTimeout(t);
    }
    setShowAnnaIntro(false);
  }, [isOpen, isTanya]);
  // Mirror banner for Karine — fires when the flow is explicitly opened
  // with prefillProvider="karine" (Dermal Fillers, Sculptra, Botox pages,
  // etc.). Amber/gold to match Karine's brand vs. Tanya's rose.
  const isKarine = prefillProvider === "karine";
  useEffect(() => {
    if (isOpen && isKarine) {
      setShowKarineIntro(true);
      const t = setTimeout(() => setShowKarineIntro(false), 5000);
      return () => clearTimeout(t);
    }
    setShowKarineIntro(false);
  }, [isOpen, isKarine]);
  // Cross-provider suggestion (e.g. "Karine is full — Jenny has Monday at 11am")
  const [altSuggestion, setAltSuggestion] = useState(null);
  // Top 3 alternative slots — rendered as a small picker beneath the
  // primary suggestion so the patient can pick the time that suits them
  // best instead of just the earliest.
  const [altSuggestionList, setAltSuggestionList] = useState([]);
  const [altSuggestionLoading, setAltSuggestionLoading] = useState(false);
  // ─── Tanya's Friday Jun 19 Free Facial Lymphatic Drainage event ───
  // Surfaced as a thin banner above the Book-Direct strip on every
  // step of the booking flow. Auto-hides when 0 spots remain or the
  // event day has passed. Backend computes the dynamic spot count.
  const [annaEvent, setAnnaEvent] = useState(null);
  // Notify-me signup popover state (phone + name → SMS when a spot opens)
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyForm, setNotifyForm] = useState({ name: "", phone: "" });
  const [notifyBusy, setNotifyBusy] = useState(false);
  const [notifyDone, setNotifyDone] = useState(false);
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/lymphatic-drainage-jun19/availability`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (cancelled) return;
        if (d && d.active && (d.remaining || 0) > 0) setAnnaEvent(d);
        else setAnnaEvent(null);
      })
      .catch(() => { if (!cancelled) setAnnaEvent(null); });
    return () => { cancelled = true; };
  }, [isOpen]);
  const submitNotifySignup = async () => {
    if (!notifyForm.name.trim() || !notifyForm.phone.trim()) {
      toast.error(isFr ? "Nom et téléphone requis" : "Name and phone are required");
      return;
    }
    setNotifyBusy(true);
    try {
      const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/events/lymphatic-drainage-jun19/notify-signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(notifyForm),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.detail || (isFr ? "Inscription échouée" : "Sign-up failed"));
      }
      setNotifyDone(true);
      toast.success(isFr ? "Inscrit·e — on vous textera dès qu'une place se libère." : "You're in — we'll text you as soon as a spot opens.");
    } catch (e) {
      toast.error(typeof e === "string" ? e : (e.message || "Sign-up failed"));
    } finally {
      setNotifyBusy(false);
    }
  };
  // One-Tap Rebook — last completed booking for this email so the user
  // can re-create their previous visit in a single tap.
  const [lastBooking, setLastBooking] = useState(null);
  const [lastBookingDismissed, setLastBookingDismissed] = useState(false);

  const [selectedLocation, setSelectedLocation] = useState(null);
  // Iter 409 · Feb 26 · Marc: "clients still pick the wrong location —
  // make it almost impossible". Layer 1 = a confirmation modal that
  // fires between the "Book Appointment" tap and handleBook(). The
  // user has to physically tap "Yes, book at <Location>" before the
  // POST goes through. No accidental Enter-key or scroll-past commits.
  const [showLocationConfirm, setShowLocationConfirm] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]); // array — supports stacking multiple treatments in one appointment
  // Iter 459 · Feb 2026 · Karine — auto-switch `selectedProvider` based
  // on the services in the cart.
  //
  // Iter 477.2 · Sep 19 2026 · Karine escalation (Oriana Laderoute):
  // moved the detection logic into `providerFromSelectedServices()`
  // (see `lib/providerFromServiceName.js`) so it also catches Square
  // catalog services whose NAME contains "Tanya" / "Linda" — not just
  // services carrying the `ottawa_only` flag. The helper is the same
  // one used by ClientPortal Quick Rebook, so both flows route
  // identically. Mirror of the backend's forbidden-keyword safety net.
  useEffect(() => {
    if (!selectedServices || selectedServices.length === 0) return;
    gaEvent("book_click", { treatments: selectedServices.map((s) => s.name).join(" + ").slice(0, 100) });
  }, [selectedServices]);
  useEffect(() => {
    if (!selectedServices || selectedServices.length === 0) return;
    const detected = providerFromSelectedServices(selectedServices);
    if (detected === "linda" && selectedProvider !== "linda") {
      setSelectedProvider("linda");
      return;
    }
    if (detected === "tanya" && selectedProvider !== "tanya") {
      setSelectedProvider("tanya");
      return;
    }
    // No aesthetician-only signals AND state is still "tanya"/"linda"
    // from a stale click → reset to Karine (unless the URL locked it).
    if (
      detected === null
      && selectedProvider !== "karine"
      && !isTanya
      && !isLinda
    ) {
      setSelectedProvider("karine");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedServices, selectedProvider, isTanya, isLinda]);

  const [catalogServices, setCatalogServices] = useState([]);
  const [serviceSearch, setServiceSearch] = useState("");
  const [availableDates, setAvailableDates] = useState([]);
  const availableDatesRef = useRef([]);
  availableDatesRef.current = availableDates;
  const datesKeyRef = useRef("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [providerView, setProviderView] = useState("any");
  const [todFilter, setTodFilter] = useState("all");
  const [showAllMonths, setShowAllMonths] = useState(false);
  const [datesRefreshing, setDatesRefreshing] = useState(false);
  const contentScrollRef = useRef(null);
  useEffect(() => { if (contentScrollRef.current) contentScrollRef.current.scrollTop = 0; }, [step]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  // Mar 4 2026 · Karine escalation (Josee Bellerive FB DM) — the
  // availability endpoint reports slots as "open" that Square then
  // rejects at book-time because another team member's overlapping
  // appointment holds the same physical resource. Old handler sent
  // Josee back to the SAME broken slot which she re-tapped and looped.
  // Fix: locally blacklist every slot Square rejects with 409 so the
  // time-grid greys it out permanently for the rest of this session.
  const [deadSlots, setDeadSlots] = useState(() => new Set()); // Set<`${date}T${time}`>
  // Mar 4 2026 · Josee Bellerive follow-up — after a 409 we compute
  // the next real opening on the same date and surface it as a big
  // amber "tap to lock" badge above the grid. Clears on manual
  // reselection or step change so it never lingers.
  const [suggestedSlot, setSuggestedSlot] = useState(null); // { date, time }
  const [customerInfo, setCustomerInfo] = useState({ first_name: "", last_name: "", email: "", phone: "", send_reminders: true });
  // Afterpay opt-in on the confirmation step. null=offered · "busy"=POSTing
  // request · "done"=Karine notified, Square invoice will follow.
  const [afterpayOptIn, setAfterpayOptIn] = useState(null);
  const [loading, setLoading] = useState(false);
  // Oct 21 2026 · dedicated flag — the shared `loading` was being flipped
  // off by the dates/snapshot effects while the catalog was still loading,
  // so clients saw "No services available" + Book-direct-on-Square.
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  // Oct 15 2026 · Smart Redirect — provider off-schedule alternatives from the 409
  const [offScheduleAlts, setOffScheduleAlts] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [showFooterDirect, setShowFooterDirect] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);
  const [packageInfo, setPackageInfo] = useState(null); // Store prefilled package info
  const [addAnotherAnswered, setAddAnotherAnswered] = useState(false); // Oct 26 2026 · prefill "add another? yes/no" shown once
  // Oct 28 2026 · Karine — one-tap "Add Dermaplaning for $49?" upsell right
  // after the $149 HydraFacial promo is picked (Tanya · Ottawa).
  const [dermaUpsellDismissed, setDermaUpsellDismissed] = useState(false);
  // Nov 2026 · Any 3 Areas $99 — optional area choice stamped into the Square note for Tanya.
  const [any3Areas, setAny3Areas] = useState([]);
  const any3PromoPicked = selectedServices.some((s) => /any 3 areas/i.test(s?.name || ""));
  const hydraPromoPicked = selectedServices.some((s) => s?.variation_id === HYDRA_PROMO_VID || /^PROMO · HydraFacial/i.test(s?.name || ""));
  const dermaAddOnPicked = selectedServices.some((s) => s?.variation_id === DERMA_ADDON_VID || /Dermaplaning with HydraFacial Promo/i.test(s?.name || ""));
  useEffect(() => { if (!hydraPromoPicked) setDermaUpsellDismissed(false); }, [hydraPromoPicked]);
  const addDermaAddOn = () => {
    const fromCatalog = (catalogServices || []).find((s) => s.variation_id === DERMA_ADDON_VID);
    const addon = fromCatalog || { name: "Add-On · Dermaplaning with HydraFacial Promo — $49 +tx", variation_id: DERMA_ADDON_VID, price: "$49", duration_minutes: 15, ottawa_only: true, category: "Hydrafacial & Facials" };
    setSelectedServices((prev) => (prev.some((s) => s.variation_id === DERMA_ADDON_VID) ? prev : [...prev, addon]));
    setDermaUpsellDismissed(true);
    try { toast(isFr ? "Dermaplaning ajouté (+49 $)" : "Dermaplaning added (+$49)", { duration: 2500, position: "top-center" }); } catch (_) {}
  };
  // Oct 29 2026 · Karine — after any Makeover: "Add a Lip Flip, Brow Lift or Smile Lift for $79?"
  // Square items: "<Area> (Botox) — $79 +tx Promo · reg $125" in every clinic. Areas already
  // inside the chosen Makeover's name are not offered again.
  const [makeoverUpsellDismissed, setMakeoverUpsellDismissed] = useState(false);
  const makeoverPicked = selectedServices.some((s) => /^Makeover\s*\d/i.test(s?.name || ""));
  const addOn79Picked = selectedServices.some((s) => /\(Botox\) — \$79/i.test(s?.name || ""));
  const makeoverAddOnOptions = MAKEOVER_ADDONS.filter((o) => !selectedServices.some((s) => new RegExp(o.label, "i").test(s?.name || "")));
  useEffect(() => { if (!makeoverPicked) setMakeoverUpsellDismissed(false); }, [makeoverPicked]);
  const addMakeoverAddOn = (opt) => {
    const svc = (catalogServices || []).find((s) => new RegExp(`^${opt.label} \\(Botox\\) — \\$79`, "i").test(s?.name || ""));
    if (!svc) { try { toast(isFr ? "Non disponible à cette clinique" : "Not available at this clinic", { duration: 2500 }); } catch (_) {} setMakeoverUpsellDismissed(true); return; }
    setSelectedServices((prev) => (prev.some((s) => s.variation_id === svc.variation_id) ? prev : [...prev, svc]));
    setMakeoverUpsellDismissed(true);
    try { toast(isFr ? `${opt.fr} ajouté (+79 $)` : `${opt.label} added (+$79)`, { duration: 2500, position: "top-center" }); } catch (_) {}
  };
  const showMakeoverUpsell = makeoverPicked && !addOn79Picked && !makeoverUpsellDismissed && makeoverAddOnOptions.length > 0;
  // Iter 353b · Feb 13 2026 · Booking concierge fallback (Karine ask —
  // "same as LMC notification"). After 2 consecutive failures we fire
  // a team alert AND surface a "we've got you" banner to the client.
  const [bookFailCount, setBookFailCount] = useState(0);
  const [conciergeNotified, setConciergeNotified] = useState(false);

  // === ADD-ON UPSELL STATE (step 5 enhancement carousel) ===
  // Fetched once from /api/addons (cached on the client). Filtered by the
  // selected service categories so we only show enhancements that actually
  // pair with what the client is booking. Each "+ Add" tap fires an
  // /api/inquiries record so Karine/Tanya can text the client back to slot
  // it in physically during the visit.
  const [addonCatalog, setAddonCatalog] = useState(null);
  const [addonsAdded, setAddonsAdded] = useState({}); // slug -> "saving" | "sent"

  // Feb 2026 · Website→Square catalog mapping. Loaded once on mount
  // so any package prefill carrying a `service_key` resolves to the
  // real priced Square variation. This is the fix for the "$0.00 in
  // Square" bug — without it, package bookings landed as unpriced
  // appointment segments and Karine had to hand-price at checkout.
  const [serviceMappings, setServiceMappings] = useState({});

  // === BOOKING ENHANCEMENTS STATE ===
  // #1 Fastest Available Slot
  const [findingFastest, setFindingFastest] = useState(false);
  // Top-3 earliest slot chooser (Fast Track results)
  const [fastTrackOptions, setFastTrackOptions] = useState(null);
  // Oct 21 2026 · Date step: "My clinic" vs "Earliest anywhere"
  const [dateMode, setDateMode] = useState("clinic");
  const [fastTrackProgress, setFastTrackProgress] = useState(null);
  const [fastTrackCallId, setFastTrackCallId] = useState(null);  // analytics correlation
  // "Notify me when sooner opens" opt-in form state
  const [showSoonerForm, setShowSoonerForm] = useState(false);
  const [soonerWatch, setSoonerWatch] = useState({ first_name: "", email: "", phone: "" });
  const [soonerSaving, setSoonerSaving] = useState(false);
  const [soonerSaved, setSoonerSaved] = useState(false);
  // Post-booking "Notify me of earlier openings" opt-in (step 6 confirmation card)
  const [earlierAlertSaving, setEarlierAlertSaving] = useState(false);
  const [earlierAlertSaved, setEarlierAlertSaved] = useState(false);
  const [earlierAlertError, setEarlierAlertError] = useState(null);
  // ─── Professional Referral code (step 4) ──────────────────────────
  const [proReferralCode, setProReferralCode] = useState("");
  const [proReferralValidating, setProReferralValidating] = useState(false);
  const [proReferralInfo, setProReferralInfo] = useState(null);    // {code, referrer_display, client_discount}
  const [proReferralError, setProReferralError] = useState(null);

  // Auto-pickup ?ref=KL-XXXX from URL (shared via /share/:code links).
  // Stash to sessionStorage so refreshes during checkout don't lose it.
  // Karine ask (Feb 28 2026): read `?addons=key1,key2` from the Éminence
  // menu so the confirmation screen — and the email that follows — can
  // echo the exact upgrades the client picked. Keys / prices are the
  // same 5 add-ons defined in `pages/EminenceFacialsPage.jsx`.
  const ADDON_CATALOG = useMemo(() => ({
    "lip-renewal":        { en: "Éminence Lip Renewal",       fr: "Renouveau des Lèvres",          price: 29, number: 19 },
    "neck-decollete":     { en: "Neck & Décolleté Renewal",   fr: "Renouveau Cou & Décolleté",     price: 49, number: 20 },
    "hand-renewal":       { en: "Hand Renewal Treatment",     fr: "Traitement Renouveau des Mains", price: 39, number: 21 },
    "extended-extractions": { en: "Extended Extractions",     fr: "Extractions Prolongées",        price: 30, number: 22 },
    "led-light-therapy":  { en: "LED Light Therapy",          fr: "Luminothérapie DEL",            price: 49, number: 23 },
  }), []);
  const [selectedAddOnsFromUrl, setSelectedAddOnsFromUrl] = useState([]);
  useEffect(() => {
    try {
      const raw = new URLSearchParams(window.location.search).get("addons") || "";
      const keys = raw.split(",").map((k) => k.trim()).filter((k) => ADDON_CATALOG[k]);
      setSelectedAddOnsFromUrl(keys);
    } catch { /* url not readable */ }
  }, [ADDON_CATALOG]);
  const addOnsInBooking = selectedAddOnsFromUrl.map((k) => ({ key: k, ...ADDON_CATALOG[k] }));
  const addOnsTotal = addOnsInBooking.reduce((s, a) => s + a.price, 0);

  useEffect(() => {
    if (proReferralInfo || proReferralCode) return;
    let urlCode = null;
    try {
      const sp = new URLSearchParams(window.location.search);
      urlCode = sp.get("ref") || sp.get("referral_code") || null;
      if (!urlCode) {
        urlCode = sessionStorage.getItem("kl_pro_ref") || null;
      }
    } catch {}
    if (!urlCode) return;
    const norm = String(urlCode).toUpperCase().trim();
    if (!norm) return;
    sessionStorage.setItem("kl_pro_ref", norm);
    setProReferralCode(norm);
    // Auto-validate so step 4 already shows the discount applied
    axios.get(`${API}/referrals/professional/validate-code`, { params: { code: norm } })
      .then((res) => {
        if (res.data?.ok) {
          setProReferralInfo({
            code: res.data.code,
            referrer_display: res.data.referrer_display,
            client_discount: res.data.client_discount,
          });
        }
      })
      .catch(() => { /* silent — user can still re-enter */ });
    // eslint-disable-next-line
  }, []);

  // Iter 410 · Feb 26 2026 · Pre-select clinic from URL query.
  // The kiosk aftercare rebook link deep-links here with
  // `?location=<id>` so the client doesn't have to re-pick their
  // clinic — the 3-layer confirmation guardrail then double-checks
  // before the booking commits.
  useEffect(() => {
    if (selectedLocation) return; // don't overwrite an active selection
    try {
      const sp = new URLSearchParams(window.location.search);
      const raw = sp.get("location");
      const id = raw ? String(raw) : null;
      // Iter 435 · Sep 11 2026 · Karine fix — the location picker was still
      // rendering for promos with ?location=3 because we were storing a
      // plain string here while the rest of the flow expects an object
      // with `.id` and `.name`. Deep-linked promos (Laser Genesis +
      // Lymphatic Drain, Glow Reset HydraFacial, kiosk aftercare rebook)
      // now truly skip the location step and drop the client straight on
      // the days/times step.
      if (id && LOCATION_NAMES[id]) {
        setSelectedLocation({ id, name: LOCATION_NAMES[id] });
        // Auto-advance out of step 0 so clients don't see the (now
        // single-clinic) location picker at all — the whole promo funnel
        // is designed as "1 tap → pick a day/time".
        setStep((s) => (s === 0 ? 1 : s));
      }
    } catch {}
    // eslint-disable-next-line
  }, []);
  // Claim flow — if the URL has ?claim=<watcher_id>, the client arrived from
  // a "your earlier slot just opened" email and the booking they're about to
  // create should auto-cancel their original Square appointment + award +25 pts.
  const claimWatcherId = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const u = new URL(window.location.href);
      return (u.searchParams.get("claim") || "").trim() || null;
    } catch { return null; }
  }, []);
  const [claimResult, setClaimResult] = useState(null);  // {cancelled_booking_id, points_awarded, already_claimed}
  // #2 Slot hold (10 min countdown)
  const [holdToken, setHoldToken] = useState(null);
  const [holdSecondsLeft, setHoldSecondsLeft] = useState(null);
  // #4 Returning client lookup
  const [rebookEmail, setRebookEmail] = useState("");
  const [rebookLoading, setRebookLoading] = useState(false);
  const [rebookProfile, setRebookProfile] = useState(null);
  // #6 Crown Reserve upsell
  const [clientProfile, setClientProfile] = useState(null);

  // Switch to the alternate provider's suggested slot. Shared between the
  // primary "Switch to Jenny →" button and the smaller "Or pick another
  // date" chips so the behavior stays identical.
  // Oct 15 2026 · Smart Redirect — one tap moves the client to the
  // provider's next real opening (clinic + date + time), back at step 3
  // so they can confirm.
  const jumpToAlternative = (alt) => {
    if (!alt) return;
    setOffScheduleAlts(null);
    setError(null);
    if (alt.provider) setSelectedProvider(alt.provider);
    if (alt.location_id && alt.location_id !== selectedLocation?.id) {
      setSelectedLocation({ id: alt.location_id, name: alt.location_name || LOCATION_NAMES[alt.location_id] || alt.location_id });
    }
    const existing = (availableDates || []).find(d => d.date === alt.date);
    setSelectedDate(existing || { date: alt.date, available: true });
    setSelectedTime(alt.start_time);
    setRefreshKey(k => k + 1);
    setStep(3);
    try {
      setTimeout(() => {
        const el = document.querySelector('[data-testid="time-picker"]') || document.querySelector('[data-testid="booking-step-3"]');
        if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    } catch { /* noop */ }
  };

  const switchToAltProvider = (alt) => {
    if (!alt) return;
    setSelectedProvider(alt.provider_slug);
    // If the alt is at a different location (Karine→Jenny always lands
    // at Ottawa), update the selected location so the next slot query
    // hits the right calendar.
    if (alt.location_id && alt.location_id !== selectedLocation?.id) {
      const newLoc = (availableDates?.[0]?.locations || []).find(l => l.id === alt.location_id);
      if (newLoc) setSelectedLocation(newLoc);
    }
    // Jump to the suggested date if we already have it in our list.
    const targetDate = (availableDates || []).find(d => d.date === alt.date);
    if (targetDate) setSelectedDate(targetDate);
    setAltSuggestion(null);
    setAltSuggestionList([]);
    setRefreshKey(k => k + 1);
  };

  // 15-min point-redemption hold — surfaces the credit code in Step 5 review
  // and auto-injects it into bookingNotes so Karine sees it at Square checkout.
  const [pendingHold, setPendingHold] = useState(null);

  // ITER 109 — Crown Points booking-funnel banner. Tracks the redemption
  // option the client picked in <CrownPointsBanner/> at step 5. We DON'T
  // burn points here — actual /api/client/redeem call happens only when
  // they tap "Confirm" so abandoning the funnel never costs them points.
  const [selectedRedemption, setSelectedRedemption] = useState(null);

  // ITER 111 — "Pairs well with" add-on recommendations shown at step 5.
  // Selected add-ons get inlined into the booking notes so reception
  // sees them at checkout and can ring them up via Tanya's SessionCart.
  const [selectedRecAddons, setSelectedRecAddons] = useState([]);
  
  // === AVAILABILITY CACHE ===
  // Format: { "locationId_date_duration": { data: [...], timestamp: Date.now() } }
  const availabilityCacheRef = useRef({});
  const [preloadingDates, setPreloadingDates] = useState(new Set()); // Dates currently being preloaded
  const [cachedDatesCount, setCachedDatesCount] = useState(0); // For UI feedback
  // Tracks elapsed loading time so we can show an empathetic message and an
  // escape-hatch "Refresh" button when Square is slow. Resets each loading cycle.
  const [loadingElapsed, setLoadingElapsed] = useState(0);
  useEffect(() => {
    if (!loading) {
      setLoadingElapsed(0);
      return;
    }
    const start = Date.now();
    setLoadingElapsed(0);
    const id = setInterval(() => {
      setLoadingElapsed(Math.floor((Date.now() - start) / 1000));
    }, 1000);
    return () => clearInterval(id);
  }, [loading]);

  // === MULTI-TREATMENT HELPERS ===
  // Sum durations across every treatment the client adds to the cart so the
  // availability search reserves the full chained block in Square.
  const totalDurationMinutes = useMemo(
    () => selectedServices.reduce((sum, s) => sum + (s?.duration_minutes || 0), 0),
    [selectedServices]
  );
  // Sum prices (numeric portion only) for display.
  const totalPriceCents = useMemo(
    () => selectedServices.reduce((sum, s) => {
      const n = parseFloat(String(s?.price || "").replace(/[^0-9.]/g, ""));
      return sum + (Number.isFinite(n) ? Math.round(n * 100) : 0);
    }, 0),
    [selectedServices]
  );
  // First service is shown as the headline in availability text & emails.
  const primaryService = selectedServices[0] || null;
  const isServiceSelected = useCallback(
    (svc) => selectedServices.some((s) => s.variation_id === svc?.variation_id),
    [selectedServices]
  );

  // ─── EMERGENCY LOADING WATCHDOG ───────────────────────────────────────
  // No matter what fetch is in flight or how it fails, `loading` is
  // GUARANTEED to clear after 10 seconds. Prevents the "stuck spinner
  // forever" symptom by making it impossible to stay in a loading state
  // for more than 10 seconds, period. Re-arms every time loading goes
  // from false → true.
  useEffect(() => {
    if (!loading) return undefined;
    const id = setTimeout(() => {
      setLoading(false);
    }, 10000);
    return () => clearTimeout(id);
  }, [loading]);


  const toggleService = useCallback((svc) => {
    // Iter 452 · Feb 2026 · Ottawa-Only Chip Sweep — location guard.
    // If a client on Kanata/Carleton Place taps a Tanya-exclusive
    // service (svc.ottawa_only), auto-switch the selected location to
    // Ottawa before adding it — otherwise Square rejects the booking
    // and the client bounces. Mirrors the existing add-on notice.
    if (svc && svc.ottawa_only && selectedLocation && selectedLocation.id !== "3") {
      setSelectedLocation({ id: "3", name: "Ottawa" });
      try {
        toast(
          isFr
            ? "Ce traitement est offert à Ottawa uniquement — basculé vers Ottawa."
            : "This treatment is offered at our Ottawa clinic only — switched to Ottawa.",
          { duration: 5000 }
        );
      } catch (_) { /* toast optional */ }
    }
    let didAdd = false;
    setSelectedServices((prev) => {
      const idx = prev.findIndex((s) => s.variation_id === svc.variation_id);
      if (idx >= 0) {
        return prev.filter((_, i) => i !== idx);
      }
      didAdd = true;
      return [...prev, svc];
    });
    // Iter 454 · Feb 2026 · Karine ask (novice-friendly booking) —
    // as soon as a client picks a service on step 1, auto-advance
    // to the Date step so novices see the calendar immediately.
    // Multi-treatment stacking now happens via the post-time
    // "Add another treatment?" prompt on step 3 instead of a huge
    // scrolling menu. Only fires when adding (not deselecting) and
    // when we're actually on the treatment step.
    if (didAdd && step === 1) {
      setAddAnotherAnswered(false);
      setTimeout(() => {
        setStep(2);
        try {
          toast(
            isFr
              ? `${svc.name} ajouté — voici vos dates disponibles`
              : `${svc.name} added — here are your available dates`,
            { duration: 2500, position: "top-center" }
          );
        } catch (_) { /* toast optional */ }
      }, 350);
    }
  }, [selectedLocation, isFr, step]);


  
  // Helper to generate cache key
  const getCacheKey = useCallback((locationId, date, duration) => {
    return `${locationId}_${date}_${duration}`;
  }, []);
  
  // Helper to check if cache is valid
  const isCacheValid = useCallback((key) => {
    const cached = availabilityCacheRef.current[key];
    if (!cached) return false;
    return Date.now() - cached.timestamp < CACHE_TTL_MS;
  }, []);
  
  // Fetch availability with caching
  const pickedVidRef = useRef(null);
  pickedVidRef.current = (selectedServices && selectedServices[0] && selectedServices[0].variation_id) || null;
  const fetchAvailability = useCallback(async (locationId, date, duration, forceRefresh = false) => {
    // ITER 107 — include provider in the cache key + the request so the
    // backend queries the right team_member_id. Without this, Tanya's
    // service slots came back empty because the endpoint always asked
    // Square about Karine.
    const provKey = (selectedProvider || "karine").toLowerCase();
    // Karine (Jul 2026) — also forward the picked service's variation_id
    // so the backend queries THAT specific treatment's availability
    // instead of walking its own top-3-shortest candidate list (which
    // was silently skipping Tanya's Free Facial Lymphatic drain).
    const pickedVid = pickedVidRef.current;
    const lsKey = `${provKey}:${pickedVid || "any"}`;
    const cacheKey = `${provKey}|${pickedVid || "any"}|${getCacheKey(locationId, date, duration)}`;

    // Return in-memory cached data if valid and not forcing refresh
    if (!forceRefresh && isCacheValid(cacheKey)) {
      return availabilityCacheRef.current[cacheKey].data;
    }

    // Warm the in-memory cache from localStorage so the next render is
    // instant — without this, every full page reload starts cold.
    if (!forceRefresh) {
      const lsSlots = readCachedSlots(locationId, date, duration, lsKey);
      if (lsSlots) {
        availabilityCacheRef.current[cacheKey] = { data: lsSlots, timestamp: Date.now() };
      }
    }

    try {
      let res = await axios.post(`${API}/square/availability`, {
        location_id: locationId,
        date: date,
        service_duration_minutes: duration,
        provider: (selectedProvider || "karine").toLowerCase(),
        service_variation_id: pickedVid,
      }, { timeout: 35000 });

      // Backend may return `is_loading: true` on a cold cache miss while it
      // warms the Square slots in the background. Show a brief loading
      // state by retrying once after 4 seconds — catches Vanessa's bug
      // where the first cold-cache hit was reading as "no availability".
      // Feb 2026 · Karine panic — Oct 8 Botox came back empty on the
      // primary call because retries exhausted before Square's cold
      // start finished. Bump the retry window from 2×4s=8s → 4×3s=12s
      // with faster probes so the "no slots" banner stops firing on
      // real availability.
      let attempts = 0;
      while (res?.data?.is_loading && attempts < 4) {
        attempts += 1;
        await new Promise((r) => setTimeout(r, 3000));
        try {
          res = await axios.post(`${API}/square/availability`, {
            location_id: locationId,
            date: date,
            service_duration_minutes: duration,
            provider: (selectedProvider || "karine").toLowerCase(),
            service_variation_id: pickedVid,
          }, { timeout: 35000 });
        } catch (_re) { break; }
      }

      const availableTimes = res.data.available_times || [];

      // Don't poison the cache with a still-loading empty response;
      // we'd rather refetch on the next interaction than persist "no slots".
      if (res?.data?.is_loading && availableTimes.length === 0) {
        return { __error: true, type: "loading" };
      }

      // Store in in-memory + localStorage
      availabilityCacheRef.current[cacheKey] = {
        data: availableTimes,
        timestamp: Date.now()
      };
      writeCachedSlots(locationId, date, duration, availableTimes, lsKey);

      // Update cached dates count for UI
      const validCacheCount = Object.keys(availabilityCacheRef.current).filter(k => isCacheValid(k)).length;
      setCachedDatesCount(validCacheCount);

      return availableTimes;
    } catch (err) {
      const isTimeout = err?.code === "ECONNABORTED" || (err?.message || "").toLowerCase().includes("timeout");
      const isNetwork = !err?.response;
      console.error(`Failed to fetch availability for ${date}:`, err);
      // If we have ANY localStorage backup for this exact bucket, return
      // it so the UI shows slots instead of a "slow" error. Square will
      // reject any stale slot at confirm-time anyway.
      const lsBackup = readCachedSlots(locationId, date, duration, lsKey);
      if (lsBackup) return lsBackup;
      // Surface timeout/network errors distinctly so we can show a retry CTA
      // instead of an "empty slots" message.
      return { __error: true, type: isTimeout ? "timeout" : (isNetwork ? "network" : "server") };
    }
  }, [getCacheKey, isCacheValid, selectedProvider]);
  
  // Preload availability for upcoming dates in background.
  // Uses an internal ref for dedup so this callback has a STABLE identity
  // across renders — otherwise the dates-fetch effect (which depends on
  // this function) re-fires every time we update `preloadingDates`, which
  // cancels the in-flight fetch and produces an infinite "Preloading..." spinner.
  const preloadingRef = useRef(new Set());
  const preloadUpcomingDates = useCallback(async (locationId, dates, duration) => {
    if (!locationId || !dates || dates.length === 0 || !duration) return;

    // Take first PRELOAD_DAYS dates that aren't already cached or being loaded
    const datesToPreload = dates
      .slice(0, PRELOAD_DAYS)
      .map(d => d.date)
      .filter(date => {
        const cacheKey = getCacheKey(locationId, date, duration);
        return !isCacheValid(cacheKey) && !preloadingRef.current.has(date);
      });

    if (datesToPreload.length === 0) return;

    // Mark dates as being preloaded — ref doesn't trigger re-renders
    datesToPreload.forEach(d => preloadingRef.current.add(d));
    // Mirror to state for any UI that reads preloadingDates (badge, etc.)
    setPreloadingDates(prev => new Set([...prev, ...datesToPreload]));

    // Fetch in parallel (but staggered to avoid hammering API)
    for (const date of datesToPreload) {
      // Small delay between requests to be gentle on the API
      await new Promise(resolve => setTimeout(resolve, 100));
      await fetchAvailability(locationId, date, duration);
    }

    // Clear preloading status
    datesToPreload.forEach(d => preloadingRef.current.delete(d));
    setPreloadingDates(prev => {
      const next = new Set(prev);
      datesToPreload.forEach(d => next.delete(d));
      return next;
    });
  }, [getCacheKey, isCacheValid, fetchAvailability]);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      document.body.style.overflow = "hidden";
      // Oct 2026 · Bianca Renaud — returning clients on the same phone get
      // their info pre-loaded without typing their email again.
      let _remembered = null;
      try { _remembered = prefillEmail ? null : (localStorage.getItem("kl_booking_email") || null); } catch { /* noop */ }
      const _autoEmail = prefillEmail || _remembered;
      if (_autoEmail && !autoFilled) {
        if (_remembered) setRebookEmail(_remembered);
        fetch(`${API}/client/lookup?email=${encodeURIComponent(_autoEmail)}`)
          .then(r => r.json())
          .then(data => {
            if (data.found && data.client) {
              setCustomerInfo(p => ({
                ...p,
                first_name: data.client.first_name || "",
                last_name: data.client.last_name || "",
                email: data.client.email || _autoEmail,
                phone: data.client.phone || ""
              }));
              setAutoFilled(true);
            }
          })
          .catch(() => {});
        // Parallel: fetch the client's last booking so we can offer a
        // "Book again — same as last time" one-tap shortcut on step 0.
        // Silently ignored on failure — booking flow continues as normal.
        fetch(`${API}/client/last-booking?email=${encodeURIComponent(_autoEmail)}`)
          .then(r => r.json())
          .then(data => {
            if (data?.found && data?.last?.location_id) {
              setLastBooking(data.last);
            }
          })
          .catch(() => {});
      }
      // Auto-select location if prefilled. Guard against re-running once
      // we've already advanced past step 0 — otherwise an unstable
      // `prefillPackage` reference upstream could bump the user back to
      // the location step every time this effect re-fires.
      if (prefillLocationId && !selectedLocation) {
        const names = { "1": "Carleton Place", "2": "Kanata", "3": "Ottawa" };
        if (names[prefillLocationId]) {
          setSelectedLocation({ id: prefillLocationId, name: names[prefillLocationId] });
          setStep(1);
        }
      }
      // ── Karine (Jul 2026) — Consume `open_booking_prefill` set by the
      // ReturningClientHero (Reserve this slot) and any future surface
      // that wants to land the client on the confirmation step with
      // location + date + time all pre-picked. Cleared after apply so
      // subsequent opens start clean.
      if (!selectedLocation) {
        try {
          const raw = localStorage.getItem("open_booking_prefill");
          if (raw) {
            const p = JSON.parse(raw);
            const names = { "1": "Carleton Place", "2": "Kanata", "3": "Ottawa" };
            if (p && names[p.location_id]) {
              setSelectedLocation({ id: p.location_id, name: names[p.location_id] });
              if (p.date) setSelectedDate(p.date);
              if (p.time) {
                setSelectedTime(p.time);
              } else if (p.time_iso) {
                // ISO string like "2026-07-27T10:00:00" → extract HH:MM local
                const t = String(p.time_iso).slice(11, 16);
                if (t) setSelectedTime(t);
              }
              // Advance to the treatment step. Once the client picks a
              // service, the existing date/time state will carry through
              // to the confirmation panel automatically.
              setStep(1);
            }
            localStorage.removeItem("open_booking_prefill");
          }
        } catch { /* noop */ }
      }
      // Store prefilled package info — only on first arrival so subsequent
      // re-renders with the same content don't replace the reference and
      // re-fire the catalog-fetch effect (which would cancel the in-flight
      // auto-match and leave the user stuck on the treatment step).
      // Oct 21 2026 · start-screen search bar — land on the treatment list
      // already filtered to what the client typed.
      if (prefillSearch && !prefillPackage) setServiceSearch(String(prefillSearch));
      if (prefillPackage && !packageInfo) {
        setPackageInfo(prefillPackage);
        // Iter 339 · Feb 2026 — Promo funnel: if the caller shipped a
        // searchQuery (e.g. from the August Laser Genesis promo page),
        // seed the treatment picker's search box so the client sees a
        // filtered list of matching Square services immediately —
        // even if auto-match fails, the picker is one tap away.
        //
        // Iter 449 · Sep 21 2026 · Karine ask — when NO explicit
        // searchQuery is passed, fall back to the package name so
        // the client never sees the full 200-service list on step 2.
        // Also strip surrounding hint words like "promo", "package",
        // "$" prices so the search filter matches the Square name.
        const _q = prefillPackage.searchQuery
          || (prefillPackage.name || "")
              .replace(/\bpromo\b/gi, "")
              .replace(/\bpackage\b/gi, "")
              .replace(/\$\d+.*/g, "")
              .replace(/·/g, "")
              .trim();
        if (_q) setServiceSearch(_q);
      }
    } else {
      document.body.style.overflow = "unset";
    }
    return () => { document.body.style.overflow = "unset"; };
  }, [isOpen, prefillEmail, autoFilled, prefillLocationId, prefillPackage, prefillSearch]);

  const handleClose = () => {
    setIsVisible(false);
    releaseHold();
    setTimeout(() => {
      onClose();
      setStep(0);
      setSelectedLocation(null);
      setSelectedServices([]);
      setCatalogServices([]);
      setServiceSearch("");
      setSelectedDate(null);
      setSelectedTime(null);
      setBooking(null);
      setError(null);
      setPackageInfo(null);
      setAddAnotherAnswered(false);
      setHoldToken(null);
      setHoldSecondsLeft(null);
      setRebookEmail("");
      setRebookProfile(null);
      setClientProfile(null);
      // Reset post-booking "earlier alert" opt-in so the next booking starts fresh
      setEarlierAlertSaving(false);
      setEarlierAlertSaved(false);
      setEarlierAlertError(null);
      setClaimResult(null);
      // Clear cache on close to ensure fresh data next time
      availabilityCacheRef.current = {};
      setCachedDatesCount(0);
      setPreloadingDates(new Set());
    }, 200);
  };

  // Feb 2026 · Load website→Square variation mappings once. Best-effort;
  // if it fails, the flow degrades to the pre-existing fuzzy-name matcher.
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/square/mappings`, { timeout: 15000 })
      .then((r) => {
        if (cancelled) return;
        const m = (r?.data?.mappings) || {};
        setServiceMappings(m);
      })
      .catch(() => { /* silent — non-blocking */ });
    return () => { cancelled = true; };
  }, []);

  // Fetch catalog services when location is selected — with retry-on-failure.
  // The error banner is suppressed on the location step (step 0) so clients
  // don't see a scary red error before they've even clicked Next. We also
  // try one MORE retry in the background after a longer pause so transient
  // Square slowdowns self-recover without forcing the user to refresh.
  useEffect(() => {
    if (!selectedLocation) return;
    setLoading(true);
    setCatalogLoading(true);
    setError(null);
    setCatalogServices([]);

    let cancelled = false;
    const maxAttempts = 5;

    const fetchServices = async () => {
      let lastErr = null;
      // Backoff schedule (ms): 0, 1.5s, 3s, 6s, 12s — total ~22s of patient retries.
      const delays = [0, 1500, 3000, 6000, 12000];
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (cancelled) return null;
        if (delays[attempt - 1] > 0) {
          await new Promise((resolve) => setTimeout(resolve, delays[attempt - 1]));
        }
        if (cancelled) return null;
        try {
          // Iter 467 · Sep 17 2026 · When a client has already picked
          // Linda (via her /book?provider=linda deep-link or the
          // provider carousel), pass ?provider=linda so the backend
          // re-orders the list: consultation → botox → fillers →
          // skin boosters → specialty facials, per Karine's ask.
          const _providerQP = (selectedProvider && String(selectedProvider).toLowerCase() === "linda") ? "?provider=linda" : "";
          const res = await axios.get(
            `${API}/square/catalog-services/${selectedLocation.id}${_providerQP}`,
            { timeout: 45000 }, // 45s — Square can be sluggish on cold paths
          );
          return res;
        } catch (err) {
          lastErr = err;
        }
      }
      throw lastErr;
    };

    fetchServices()
      .then(res => {
        if (cancelled || !res) return;
        const services = res.data.services || [];
        setCatalogServices(services);
        // Clear any previous stale error — we succeeded.
        setError(null);

        // Aug 24 2026 · Tammy Thompson bug — clients arriving via
        // /book?type=consultation from the "Book Virtual Consultation"
        // hero button used to see 40+ paid services with nothing pre-
        // picked. Now we auto-select "Virtual Consultation" (or the
        // best-matching consultation service) and jump straight to
        // step 2 so the picker is one Next away from the date.
        //
        // Only fires when there is NO explicit `packageInfo` — a
        // promo-card / AI-preview handoff still owns its own pre-fill
        // logic below.
        if (!packageInfo && prefillBookingType === "consultation" && services.length > 0) {
          const _consultPool = (prefillProvider || "").toLowerCase() === "karine"
            ? services.filter((s) => !s.tanya_only && !s.linda_only && !["tanya", "linda"].includes(providerFromServiceName(s.name || "")))
            : services;
          const findConsult = (needles) =>
            _consultPool.find((s) => {
              const n = (s.name || "").toLowerCase();
              return needles.some((k) => n.includes(k));
            });
          // Priority: exact "Virtual Consultation" → any consult that
          // mentions "virtual/video/online/phone" → the plain
          // "Consultation" → Free Consultation. First match wins.
          const virtualConsult =
            findConsult(["virtual consultation"]) ||
            findConsult(["virtual consult", "video consult", "online consult", "phone consult", "zoom consult"]) ||
            findConsult(["free consultation", "complimentary consult"]) ||
            findConsult(["consultation"]);
          if (virtualConsult) {
            setSelectedServices([virtualConsult]);
            setStep((s) => (s === 1 ? 2 : s));
          }
        }
        
        // Auto-select service if we have a prefilled package
        // (Oct 29 2026 · menu-box "Book now" passes noAutoPick → filtered picker only)
        if (packageInfo && services.length > 0 && !packageInfo.noAutoPick) {
          let matchedService = null;
          const packageName = (packageInfo.name || "").toLowerCase();
          const packageCategory = (packageInfo.category || "").toLowerCase();
          const packageType = (packageInfo.type || "").toLowerCase();

          // Feb 2026 · Laser-hair cart passes `services[]` with per-area
          // variation IDs. Resolve EVERY variation first (Priority 0),
          // BEFORE any duration-bucket / fuzzy / keyword matcher fires.
          // Without this, resolveTanyaVariation(10) was mapping to the
          // 30-min Tanya bucket (which Square had re-labelled "Facial
          // Lymphatic Drain"), landing the client on an unbookable
          // service even though the cart was for Laser Hair · Linea.
          let laserStackPrefill = null;
          if (Array.isArray(packageInfo.services) && packageInfo.services.length > 0) {
            const picks = [];
            const seen = new Set();
            for (const extra of packageInfo.services) {
              const vid = (extra && extra.variation_id) || "";
              if (!vid || seen.has(vid)) continue;
              const hit = services.find((s) => s.variation_id === vid);
              if (hit) {
                picks.push({ ...hit, duration_minutes: extra.duration || hit.duration_minutes });
                seen.add(vid);
              }
            }
            if (picks.length > 0) {
              matchedService = picks[0];
              laserStackPrefill = picks;
              // eslint-disable-next-line no-console
              console.log(`[BOOKING] Priority-0 laser-hair stack prefill: ${picks.length} area(s)`,
                picks.map((s) => s.name));
            }
          }

          // === UNIVERSAL FAST BOOKING (2026-02) — Priority 0 ============
          // If the cart item ships an exact Square identifier or a
          // canonical exact name, resolve it deterministically BEFORE any
          // keyword heuristics. This is the fast-path that skips the
          // treatment picker for ANY service — no per-item hardcoding.
          //
          //   1. variation_id — exact Square item variation id
          //   2. catalog_id   — exact Square catalog object id
          //   3. exact_name   — canonical Square service name (case &
          //                     whitespace insensitive)
          //   4. name         — fallback: cart's display name equals a
          //                     Square service name after normalisation
          // ============================================================
          const stripAllWs = (s) => (s || "").toString().toLowerCase().replace(/[\s_\-.]+/g, "").trim();
          // Feb 2026 · Priority 0a — website `service_key` mapping.
          // Every homepage/laser/package CTA can now include a
          // `service_key` (the canonical WEBSITE_SERVICES id). If we
          // have a mapping AND the mapped variation exists in the
          // current location's catalog, use it. This is the
          // deterministic $0.00-fix path.
          if (packageInfo.service_key && serviceMappings[packageInfo.service_key]) {
            const mvid = serviceMappings[packageInfo.service_key].variation_id;
            if (mvid) {
              matchedService = services.find((s) => s.variation_id === mvid) || null;
              if (matchedService) {
                // eslint-disable-next-line no-console
                console.log("[BOOKING] service_key auto-picked:", packageInfo.service_key, "→", matchedService.name);
              }
            }
          }
          if (!matchedService && packageInfo.variation_id) {
            matchedService = services.find((s) => s.variation_id === packageInfo.variation_id) || null;
          }
          if (!matchedService && packageInfo.catalog_id) {
            matchedService = services.find(
              (s) => s.catalog_id === packageInfo.catalog_id || s.item_id === packageInfo.catalog_id,
            ) || null;
          }
          if (!matchedService && packageInfo.exact_name) {
            const target = stripAllWs(packageInfo.exact_name);
            matchedService = services.find((s) => stripAllWs(s.name) === target) || null;
          }
          if (!matchedService && packageName) {
            const target = stripAllWs(packageName);
            // Only accept exact-name matches long enough to be unambiguous
            // (avoids matching a cart item called "Botox" against every
            // Botox variation — the keyword logic below handles that case).
            if (target.length >= 6) {
              matchedService = services.find((s) => stripAllWs(s.name) === target) || null;
            }
          }
          // Oct 2026 · Bianca Renaud — promo "Book now" (/book?search=Makeover 9)
          // used to land on an unfiltered picker with nothing selected. Match
          // the search query against Square names (word-boundary so
          // "Makeover 1" never grabs "Makeover 10") and pick the most specific.
          if (!matchedService && packageInfo.source === "search-prefill" && packageInfo.searchQuery && !packageInfo.noAutoPick) {
            const q = String(packageInfo.searchQuery).trim().replace(/\s+/g, " ");
            const esc = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/ /g, "\\s*");
            const rx = new RegExp(`(^|[^a-z0-9])${esc}(?![a-z0-9])`, "i");
            const hits = services.filter((s) => rx.test((s.name || "").replace(/\s+/g, " ")) && !(s.name || "").trim().startsWith("-"));
            if (hits.length > 0) {
              hits.sort((a, b) => (a.name || "").length - (b.name || "").length);
              matchedService = hits[0];
              // eslint-disable-next-line no-console
              console.log("[BOOKING] search-prefill auto-picked:", q, "→", matchedService.name);
            }
          }
          if (matchedService) {
            // eslint-disable-next-line no-console
            console.log("[BOOKING] Fast-path auto-picked:", matchedService.variation_id, "→", matchedService.name);
          }

          // 0) Tanya direct mapping — when the booking is locked to Tanya
          //    and the prefill carries a duration/bookingType, resolve
          //    the EXACT Square variation ID from the curated map so we
          //    don't depend on Square's fuzzy name match. This is the
          //    primary path for /laser-and-skin's per-package CTAs.
          if (isTanya && !matchedService) {
            try {
              const annaVarId = resolveTanyaVariation({
                duration: packageInfo.duration,
                bookingType: packageInfo.bookingType || prefillBookingType,
                eventId: packageInfo.eventId,
              });
              if (annaVarId) {
                matchedService = services.find(s => s.variation_id === annaVarId) || null;
                if (matchedService) {
                  // eslint-disable-next-line no-console
                  console.log("[BOOKING] Tanya variation auto-picked:", annaVarId, "→", matchedService.name);
                }
              }
            } catch (_e) {
              // Resolver shouldn't throw; if it does we just fall through
              // to the keyword/name matchers below.
            }
          }

          // Everything above is an explicit pick (service_key, variation_id, exact
          // name, search prefill, Tanya map); everything below is fuzzy.
          const explicitMatch = !!matchedService;

          // Normalize for whitespace-insensitive matching — Square catalog
          // sometimes splits words (e.g. "Medical Micro Needling") while our
          // category labels are one word ("Medical Microneedling").
          const stripWs = (s) => s.replace(/[\s_-]+/g, "");
          const packageNameStripped = stripWs(packageName);
          const packageCategoryStripped = stripWs(packageCategory);

          // 0) TOKEN MATCH — Sept 6 2026 · Bryan bug fix.
          // Treatment Map deep-links pass rich descriptive names like
          // "BIG 3 · Forehead · 11's · Crow's feet" or
          // "Full Upper Face (7 areas: BIG 3 + Brow Lift · Smile Lift · Bunny Lines · Lip Flip)".
          // The FAMILY_KEYWORDS matcher below can miss these when the raw
          // name contains no family word. Tokenize the packageName by
          // common delimiters and try to resolve the FIRST meaningful
          // token against the Square catalog before falling through.
          // Skips very short tokens (<3 chars) and pure numerics.
          if (!matchedService && packageName) {
            // Legacy promo / seasonal entries are junk — the family
            // matcher already penalises them at line ~1085. Apply the
            // same filter here so the priority-0 token match doesn't
            // rank them. Also deprioritise "virtual" for in-person
            // consult deep-links (Radiesse hands / face / neck).
            const isPromoJunk = (s) => {
              const n = (s.name || "").trim();
              if (n.startsWith("-")) return true;
              return /\b(valentines?|spring|summer|fall|autumn|winter|easter|halloween|christmas|black\s*friday|cyber\s*monday|door\s*crasher|promo|xmas|noel|kiss\s*me)\b/i.test(n);
            };
            const packageWantsVirtual = /\bvirtual\b/i.test(packageName);
            const isVirtual = (s) => /\bvirtual\b/i.test(s.name || "");
            const validServices = services.filter(
              (s) => !isPromoJunk(s) && (packageWantsVirtual || !isVirtual(s))
            );

            // Stronger normalizer — strips ALL non-alphanumeric (so
            // "(Free) Consultation" and "Free Consultation" both collapse
            // to "freeconsultation").
            const alnum = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/gi, "");

            // Priority 0a: try the FULL packageName first (most specific).
            const fullAlnum = alnum(packageName);
            matchedService = fullAlnum.length >= 4 ? (validServices.find((s) => {
              return alnum(s.name).includes(fullAlnum);
            }) || null) : null;
            // Priority 0b: split into tokens, longest-first (so distinctive
            // words like "Radiesse" beat generic ones like "Face").
            if (!matchedService) {
              const rawTokens = packageName
                .split(/[·•\-–—+/(),&]|\s+/g)
                .map((t) => t.trim())
                .filter((t) => t.length >= 5 &&
                               !/^\d+$/.test(t) &&
                               !/^(areas?|full|upper|lower|package|treatment|session|combo|face|neck|hand|hands|only|lift|jelly|smile|brow|line|lines|flip|bunny)$/i.test(t))
                .sort((a, b) => b.length - a.length);  // longest first
              for (const tok of rawTokens) {
                const tokAlnum = alnum(tok);
                const hit = validServices.find((s) => alnum(s.name).includes(tokAlnum));
                if (hit) {
                  matchedService = hit;
                  // eslint-disable-next-line no-console
                  console.log(`[BOOKING] Token match: "${tok}" → ${hit.name} (${hit.variation_id})`);
                  break;
                }
              }
            } else {
              // eslint-disable-next-line no-console
              console.log(`[BOOKING] Full-name match: "${packageName}" → ${matchedService.name} (${matchedService.variation_id})`);
            }
          }

          // 1) Treatment-family keyword match — covers cart hand-offs where
          //    the package name is "3 Treatments Transformation Package"
          //    and we need to find Square's "Medical Microneedling" service.
          //    The family is inferred from `category` or `name` keywords.
          const FAMILY_KEYWORDS = [
            { keys: ["microneed", "rejuvapen"], match: ["microneed", "rejuvapen"] },
            { keys: ["prp"],                     match: ["prp"] },
            // Botox family — Sept 6 2026 · Bryan bug fix. Treatment Map
            // deep-links pass names like "BIG 3 · Forehead · 11's · Crow's
            // feet" or "Full Upper Face (7 areas: BIG 3 + Brow Lift…)" —
            // none contain the word "botox", so the family matcher used
            // to miss them and the auto-advance-past-service never fired.
            // That state-drift (visual chip but no resolved variation)
            // caused Bryan's ERR_CONNECTION_CLOSED on confirm.
            { keys: ["botox", "dysport", "neuro",
                     "big 3", "big3", "the big 3", "big three",
                     "full upper face", "total face", "upper face",
                     "forehead", "11's", "11s", "glabella",
                     "crow's feet", "crows feet", "crow feet",
                     "brow lift", "smile lift", "lip flip",
                     "bunny line", "gummy smile", "jelly roll",
                     "nefertiti", "neck band", "platysmal",
                     "microtox", "micro-tox", "traptox", "trapezoid",
                     "makeover", "migraine", "tmj", "jaw grind",
                     "hyperhidrosis", "underarm sweat"],
              match: ["botox", "dysport", "big 3", "the big 3",
                      "upper face", "total face", "forehead", "crow",
                      "brow", "smile", "lip flip", "bunny", "gummy",
                      "jelly", "nefertiti", "neck band", "microtox",
                      "traptox", "migraine", "tmj", "hyperhidrosis"] },
            { keys: ["filler", "lip", "cheek"],  match: ["filler", "lip"] },
            { keys: ["sculptra"],                match: ["sculptra"] },
            // Radiesse® CaHA biostimulator (Sept 2026 · Karine ask —
            // deep-link from the /radiesse editorial page). Matches any
            // Square service whose name contains "radiesse" (face, hands,
            // hyperdilute for neck / décolleté) so the picker is skipped.
            { keys: ["radiesse", "caha"],       match: ["radiesse", "caha"] },
            { keys: ["booster", "polynuc", "profhilo", "baby glow"], match: ["booster", "polynuc", "profhilo"] },
            // Karine + Renergie hand-off (Jul 7 2026) — when a client adds
            // "The Renergie Package" to cart, resolve to the closest
            // Square filler/booster combo so the booking flow can auto-
            // select and skip the Treatment picker instead of stranding
            // the client on a search that returns nothing.
            { keys: ["renergie"], match: ["renergie", "rha1", "redensity", "filler", "booster"] },
            { keys: ["hyperhidrosis", "underarm", "sweat"], match: ["hyperhidrosis", "sweat", "underarm"] },
            // Iter 339 · Feb 2026 — Laser Genesis + Lymphatic Facial
            // Drainage August promo. Prefer combo/lymphatic services
            // if they exist in Tanya's catalog, else fall back to a
            // standalone Laser Genesis session (Tanya adds lymphatic on-site).
            { keys: ["laser", "genesis", "lymphatic", "drainage"], match: ["laser genesis", "lymphatic", "drainage"] },
            // Iter 344 · Feb 12 2026 — GLOW RESET promo (Redensity 1 3 mL
            // + HydraFacial). Karine's Square catalog entry is named
            // "GLOW RESET" verbatim; fallback matches are Redensity /
            // Skin Booster / HydraFacial for graceful degradation.
            { keys: ["glow", "reset", "redensity", "hydrafacial", "booster"], match: ["glow reset", "redensity", "hydrafacial", "skin booster"] },
            // Iter 449 · Sep 21 2026 · Karine ask — Skin Tightening 360
            // deep-link should skip the picker entirely. Same for
            // Facial Lymphatic Drain and the aesthetician-side combos
            // that were formerly house-only.
            { keys: ["skin tightening 360", "tightening 360", "4-modality"], match: ["skin tightening 360", "4-modality"] },
            { keys: ["lymphatic drain", "lymph drain"], match: ["lymphatic drain", "lymphatic facial"] },
            { keys: ["dermaplane"], match: ["dermaplane"] },
            { keys: ["éminence", "eminence"], match: ["éminence", "eminence"] },
            { keys: ["hydrafacial"], match: ["hydrafacial"] },
            { keys: ["potenza", "rf micro", "microneedl"], match: ["rf microneedling", "microneedling", "potenza"] },
            { keys: ["lash lift", "brow lam", "brow tint", "lash tint"], match: ["lash", "brow"] },
            { keys: ["consult"], match: ["consult"] },
          ];

          // Iter 449 · Sep 21 2026 · Karine ask — generic exact-name
          // shortcut BEFORE the keyword-family loop. If the deep-link
          // package name matches a Square service name closely (either
          // exact contains-substring, or all-tokens-present), auto-pick
          // it and skip the treatment list entirely so clients see
          // Days & Times immediately.
          if (!matchedService) {
            const cleanPkg = stripWs(packageNameStripped
              .replace(/\bpromo\b/g, "")
              .replace(/\bpackage\b/g, "")
              .replace(/\$\d+.*/g, "")
              .trim());
            const pkgTokens = cleanPkg
              .split(/\s+/)
              .filter(t => t.length >= 3);
            const directHit = services.find(s => {
              const nm = stripWs((s.name || "").toLowerCase());
              return cleanPkg && (nm.includes(cleanPkg) || cleanPkg.includes(nm));
            });
            const tokenHit = directHit || (pkgTokens.length >= 2
              ? services.find(s => {
                  const nm = stripWs((s.name || "").toLowerCase());
                  return pkgTokens.every(t => nm.includes(t));
                })
              : null);
            if (tokenHit) {
              matchedService = tokenHit;
              // eslint-disable-next-line no-console
              console.log(`[BOOKING] Exact-name match: "${cleanPkg}" → ${tokenHit.name} (${tokenHit.variation_id})`);
            }
          }

          for (const fam of (matchedService ? [] : FAMILY_KEYWORDS)) {
            const isInPackage = fam.keys.some(
              (k) => packageNameStripped.includes(k) ||
                     packageCategoryStripped.includes(k) ||
                     packageType.includes(k)
            );
            if (!isInPackage) continue;            // Collect ALL services that match the family keyword (whitespace-insensitive)
            const familyServices = services.filter((s) => {
              const nStripped = stripWs((s.name || "").toLowerCase());
              return fam.match.some((m) => nStripped.includes(m));
            });
            if (familyServices.length === 0) continue;
            // Prefer the closest match by package name (e.g. "3 Treatments")
            //   1) service name contains the same treatment-count phrase
            //   2) closest duration
            //   3) closest price
            const targetDuration = packageInfo.duration || 60;
            const targetPrice = packageInfo.priceOverride || 0;
            const variationHint = (packageInfo.variationHint || "").toLowerCase();
            const phraseHints = [];
            const countMatch = packageName.match(/(\d+)\s*treatment/);
            if (countMatch) phraseHints.push(`${countMatch[1]} treatment`);
            // Extract syringe count (e.g. "1 Syringe Filler" → "1 syringe" /
            // "1 full syringe") so the scorer can pick the canonical Square
            // variant out of a pool of equally-long seasonal promos.
            const syringeMatch = packageName.match(/(\d+|½|1\/2)\s*syringe/i);
            if (syringeMatch) {
              const n = syringeMatch[1] === "½" ? "1/2" : syringeMatch[1];
              phraseHints.push(`${n} full syringe`);
              phraseHints.push(`${n} syringes filler`);
              phraseHints.push(`${n} syringe filler`);
              phraseHints.push(`${n} full syringes`);
            }
            matchedService = familyServices
              .map((s) => {
                const nLower = (s.name || "").toLowerCase();
                let score = 0;
                // Strongest signal: exact variation hint phrase
                if (variationHint && nLower.includes(variationHint)) score += 5000;
                phraseHints.forEach((p) => {
                  if (nLower.includes(p)) score += 1000;
                });
                // Penalize stale / seasonal promo entries — services whose
                // names start with "-" or contain a season tag are typically
                // legacy items with no team-member assignment (no availability).
                if ((s.name || "").trim().startsWith("-")) score -= 2000;
                if (/\b(valentines?|spring|summer|fall|autumn|winter|easter|halloween|christmas|black\s*friday|cyber\s*monday|door\s*crasher)\b/i.test(s.name || "")) {
                  score -= 1500;
                }
                const sPrice = Number(s.price) || 0;
                if (targetPrice > 0 && sPrice > 0) {
                  // Penalize price gap (small penalty so duration still matters)
                  score -= Math.abs(sPrice - targetPrice) * 0.5;
                }
                score -= Math.abs((s.duration_minutes || 45) - targetDuration);
                return { s, score };
              })
              .sort((a, b) => b.score - a.score)[0]?.s || familyServices[0];
            if (matchedService) break;
          }

          // 2) If this is a combo package (Sculptra + Skin Booster or Filler + Skin Booster)
          if (!matchedService && (packageInfo.hasSkinBooster || packageInfo.isCombo)) {
            const duration = packageInfo.duration || 60;
            matchedService = services.find(s => {
              const serviceNameLower = (s.name || "").toLowerCase();
              const hasComboKeyword = serviceNameLower.includes('combo') ||
                                       serviceNameLower.includes('package') ||
                                       serviceNameLower.includes('forfait');
              const durationMatch = Math.abs((s.duration_minutes || 45) - duration) <= 15;
              return hasComboKeyword && durationMatch;
            });
            if (!matchedService) {
              const comboServices = services.filter(s => {
                const n = (s.name || "").toLowerCase();
                return n.includes('combo') || n.includes('package') || n.includes('forfait');
              });
              if (comboServices.length > 0) {
                matchedService = comboServices.reduce((closest, s) => {
                  const sDiff = Math.abs((s.duration_minutes || 45) - duration);
                  const closestDiff = Math.abs((closest.duration_minutes || 45) - duration);
                  return sDiff < closestDiff ? s : closest;
                });
              }
            }
          }

          // 3) Fallback: Map package type to Square service name
          if (!matchedService) {
            const serviceMap = {
              'booster': 'Skin Booster',
              'botox': 'Botox',
              'sculptra': 'Sculptra',
              'radiesse': 'Radiesse',
              'filler': 'Filler',
              'combo': 'Combo',
              'vip': 'VIP Package',
              'microneedling': 'Microneedling',
              'prp': 'PRP',
            };
            const targetServiceName = serviceMap[packageInfo.serviceType || packageInfo.type] || packageInfo.type || "";
            if (targetServiceName) {
              matchedService = services.find(s =>
                s.name.toLowerCase().includes(targetServiceName.toLowerCase()) ||
                targetServiceName.toLowerCase().includes(s.name.toLowerCase())
              );
            }
          }

          // 4) Oct 26 2026 · Karine — "they already told you what they want". Last-resort
          //    word-overlap match so a prefilled promo/treatment never lands on an
          //    unselected list with Next greyed out. Picks the service sharing the most
          //    meaningful words with the prefill (≥2 words or ≥60% of them).
          if (!matchedService) {
            const STOP = new Set(["the", "and", "with", "for", "of", "a", "an", "to", "promo", "fall", "winter", "spring", "summer", "package", "special", "treatment", "session", "regular", "reg", "tax", "plus"]);
            const words = (txt) => String(txt || "").toLowerCase().replace(/[^a-z0-9àâçéèêëîïôûùüÿñæœ ]+/g, " ").split(/\s+/).filter((w) => w.length > 2 && !STOP.has(w));
            const want = [...new Set([...words(packageInfo.name), ...words(packageInfo.exact_name), ...words(packageInfo.searchQuery)])];
            if (want.length) {
              let best = null; let bestScore = 0;
              for (const s of services) {
                if ((s.name || "").trim().startsWith("-")) continue;
                const have = new Set(words(s.name));
                const score = want.filter((w) => have.has(w) || [...have].some((h) => h.startsWith(w.slice(0, 5)) && w.length >= 5)).length;
                if (score > bestScore || (score === bestScore && best && (s.name || "").length < (best.name || "").length)) { best = s; bestScore = score; }
              }
              if (best && (bestScore >= 2 || bestScore / want.length >= 0.6)) {
                matchedService = best;
                // eslint-disable-next-line no-console
                console.log("[BOOKING] word-overlap auto-picked:", want.join(" "), "→", best.name, `(${bestScore}/${want.length})`);
              }
            }
          }

          // Oct 28 2026 · ticket #294006 — fuzzy picks must not silently book
          // a different-priced package when one at the card's price exists.
          if (matchedService && !explicitMatch) {
            const repicked = repickByCardPrice(matchedService, services, packageInfo);
            if (repicked !== matchedService) {
              // eslint-disable-next-line no-console
              console.log("[BOOKING] price re-pick:", matchedService.name, "→", repicked.name);
              matchedService = repicked;
            }
          }

          if (matchedService) {
            // Override price + duration from the cart's package so the modal
            // shows the customer-facing package price ($1099 etc.), not
            // Square's single-session price.
            const serviceWithDuration = {
              ...matchedService,
              duration_minutes: packageInfo.duration || matchedService.duration_minutes,
            };
            if (packageInfo.priceOverride != null) {
              serviceWithDuration.price = packageInfo.priceOverride;
              serviceWithDuration.price_label = packageInfo.priceLabel;
              serviceWithDuration.package_label = packageInfo.name; // shown in summary
            }
            // AI Skin Analysis "Book This Look" handoff — packageInfo may
            // carry `additional_treatments: [<name>, …]` alongside the
            // primary. Match each by canonical name / family-keyword and
            // stack them into `selectedServices` so the client lands on
            // step 2 with the full cart pre-built.
            const stack = laserStackPrefill ? [...laserStackPrefill] : [serviceWithDuration];
            // Feb 2026 · Laser-hair multi-area stack — the Priority-0
            // block above already resolved every area deterministically
            // into `laserStackPrefill`. Skip the per-area loop below if
            // we have the pre-built stack to avoid duplicate adds.
            if (!laserStackPrefill && Array.isArray(packageInfo.services) && packageInfo.services.length > 0) {
              const already = new Set([serviceWithDuration.variation_id]);
              for (const extra of packageInfo.services) {
                const vid = (extra && extra.variation_id) || "";
                if (!vid || already.has(vid)) continue;
                const match = services.find((s) => s.variation_id === vid);
                if (match) {
                  stack.push({
                    ...match,
                    // Preserve the cart's per-area duration so Square's
                    // segment length is correct (Linea = 10m, Full Legs = 60m)
                    duration_minutes: extra.duration || match.duration_minutes,
                  });
                  already.add(vid);
                }
              }
              if (stack.length > 1) {
                // eslint-disable-next-line no-console
                console.log(`[BOOKING] Laser-hair multi-area stack: ${stack.length} areas`, stack.map(s => s.name));
              }
            }
            if (Array.isArray(packageInfo.additional_treatments) && packageInfo.additional_treatments.length > 0) {
              const addAlready = new Set(stack.map(s => s.variation_id));
              for (const rawName of packageInfo.additional_treatments) {
                const target = stripAllWs(rawName);
                if (!target) continue;
                // Priority 1: exact canonical-name match
                let extra = services.find((s) => stripAllWs(s.name) === target);
                // Priority 2: family-keyword match (Botox → any Botox variation)
                if (!extra) {
                  const nameStripped = stripWs(String(rawName).toLowerCase());
                  for (const fam of FAMILY_KEYWORDS) {
                    const inRaw = fam.keys.some((k) => nameStripped.includes(k));
                    if (!inRaw) continue;
                    extra = services.find((s) => {
                      const sname = stripWs(s.name.toLowerCase());
                      return fam.match.some((m) => sname.includes(m));
                    });
                    if (extra) break;
                  }
                }
                // Priority 3: loose substring
                if (!extra) {
                  const lower = String(rawName).toLowerCase();
                  extra = services.find((s) => s.name.toLowerCase().includes(lower));
                }
                if (extra && !addAlready.has(extra.variation_id)) {
                  stack.push(extra);
                  addAlready.add(extra.variation_id);
                }
              }
              if (stack.length > 1) {
                // eslint-disable-next-line no-console
                console.log(`[BOOKING] AI "Book This Look" prefill: ${stack.length} treatments stacked`, stack.map(s => s.name));
              }
            }
            setSelectedServices(stack);
            // Skip service-selection step since we auto-selected.
            // Use functional setter so we always read the latest step value
            // (closure may be stale if a re-render happened during the fetch).
            setStep((s) => (s === 1 ? 2 : s));
          }
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Only show the red error banner once the user has actually
        // advanced past the location step — they need the catalog to
        // pick a treatment. On step 0 the error is irrelevant and just
        // looks scary; the next retry will likely succeed by then.
        // We use a functional check so the latest step value is used.
        setStep((curr) => {
          if (curr >= 1) {
            setError(isFr
              ? "La liste des soins prend plus de temps que prévu. Réessayez dans un instant — Karine est notifiée."
              : "Service list is taking a moment to load. Please try again — Karine has been notified."
            );
          }
          return curr;
        });
      })
      .finally(() => {
        if (!cancelled) { setLoading(false); setCatalogLoading(false); }
      });

    return () => { cancelled = true; };
  }, [selectedLocation, isFr, packageInfo]);

  // ─── Snapshot listener — handles the async script-tag race ──────────
  // The snapshot script tag (`<script src="/api/booking/snapshot.js" async>`
  // in index.html) loads in parallel with the React bundle. On slow phones
  // React can mount BEFORE the snapshot finishes executing, so a one-shot
  // read of window.__KL_BOOKING_DATA on mount sees `undefined`.
  // This effect polls every 200ms for up to 8 seconds. The moment the
  // snapshot becomes available, it seeds `availableDates` and turns off
  // `loading` — bypassing any hanging /api/square/available-dates fetch.
  useEffect(() => {
    if (!selectedLocation?.id) return undefined;
    if (availableDates && availableDates.length > 0) return undefined;

    let cancelled = false;
    let attempts = 0;
    const provKey = (selectedProvider || "karine").toLowerCase();
    const tick = () => {
      if (cancelled || attempts >= 40) return; // 40 × 200 ms = 8 s
      attempts += 1;
      try {
        const snap = (typeof window !== "undefined") ? window.__KL_BOOKING_DATA : null;
        // Read Tanya's provider-scoped dates when the funnel is in Tanya
        // mode; otherwise fall back to the default (Karine) location block.
        const dates = (provKey === "tanya"
          ? snap?.providers?.tanya?.locations?.[selectedLocation.id]?.dates
          : provKey === "karine" ? snap?.locations?.[selectedLocation.id]?.dates : null);
        if (dates && dates.length > 0) {
          setAvailableDates(dates);
          datesKeyRef.current = `${selectedLocation.id}|${provKey}`;
          writeCachedDates(selectedLocation.id, dates, provKey);
          setLoading(false);
          return;
        }
      } catch { /* ignore */ }
      setTimeout(tick, 200);
    };
    setTimeout(tick, 200);
    return () => { cancelled = true; };
  }, [selectedLocation?.id, availableDates, selectedProvider]);

  // Saves 3-8 seconds of perceived wait time. While the user is browsing
  // treatments (step 2), we kick off the dates fetch in the background so
  // by the time they reach step 3 the response is already cached on the
  // backend (and often the frontend too). Fire-and-forget — errors are
  // silently ignored because the real fetch on step 3 will retry properly.
  useEffect(() => {
    if (!selectedLocation?.id) return;

    // FIRST: Instantly seed availableDates from window.__KL_BOOKING_DATA
    // (preloaded by index.html script tag) OR from localStorage. This
    // means dates appear in <50ms on EVERY visit, including first-ever,
    // because the snapshot script loads in parallel with the React bundle.
    const provKey = (selectedProvider || "karine").toLowerCase();
    let seeded = false;
    try {
      const snapshot = (typeof window !== "undefined") ? window.__KL_BOOKING_DATA : null;
      const fromSnap = (provKey === "tanya"
        ? snapshot?.providers?.tanya?.locations?.[selectedLocation.id]?.dates
        : provKey === "karine" ? snapshot?.locations?.[selectedLocation.id]?.dates : null);
      if (fromSnap && fromSnap.length > 0) {
        // Oct 20 2026 — only seed when empty; never clobber a live
        // (variation-aware, Linda-inclusive) list already on screen.
        if (!availableDatesRef.current.length) { setAvailableDates(fromSnap); datesKeyRef.current = `${selectedLocation.id}|${provKey}`; }
        writeCachedDates(selectedLocation.id, fromSnap, provKey);
        seeded = true;
      }
    } catch {}
    if (!seeded && !availableDatesRef.current.length) {
      const cached = readCachedDates(selectedLocation.id, provKey);
      if (cached && cached.length > 0) {
        setAvailableDates(cached);
        datesKeyRef.current = `${selectedLocation.id}|${provKey}`;
      }
    }

    let cancelled = false;
    const t = setTimeout(() => {
      if (cancelled) return;
      // Use a default 30-min duration which warms the most common cache
      // bucket; the real duration-specific fetch on step 3 hits a warm
      // backend cache and returns in <500ms.
      // Oct 2 2026 · Karine P0 — pass service_variation_id so the
      // backend's Linda-union logic surfaces Thursday dates for
      // Botox/filler variations she's assigned to.
      const _firstVar = (selectedServices && selectedServices[0] && selectedServices[0].variation_id) || null;
      axios.get(`${API}/square/available-dates/${selectedLocation.id}`, {
        timeout: 20000,
        params: {
          duration_minutes: 30,
          provider: provKey,
          ...(_firstVar ? { service_variation_id: _firstVar } : {}),
        },
      })
        .then(res => {
          const list = res?.data?.available_dates || [];
          if (list.length > 0) writeCachedDates(selectedLocation.id, list, provKey);
        })
        .catch(() => {});
    }, 50);  // tiny delay so the user's click is not blocked
    return () => { cancelled = true; clearTimeout(t); };
  }, [selectedLocation?.id, selectedProvider]);

  // ─── AUTOMATIC RECOVERY on dates step ────────────────────────────────
  // Tightened to fire BEFORE a user would manually hit refresh (~13 s).
  //   T+4 s   → re-fetch the snapshot.js (fresh dates from CDN, <200 ms)
  //   T+8 s   → bump refreshKey (forces a new dates fetch)
  //   T+14 s  → wipe localStorage caches + refresh
  //   T+20 s  → unregister service-worker + full window.location.reload()
  useEffect(() => {
    if (step !== 2) return undefined;
    if (!loading) return undefined;
    if (availableDates && availableDates.length > 0) return undefined;

    let cancelled = false;
    const fired = { snap: false, refetch: false, soft: false, hard: false };

    const reseedFromSnapshot = async () => {
      try {
        const r = await fetch(`/api/booking/snapshot.js?ts=${Date.now()}`, { cache: "no-store" });
        const code = await r.text();
        // eslint-disable-next-line no-new-func
        new Function(code)();
        const provKey = (selectedProvider || "karine").toLowerCase();
        const fresh = (provKey === "tanya"
          ? window?.__KL_BOOKING_DATA?.providers?.tanya?.locations?.[selectedLocation?.id]?.dates
          : provKey === "karine" ? window?.__KL_BOOKING_DATA?.locations?.[selectedLocation?.id]?.dates : null);
        if (!cancelled && fresh && fresh.length > 0) {
          setAvailableDates(fresh);
          datesKeyRef.current = `${selectedLocation.id}|${provKey}`;
          writeCachedDates(selectedLocation.id, fresh, provKey);
          setLoading(false);
        }
      } catch { /* fall through to next recovery stage */ }
    };

    const t1 = setTimeout(() => { if (cancelled || fired.snap) return; fired.snap = true; reseedFromSnapshot(); }, 4000);
    const t2 = setTimeout(() => { if (cancelled || fired.refetch) return; fired.refetch = true; setRefreshKey(k => k + 1); }, 8000);
    const t3 = setTimeout(() => {
      if (cancelled || fired.soft) return;
      fired.soft = true;
      try {
        Object.keys(localStorage).forEach((k) => { if (k.startsWith("kl_dates_v1_") || k.startsWith("kl_slots_v1_")) localStorage.removeItem(k); });
      } catch { /* ignore */ }
      setRefreshKey(k => k + 1);
      reseedFromSnapshot();
    }, 14000);
    const t4 = setTimeout(() => {
      if (cancelled || fired.hard) return;
      fired.hard = true;
      if (userIsBusyElsewhere()) return;
      try {
        if (navigator?.serviceWorker?.getRegistrations) {
          navigator.serviceWorker.getRegistrations().then(rs => rs.forEach(r => r.unregister())).finally(() => window.location.reload());
        } else {
          window.location.reload();
        }
      } catch { try { window.location.reload(); } catch { /* nothing else we can do */ } }
    }, 20000);

    return () => {
      cancelled = true;
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); clearTimeout(t4);
    };
  }, [step, loading, availableDates, selectedLocation?.id]);



  // Fetch available dates when at least one treatment is selected (step 2) — with retry
  // Tightened from 3x 40s = up to 120s to 2x 12s = max ~25s so the user gets
  // an actionable error and "Try again" button quickly when Square stalls.
  useEffect(() => {
    if (!selectedLocation || selectedServices.length === 0) return;

    // Stale-while-revalidate: if we already have dates cached (window
    // snapshot, prior visit via localStorage, or the location-pick preload),
    // DON'T flip to the skeleton loading state — refresh silently in the
    // background. This is what makes the dates step feel instant.
    const provKey = (selectedProvider || "karine").toLowerCase();
    let snapshotDates = null;
    try {
      const snap = (typeof window !== "undefined") ? window?.__KL_BOOKING_DATA : null;
      snapshotDates = (provKey === "tanya"
        ? snap?.providers?.tanya?.locations?.[selectedLocation.id]?.dates
        : provKey === "karine" ? snap?.locations?.[selectedLocation.id]?.dates : null);
    } catch {}
    // Oct 20 2026 — when the service/provider just changed, the old list was
    // cleared below; don't re-seed it from cache/snapshot (Linda-only consult
    // was showing Karine's Mon/Fri days for 15 s).
    const _keyNow = `${selectedLocation.id}|${provKey}`;
    const _keyChanged = availableDatesRef.current.length > 0 && datesKeyRef.current !== _keyNow;
    const cachedNow = _keyChanged ? null : (readCachedDates(selectedLocation.id, provKey) || snapshotDates);
    if (_keyChanged && availableDatesRef.current.length) setAvailableDates([]);
    const haveDates = (!_keyChanged && availableDates && availableDates.length > 0) || (cachedNow && cachedNow.length > 0);
    if (cachedNow && cachedNow.length > 0 && (!availableDates || availableDates.length === 0)) {
      setAvailableDates(cachedNow);
      datesKeyRef.current = _keyNow;
    }
    if (!haveDates) {
      setLoading(true);
    }
    setError(null);

    let cancelled = false;
    const maxAttempts = 2;
    // Oct 2 2026 · Karine P0 — pass variation to backend so Linda's
    // Thursdays union into Botox/filler Date pickers.
    const firstVariationId = (selectedServices && selectedServices[0] && selectedServices[0].variation_id) || null;

    const fetchDates = async () => {
      let lastErr = null;
      for (let attempt = 1; attempt <= maxAttempts; attempt++) {
        if (cancelled) return null;
        try {
          const res = await axios.get(
            `${API}/square/available-dates/${selectedLocation.id}`,
            // 25s timeout: production Square calls can take 18-22s on
            // cold cache. The backend has a 12s budget BUT now always
            // falls back to last-known-good Mongo dates on its own,
            // so any timeout here is genuinely a network issue, not
            // backend slowness. 25s on the client is conservative.
            // ITER 107: pass `provider` so the dates list is filtered
            // through Tanya's schedule when applicable.
            // Oct 2 2026 · Karine P0 — ALSO pass service_variation_id
            // so the backend auto-unions Linda's Thursdays into the
            // calendar for Botox/filler variations she can inject.
            {
              timeout: 25000,
              params: {
                provider: provKey,
                ...(firstVariationId ? { service_variation_id: firstVariationId } : {}),
              },
            }
          );
          return res;
        } catch (err) {
          lastErr = err;
          if (attempt < maxAttempts) {
            await new Promise((resolve) => setTimeout(resolve, 600));
          }
        }
      }
      throw lastErr;
    };

    const _datesKey = _keyNow;
    setDatesRefreshing(true);
    const _refreshCap = setTimeout(() => { if (!cancelled) setDatesRefreshing(false); }, 20000);
    fetchDates()
      .then(res => {
        if (cancelled || !res) return;
        const dates = res.data.available_dates || [];
        // GUARD: never replace populated dates with an empty list.
        // The never-block backend returns [] on cold-miss instead of
        // waiting on Square; without this guard, a perfectly-good
        // 94-date snapshot would be wiped to [] and the user would see
        // skeletons forever.
        datesKeyRef.current = _datesKey;
        if (dates.length > 0) {
          setAvailableDates(dates);
          writeCachedDates(selectedLocation.id, dates, provKey);
        } else if (!availableDates || availableDates.length === 0) {
          // Only set empty if we genuinely have nothing else
          setAvailableDates([]);
        }
        const duration = totalDurationMinutes || 15;
        const effectiveDates = dates.length > 0 ? dates : availableDates;
        if (effectiveDates && effectiveDates.length > 0) {
          preloadUpcomingDates(selectedLocation.id, effectiveDates, duration);
        }
      })
      .catch(() => {
        if (cancelled) return;
        // Only show the error banner when we have NO cached dates to fall
        // back on. With a stale list visible, a silent retry is enough.
        if (!haveDates) {
          setError(isFr
            ? "Les disponibilités sont lentes en ce moment. Appuyez sur Réessayer ou rafraîchissez la page."
            : "Availability is slow right now. Tap Try Again or refresh the page."
          );
        }
      })
      .finally(() => {
        clearTimeout(_refreshCap);
        if (!cancelled) { setLoading(false); setDatesRefreshing(false); }
      });

    return () => { cancelled = true; clearTimeout(_refreshCap); setDatesRefreshing(false); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLocation, selectedServices, totalDurationMinutes, isFr, preloadUpcomingDates, selectedProvider]);

  // Track when we need to refresh availability (e.g., after a conflict)
  const [refreshKey, setRefreshKey] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [timeSinceUpdate, setTimeSinceUpdate] = useState("");

  // Sep 23 2026 · Carole Brown escalation — cap infinite "is_loading:true"
  // retry loop so the platform can't spin forever on a slow Square cold cache.
  // Reset every time the user picks a new date/service/location.
  const loadingRetryCountRef = useRef(0);
  useEffect(() => { loadingRetryCountRef.current = 0; }, [
    selectedLocation?.id, selectedDate?.date, selectedProvider, selectedServices?.length,
  ]);
  
  // Update "time since" display every 10 seconds
  useEffect(() => {
    if (!lastUpdated) return;
    
    const updateTimeSince = () => {
      const seconds = Math.floor((Date.now() - lastUpdated) / 1000);
      if (seconds < 10) {
        setTimeSinceUpdate(isFr ? "à l'instant" : "just now");
      } else if (seconds < 60) {
        setTimeSinceUpdate(isFr ? `il y a ${seconds}s` : `${seconds}s ago`);
      } else {
        const minutes = Math.floor(seconds / 60);
        setTimeSinceUpdate(isFr ? `il y a ${minutes}min` : `${minutes}min ago`);
      }
    };
    
    updateTimeSince();
    const interval = setInterval(updateTimeSince, 10000);
    return () => clearInterval(interval);
  }, [lastUpdated, isFr]);
  
  // Fetch time slots when entering step 3 (time selection) - uses cache
  useEffect(() => {
    if (step !== 3 || !selectedLocation || !selectedDate || selectedServices.length === 0) return undefined;

    const duration = totalDurationMinutes || 15;
    const cacheKey = getCacheKey(selectedLocation.id, selectedDate.date, duration);

    // Check in-memory cache first — instant return, no spinner.
    if (isCacheValid(cacheKey) && refreshKey === 0) {
      const cachedData = availabilityCacheRef.current[cacheKey].data;
      setTimeSlots(cachedData);
      setLastUpdated(availabilityCacheRef.current[cacheKey].timestamp);
      return undefined;
    }

    // localStorage backup — seed timeSlots INSTANTLY so the step-4 UI
    // never shows a skeleton on return visits / cold reloads. The live
    // fetch below still runs and silently refreshes the list.
    let seededFromLS = false;
    if (refreshKey === 0) {
      const lsSlots = readCachedSlots(selectedLocation.id, selectedDate.date, duration, `${(selectedProvider || "karine").toLowerCase()}:${pickedVidRef.current || "any"}`);
      if (lsSlots && lsSlots.length > 0) {
        availabilityCacheRef.current[cacheKey] = { data: lsSlots, timestamp: Date.now() };
        setTimeSlots(lsSlots);
        setLastUpdated(Date.now());
        seededFromLS = true;
      }
    }

    // Not in cache or forced refresh - fetch from API.
    // CRITICAL: use a cancellation flag so if this effect re-fires before
    // Square responds (Square cold paths can take 8-10s), stale fetches
    // do NOT update state — preventing the perpetual-spinner symptom
    // clients were reporting where loading would never clear.
    let cancelled = false;
    if (!seededFromLS) {
      setLoading(true);
      setError(null);
      setSelectedTime(null);
    }

    // Safety net: if Square truly hangs, force-clear loading after 40s so
    // the user sees a retry button rather than an infinite spinner. The
    // inner fetchAvailability has its own 35s timeout, so 40s outer leaves
    // a 5s margin — preventing the previous race where outer fired BEFORE
    // a slow-but-successful inner fetch could resolve.
    const hardTimeout = setTimeout(() => {
      if (cancelled) return;
      cancelled = true;
      setLoading(false);
      // Only show the "slow" error when we don't have stale slots visible.
      if (!seededFromLS) {
        setError(isFr
          ? "Square est lent à répondre — appuyez sur Réessayer, ou envoyez-nous un texto au (613) 850-0805 et nous vous réserverons directement."
          : "Square is slow to respond — tap Try Again, or text us at (613) 850-0805 and we'll book you directly.");
      }
    }, 40000);

    fetchAvailability(selectedLocation.id, selectedDate.date, duration, refreshKey > 0)
      .then(availableTimes => {
        if (cancelled) return;
        clearTimeout(hardTimeout);
        if (Array.isArray(availableTimes)) {
          // Success — whether empty or full array
          setTimeSlots(availableTimes);
          setLastUpdated(Date.now());
          // Cross-provider suggestion (Jenny) is REMOVED per Karine's
          // explicit request. The alternate-provider lookup used to fire
          // here when slots were empty — now the user just sees the "no
          // slots" message and the Square direct fallback. This block is
          // intentionally a no-op so future toggling is a single line.
          if (availableTimes.length === 0 && primaryService?.name) {
            setAltSuggestion(null);
            setAltSuggestionList([]);
            setAltSuggestionLoading(false);
          } else if (availableTimes.length > 0) {
            setAltSuggestion(null);
            setAltSuggestionList([]);
          }
        } else if (availableTimes && availableTimes.__error) {
          // Distinguish timeout / network / loading / server so the user
          // knows whether to wait, retry, or call us.
          const t = availableTimes.type;
          if (t === "loading") {
            // Cold-cache "still warming" path. Cap retries so we don't
            // loop forever on a slow Square backend (Carole Brown ·
            // Sep 23 2026 · "platform froze on Friday consultation").
            loadingRetryCountRef.current += 1;
            if (loadingRetryCountRef.current <= 3) {
              setError("");
              // eslint-disable-next-line no-undef
              setTimeout(() => { if (!cancelled) setRefreshKey((k) => k + 1); }, 3500);
            } else {
              setError(isFr
                ? "Square met du temps à répondre. Envoyez-nous un texto au (613) 850-0805 et nous vous réserverons directement — désolé pour l'attente !"
                : "Square is taking a while to respond. Text us at (613) 850-0805 and we'll book you directly — sorry for the wait!");
            }
          } else if (t === "timeout") {
            setError(isFr
              ? "Square est lent à répondre — appuyez sur Actualiser, ou envoyez-nous un texto au (613) 850-0805 et nous vous réserverons directement."
              : "Square is slow to respond — tap Refresh to try again, or text us at (613) 850-0805 and we'll book you directly.");
          } else if (t === "network") {
            setError(isFr
              ? "Connexion réseau interrompue — vérifiez votre signal et réessayez. Vous pouvez aussi envoyer un texto au (613) 850-0805."
              : "Network connection dropped — check your signal and try again. You can also text us at (613) 850-0805.");
          } else {
            setError(isFr
              ? "Erreur du serveur — réessayez ou envoyez-nous un texto au (613) 850-0805."
              : "Server error — please try again or text us at (613) 850-0805.");
          }
        } else {
          setError(isFr
            ? "Impossible de charger les heures — envoyez-nous un texto au (613) 850-0805 et nous vous réserverons directement."
            : "Could not load time slots — text us at (613) 850-0805 and we'll book you directly.");
        }
      })
      .finally(() => {
        if (cancelled) return;
        clearTimeout(hardTimeout);
        setLoading(false);
      });

    return () => {
      cancelled = true;
      clearTimeout(hardTimeout);
    };
  }, [step, selectedLocation, selectedDate, selectedServices, totalDurationMinutes, isFr, refreshKey, getCacheKey, isCacheValid, fetchAvailability]);

  // ─── AUTOMATIC RECOVERY on time-slots step ───────────────────────────
  // Tightened to fire BEFORE a user would manually hit refresh (~13 s).
  //   T+5 s   — silent force-refresh (bumps refreshKey)
  //   T+12 s  — wipe slot cache + refresh
  //   T+20 s  — service-worker unregister + window.location.reload()
  useEffect(() => {
    if (step !== 3) return undefined;
    if (!loading) return undefined;
    if (timeSlots && timeSlots.length > 0) return undefined;

    let cancelled = false;
    const fired = { refetch: false, soft: false, hard: false };

    const t1 = setTimeout(() => {
      if (cancelled || fired.refetch) return;
      fired.refetch = true;
      setRefreshKey(k => k + 1);
    }, 5000);
    const t2 = setTimeout(() => {
      if (cancelled || fired.soft) return;
      fired.soft = true;
      try {
        Object.keys(localStorage).forEach((k) => { if (k.startsWith("kl_slots_v1_")) localStorage.removeItem(k); });
      } catch { /* ignore */ }
      setRefreshKey(k => k + 1);
    }, 12000);
    const t3 = setTimeout(() => {
      if (cancelled || fired.hard) return;
      fired.hard = true;
      if (userIsBusyElsewhere()) return;
      try {
        if (navigator?.serviceWorker?.getRegistrations) {
          navigator.serviceWorker.getRegistrations().then(rs => rs.forEach(r => r.unregister())).finally(() => window.location.reload());
        } else {
          window.location.reload();
        }
      } catch { try { window.location.reload(); } catch { /* nothing else we can do */ } }
    }, 20000);

    return () => {
      cancelled = true;
      clearTimeout(t1); clearTimeout(t2); clearTimeout(t3);
    };
  }, [step, loading, timeSlots, selectedDate?.date, selectedLocation?.id]);



  const availableTimeSlots = useMemo(() => timeSlots.filter(t => t.available), [timeSlots]);

  // Group catalog services: consultations first, featured (badged) next, then by category
  const [serviceSort, setServiceSort] = useState("category");  // category | az
  const [quickFilter, setQuickFilter] = useState("all");
  const [treatmentCodes, setTreatmentCodes] = useState(null);
  useEffect(() => { loadTreatmentCodes().then(setTreatmentCodes); }, []);
  const codeBadge = (svc) => { const c = codeOf(svc, treatmentCodes); return c ? <span className="flex-shrink-0 px-1.5 py-0.5 rounded bg-amber-300 text-zinc-900 text-[12px] font-black tracking-wider ring-1 ring-amber-500/40" data-testid={`service-code-${svc.variation_id}`}>#{c}</span> : null; };
  useEffect(() => { try { if (selectedLocation?.id) localStorage.setItem("kl_last_location_id", String(selectedLocation.id)); } catch { /* noop */ } }, [selectedLocation?.id]);
  const { consultationServices, promoServices, featuredServices, groupedServices, azServices } = useMemo(() => {
    const search = serviceSearch.toLowerCase().trim();
    let pool = catalogServices;

    // Oct 20 2026 — the client chose Karine in the provider chooser: don't
    // lead the list with Linda's / Tanya's own consults and services.
    if ((prefillProvider || "").toLowerCase() === "karine") {
      pool = pool.filter((s) => {
        if (s.tanya_only || s.linda_only) return false;
        const p = providerFromServiceName(s.name || "");
        return p !== "tanya" && p !== "linda";
      });
    }

    // Booking-type filter — Jenny's flow may be opened with either
    // type=aesthetic (Botox, fillers, microneedling) or type=medical
    // (Women's Health: PAP, contraception, hormones, perimenopause,
    // dermatology consults). Narrows the treatment list so the patient
    // doesn't have to scroll past services that don't apply to their
    // visit. Defensive: an unknown booking-type keyword is ignored
    // and the full list is shown.
    if (prefillBookingType === "medical") {
      const MEDICAL_INCLUDE = [
        "women's health", "womens health", "contraception", "hormone",
        "perimenopaus", "menopaus", "pap", "pelvic", "vulvar", "vulvodyn",
        "pcos", "medical visit", "medical consult", "ocean md", "oceanmd",
        "dermatology", "acne", "rosacea", "eczema", "psoriasis",
        "hyperpigment", "hypopigment", "scar", "hair loss", "wound",
        "lump", "fungal", "hidradenit",
        // Therapeutic neuromodulator visits
        "migraine", "hyperhidrosis", "bruxism",
      ];
      pool = pool.filter((s) => {
        const text = `${s.name || ""} ${s.category || ""} ${s.description || ""}`.toLowerCase();
        return MEDICAL_INCLUDE.some((kw) => text.includes(kw));
      });
    } else if (prefillBookingType === "aesthetic") {
      // For aesthetic flow, hide purely-medical services so the patient
      // doesn't accidentally book "PAP smear" when they wanted Botox.
      const MEDICAL_EXCLUDE = [
        "women's health", "womens health", "pap smear", "contraception",
        "hormone therapy", "perimenopaus", "menopaus", "pelvic pain",
        "vulvar", "vulvodyn", "pcos consult", "ocean md", "oceanmd",
        "medical visit",
      ];
      pool = pool.filter((s) => {
        const text = `${s.name || ""} ${s.category || ""} ${s.description || ""}`.toLowerCase();
        return !MEDICAL_EXCLUDE.some((kw) => text.includes(kw));
      });
    } else if (prefillBookingType === "consultation") {
      // Aug 24 2026 · Tammy Thompson bug — clients who tap "Book
      // Virtual Consultation" on the hero landed here with
      // type=consultation, but the funnel wasn't filtering the
      // treatment list at all. They saw 40+ paid services (Botox
      // promos, fillers, RF microneedling) and couldn't find the
      // free virtual consult they came for. Now the treatment list
      // is filtered to ONLY consultation-shaped services so the
      // patient is always one tap away from Next → Date picker.
      const CONSULT_INCLUDE = [
        "virtual consult", "virtual consultation", "video consult",
        "phone consult", "online consult", "zoom consult",
        "consultation", "consult appointment", "consult visit",
        "free consult", "complimentary consult", "meet & greet",
        "meet and greet", "follow up", "follow-up",
      ];
      pool = pool.filter((s) => {
        const text = `${s.name || ""} ${s.category || ""} ${s.description || ""}`.toLowerCase();
        return CONSULT_INCLUDE.some((kw) => text.includes(kw));
      });
    }

    // ─── Provider filter — Tanya (laser/skin) vs Karine (injectables) ────
    // When the booking modal is opened from "Book with Tanya", the Ottawa
    // Square location returns ALL services including Karine's injectable
    // promos (Full Upper Face $599, Crow's Feet, fillers, etc.). Clients
    // see them and assume Tanya performs them — which is wrong and causes
    // booking confusion. Filter to Tanya's actual scope of practice.
    //
    // The mirror is true for Karine: when /book?provider=karine (or any of
    // her injectable landing pages), the pool must hide Tanya's laser/skin
    // services, otherwise Karine sees "Brazilian", "IPL", "Chemical Peel"
    // as bookable options — services she doesn't perform.
    //
    // Rule (provider-scoped):
    //   1. Consultations always show (unless tagged to the other provider).
    //   2. If the name contains an out-of-scope keyword → BLOCK.
    //   3. If the name contains an in-scope keyword → ALLOW.
    //   4. Anything else (unknown) → default block (conservative).
    if (isTanya) {
      pool = pool.filter((s) => {
        const name = (s.name || "").toLowerCase();
        // Consultations: keep unless the name is explicitly Karine's
        if (s.category === "Consultations") {
          if (/karine|lauzon\s+rn|with\s+karine|karine\s+lauzon/.test(name)) return false;
          return true;
        }
        // NB: deliberately matching `name` only — the backend categorizer
        // defaults unknown items to "Neuromodulator / Botox", so including
        // category here would falsely block legitimate Tanya services
        // (Hydrafacial, Laser Genesis, Chemical Peels) whose names don't
        // contain any injectable keyword but whose category does.
        const isKarineOnly = KARINE_INJECTABLE_KEYWORDS.some((k) => name.includes(k));
        if (isKarineOnly) return false;
        const isTanyaScope = TANYA_SERVICE_KEYWORDS.some((k) => name.includes(k));
        return isTanyaScope;
      });
    } else if (isKarine) {
      // Oct 21 2026 · Karine escalation — the old keyword ALLOW-list hid
      // ~30 of her own treatments per clinic (Forehead, Brow lift, Smile
      // lift, Chin, Trapezius, Migraine, Medical Microneedling, Exosomes…).
      // Now a BLOCK-list: hide only what is Tanya's / Linda's, show the rest.
      pool = pool.filter((s) => {
        const name = (s.name || "").toLowerCase();
        if (s.tanya_only || s.linda_only) return false;
        if (s.category === "Consultations") {
          return !/with\s+tanya|tanya\s+'s|tanya's|with tanya|linda/.test(name);
        }
        const isTanyaScope = TANYA_SERVICE_KEYWORDS.some((k) => name.includes(k));
        if (isTanyaScope) {
          // "Filler + RF Microneedling combo" — Karine leads the combo, keep it.
          return KARINE_INJECTABLE_KEYWORDS.some((k) => name.includes(k));
        }
        return true;
      });
    }

    const qf = QUICK_FILTERS.find((f) => f.id === quickFilter) || QUICK_FILTERS[0];
    const codeHit = search && pool.some((s) => matchesCode(search, s, treatmentCodes));
    const filtered = codeHit
      ? pool.filter((s) => matchesCode(search, s, treatmentCodes))
      : (search
      ? pool.filter((s) => matchesService(search, s, treatmentCodes))
      : pool).filter((s) => qf.test(s) && !isLegacyHidden(s));

    const consultations = filtered.filter(s => s.category === "Consultations");
    // Mar 4 2026 · Karine ask (Lesley Quinn FB DM) — Crown members were
    // signing up for the promo, tapping Book, and never finding the
    // $75 Laser Genesis + Lymphatic offer because it sat below "Book
    // with Tanya" and every regular treatment. Fix: pin every service
    // whose name contains the word "promo" as the FIRST section of the
    // picker so the offer is unmissable. Exclude them from every
    // downstream bucket so they never render twice.
    // Oct 2026 · client feedback ("promotions must be listed first") — every
    // offer-type item is pinned at the top: promo, Makeover packages,
    // door crashers, specials, bundles, flash deals.
    const isPromo = isPromoService;
    // Oct 10 2026 · Karine ask (client SMS: "couldn't find Makeover 6 $499
    // botox pkg") — badged Botox items (Makeover 6 = "popular") were being
    // pulled out of the Botox category into "Featured", so clients browsing
    // Botox never saw them. Keep every Botox item inside its category and
    // order it: Free consultation → Follow up → Makeovers (1…9, 360) → rest.
    const promos = sortPromos(filtered.filter(s => isPromo(s) && s.category !== "Consultations"));
    // Oct 2026 · badged items stay inside their category (badge still shows) so
    // the list has one clear order instead of a separate "Popular" bucket.
    const featured = [];
    const regular = filtered.filter(s => !isPromo(s) && s.category !== "Consultations" && !/do not book/i.test(s.name || ""));

    // Oct 2026 · "no order to the treatments" — Square categories are unreliable,
    // so group by name-derived category in a fixed clinical order, A→Z inside.
    const groups = Object.fromEntries(groupByMenu(regular.filter((s) => menuCategory(s) !== "Consultations")));
    const azServices = [...featured, ...regular].sort(azCompare);
    return { consultationServices: consultations, promoServices: promos, featuredServices: featured, groupedServices: groups, azServices };
  }, [catalogServices, serviceSearch, prefillBookingType, isTanya, isKarine, quickFilter, treatmentCodes]);

  const handleBook = async () => {
    setLoading(true);
    setError(null);
    try {
      // Build detailed booking notes with package info
      let bookingNotes = "";
      if (packageInfo) {
        const isComboPackage = packageInfo.hasSkinBooster || packageInfo.isCombo;
        
        bookingNotes = `🌟 WEBSITE BOOKING - ${isComboPackage ? 'COMBO PACKAGE' : 'PACKAGE'}\n`;
        bookingNotes += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        bookingNotes += `📦 Package: ${packageInfo.name}\n`;
        // Bug fix Feb 2026 (Danielle Hawes appt · "Complete Rejuvenation"):
        // some packages ship without a `price` field (bundle-only). Instead
        // of writing "$undefined" into the Square note — which forced Karine
        // to guess the price at checkout — fall back to `totalPrice`, then
        // `startingPrice`, then a clear "See catalogue for pricing" so the
        // note is always actionable.
        const _priceRaw = packageInfo.price ?? packageInfo.totalPrice ?? packageInfo.startingPrice;
        const _priceStr = (typeof _priceRaw === 'number' && !isNaN(_priceRaw))
          ? `$${_priceRaw.toLocaleString()}`
          : (typeof _priceRaw === 'string' && _priceRaw.trim() && _priceRaw.trim().toLowerCase() !== 'undefined')
              ? (_priceRaw.trim().startsWith('$') ? _priceRaw : `$${_priceRaw}`)
              : 'See catalogue';
        bookingNotes += `💰 Price: ${_priceStr}\n`;
        
        if (packageInfo.subtitle || packageInfo.vials) {
          bookingNotes += `📋 Details: ${packageInfo.subtitle || packageInfo.vials}\n`;
        }
        
        if (packageInfo.duration) {
          bookingNotes += `⏱️ Duration: ${packageInfo.duration} minutes\n`;
        }
        
        // Add badges if present (e.g., SCULPTRA, SKIN BOOSTER)
        if (packageInfo.badges && packageInfo.badges.length > 0) {
          bookingNotes += `🏷️ Type: ${packageInfo.badges.join(' + ')}\n`;
        }
        
        bookingNotes += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
        
        // Add description if available
        if (packageInfo.description) {
          bookingNotes += `\n✨ INCLUDES:\n${packageInfo.description}\n`;
        }
        
        // Add special instructions for combo packages
        if (isComboPackage) {
          bookingNotes += `\n📝 NOTE: This is a combo package. Please prepare both treatments.\n`;
        }
      }
      
      // Add custom customer note from prefillPackage (e.g., consultation interest)
      if (prefillPackage?.customerNote) {
        bookingNotes = bookingNotes 
          ? `${bookingNotes}\n\n📝 ${prefillPackage.customerNote}`
          : `📝 ${prefillPackage.customerNote}`;
      }
      if (any3PromoPicked && any3Areas.length > 0) {
        const line = `✅ ANY 3 AREAS — client chose: ${any3Areas.join(" + ")}${any3Areas.length < 3 ? ` (${3 - any3Areas.length} more to pick at check-in)` : ""}`;
        bookingNotes = bookingNotes ? `${bookingNotes}\n\n${line}` : line;
      }

      // Auto-inject the active loyalty credit so Karine can apply it at Square checkout.
      // The booking-success handler also marks this redemption as consumed
      // server-side, stopping the 15-min auto-revert watcher.
      // ITER 109 — if the client picked a Crown Points redemption in the
      // booking banner at step 5 (and we don't already have a pending
      // hold from elsewhere), redeem the points NOW so the credit code
      // can be stamped into the booking notes below.
      let effectiveHold = pendingHold;
      if (!effectiveHold && selectedRedemption) {
        try {
          const tok = typeof window !== "undefined" ? localStorage.getItem("client_token") : null;
          if (tok) {
            const rr = await axios.post(
              `${API}/client/redeem`,
              { points: selectedRedemption.points },
              { headers: { Authorization: `Bearer ${tok}` }, timeout: 15000 }
            );
            if (rr?.data?.success && rr.data.credit_code) {
              effectiveHold = {
                credit_code: rr.data.credit_code,
                dollar_value: rr.data.dollar_value,
                points: rr.data.points_used,
              };
              setPendingHold(effectiveHold);
            }
          }
        } catch (_e) {
          // Non-fatal: continue without the discount. We log + toast so
          // the client knows their booking went through but the points
          // couldn't be redeemed this time (they can redeem manually
          // from their account afterward).
          toast.error(isFr
            ? "Réservation continue, mais nous n'avons pas pu utiliser vos points cette fois. Réessayez depuis votre compte."
            : "Booking will continue, but we couldn't redeem your points this time. Try again from your account."
          );
        }
      }
      if (effectiveHold?.credit_code) {
        const creditLine = `🎁 LOYALTY CREDIT: Apply discount code ${effectiveHold.credit_code} at checkout (-$${effectiveHold.dollar_value} for ${effectiveHold.points} pts)`;
        bookingNotes = bookingNotes ? `${bookingNotes}\n\n${creditLine}` : creditLine;
      }
      // ITER 111 — "Pairs well with" add-on recommendations the client
      // checked at step 5. Reception adds these as Add-on line items in
      // SessionCart when they ring up the visit.
      if (selectedRecAddons && selectedRecAddons.length > 0) {
        const lines = selectedRecAddons
          .map((a) => `  • ${a.name}${a.price_from ? ` (+$${a.price_from})` : ""}`)
          .join("\n");
        const recBlock = `✨ ADD-ONS REQUESTED (apply at checkout):\n${lines}`;
        bookingNotes = bookingNotes ? `${bookingNotes}\n\n${recBlock}` : recBlock;
      }
      
      // ─── Feb 2026 · One-Click Reroute Confirm ─────────────────────
      // Ask the backend if this booking will silently swap providers
      // (e.g. Linda picked but service is aesthetician → we'd swap to
      // Tanya). If yes, surface a friendly confirm so the client stays
      // in the loop and trusts the swap. Non-fatal — if preflight
      // fails or is offline we fall through to the direct POST below.
      try {
        const _pfRes = await axios.post(`${API}/booking/preflight`, {
          location_id: selectedLocation.id,
          date: selectedDate.date,
          start_time: selectedTime,
          provider: (selectedProvider && selectedProvider !== "karine")
            ? selectedProvider : null,
          service_name: (selectedServices?.[0]?.name) || null,
          service_variation_id: primaryService?.variation_id || null,
          services: selectedServices.map(s => ({ name: s.name })),
          service_segments: selectedServices.map(s => ({
            name: s.name,
            service_variation_id: s.variation_id,
          })),
          customer: { email: customerInfo.email },
        }, { timeout: 6000 });

        if (_pfRes?.data?.needs_confirm && _pfRes.data?.reroute) {
          const rr = _pfRes.data.reroute;
          const _confirmMsg = isFr
            ? `${rr.from_label} n'est pas disponible pour ce service.\n\n${rr.reason}\n\nNous pouvons vous réserver avec ${rr.to_label} au même créneau. Voulez-vous continuer?`
            : `${rr.from_label} isn't available for this service.\n\n${rr.reason}\n\nWe can book you with ${rr.to_label} at the same time. Continue?`;
          const _confirmed = window.confirm(_confirmMsg);
          if (!_confirmed) {
            setLoading(false);
            setError(isFr
              ? "Réservation annulée. Choisissez un autre professionnel ou une autre date."
              : "Booking cancelled. Pick a different provider or date.");
            return;
          }
        }
      } catch (_pfErr) {
        // Preflight is best-effort. Never block the booking on it.
      }

      const res = await axios.post(`${API}/square/book`, {
        location_id: selectedLocation.id,
        date: selectedDate.date,
        start_time: selectedTime,
        customer: {
          first_name: customerInfo.first_name,
          last_name: customerInfo.last_name,
          email: customerInfo.email,
          phone: customerInfo.phone,
        },
        send_reminders: !!customerInfo.send_reminders,
        services: selectedServices.map(s => ({ name: s.name, price: s.price || "" })),
        service_variation_id: primaryService?.variation_id || null,
        service_duration_minutes: totalDurationMinutes || null,
        service_segments: selectedServices.map(s => ({
          service_variation_id: s.variation_id,
          duration_minutes: s.duration_minutes || 15,
          name: s.name,
        })),
        // Feb 2026 · surface the website service_key(s) so the backend
        // can hydrate any missing variation_id from `square_mappings`
        // even if the auto-picker on the client couldn't find one.
        service_key: packageInfo?.service_key || null,
        service_keys: selectedServices
          .map(s => s.service_key)
          .filter(k => typeof k === "string" && k.length > 0),
        customer_note: bookingNotes || null,
        // Éminence add-ons picked on the treatment page — sent as
        // structured keys so the backend confirmation email can render
        // a proper "Add-ons included" card (frontend already echoes them
        // on the success screen).
        addons: selectedAddOnsFromUrl.length ? selectedAddOnsFromUrl : null,
        referral_code: proReferralInfo?.code || null,
        // Iter 477.2 · Sep 19 2026 · Karine — send the AUTO-DETECTED
        // provider from `selectedProvider` (updated by the staff-name +
        // ottawa_only effect above). Previously this only sent "tanya"
        // when `isTanya` (URL-derived) was true, which meant a client
        // who added an "Aesthetician Tanya FREE Consultation" service
        // from the treatments picker still had provider=null → backend
        // defaulted to Karine's team_member. Now: any of "karine" /
        // "tanya" / "linda" routes to the correct Square calendar
        // without a URL flag. `null` is sent only when the value is
        // "karine" so we keep the legacy-default path for un-specified
        // provider selections (backend treats null == karine).
        provider: (selectedProvider && selectedProvider !== "karine")
          ? selectedProvider
          : null,
        prize_code: prefillPrizeCode || null,
        // Iter 339 · Feb 2026 — Attach promo campaign so the confirmation
        // email can render the August Crown Rewards promo banner.
        promo_campaign: packageInfo?.campaign || null,
        // Mar 4 2026 · Karine ask — forward UTM so the backend can
        // ping Karine's phone the instant an ad-sourced booking hits
        // a 409, before the client gives up.
        utm_source: (() => {
          try { return new URLSearchParams(window.location.search).get("utm_source"); } catch { return null; }
        })(),
        utm_campaign: (() => {
          try { return new URLSearchParams(window.location.search).get("utm_campaign"); } catch { return null; }
        })(),
        src: (() => {
          try { return new URLSearchParams(window.location.search).get("src") || sessionStorage.getItem("kl_booking_src"); } catch { return null; }
        })(),
      });
      // **CRITICAL safety check** — never show the success screen unless
      // the server returned `success: true` AND a real Square booking id.
      // Without this guard, a 200 OK with `{success:false}` or an empty
      // body (e.g., from an intermediate proxy) would silently push the
      // user to step 6 thinking they're booked — Nat Colette's report
      // on 2026-06-03 proved this had been happening.
      if (!res?.data?.success || !res.data?.booking_id) {
        const reason = res?.data?.message || res?.data?.detail || "Booking could not be confirmed by Square.";
        throw new Error(reason);
      }
      setBooking(res.data);
      // GA4 conversion — treatment + clinic only, never client details.
      gaEvent("booking_complete", {
        clinic: selectedLocation?.name || "",
        treatments: (selectedServices || []).map((s) => s.name).join(" + ").slice(0, 100),
        value: (selectedServices || []).reduce((sum, s) => sum + (Number(s.price) || 0), 0),
        currency: "CAD",
      });
      try { if (customerInfo.email) localStorage.setItem("kl_booking_email", customerInfo.email.trim().toLowerCase()); } catch { /* noop */ }
      reportLanguagePreference(customerInfo.email, isFr ? "fr" : "en");
      setBookFailCount(0); // successful booking → clear failure streak
      setConciergeNotified(false);
      setStep(6);

      // Sept 4 2026 · Sherralee bug — if the client wanted an Ottawa-only
      // add-on (facial-lymphatic-drain-addon) on a Kanata/Carleton Place
      // booking, ping reception so Karine can text them about scheduling
      // it as a separate Ottawa visit at the promo $45 rate. Fire-and-
      // forget: we never block the confirmation screen on this.
      if (pendingOttawaOnlyAddon) {
        try {
          const _loc = selectedLocation?.city || selectedLocation?.name || "Kanata/Carleton Place";
          const _svc = (Array.isArray(selectedServices) && selectedServices[0]?.name) || "their booked treatment";
          axios.post(`${API}/staff/reception-alert`, {
            kind: "ottawa_only_addon_wanted",
            addon_slug: pendingOttawaOnlyAddon,
            client_email: (customerInfo?.email || "").trim(),
            client_name: `${customerInfo?.first_name || ""} ${customerInfo?.last_name || ""}`.trim(),
            client_phone: (customerInfo?.phone || "").trim(),
            booking_id: res.data?.booking_id || null,
            booked_location: _loc,
            booked_service: _svc,
            message: `Book ${_svc} in Ottawa for the promo $45 add-on Lymphatic Drain — client is booked at ${_loc}.`,
          }, { timeout: 15000 }).catch(() => {});
        } catch (_) { /* never block */ }
      }
      // Celebration toast: every successful booking earns Crown Points
      // (visit credit). Fire instantly so the "+pts" hits at the same moment
      // they see the success screen — strongest dopamine moment in the flow.
      if (res.data?.success) {
        try {
          const { firePointsToast } = await import("@/components/PointsToast");
          firePointsToast(100, isFr ? "Rendez-vous confirmé!" : "Appointment confirmed!", "star");
        } catch (_) { /* graceful: never block booking success */ }
      }
      // Claim flow: if this booking came from an "earlier slot opened" email,
      // call the claim endpoint to auto-cancel the original Square booking
      // and award the +25 Crown Points bonus. Best-effort — never block step 6.
      if (claimWatcherId && res.data?.success) {
        axios
          .post(
            `${API}/booking/fast-track-watch/${claimWatcherId}/claim`,
            {
              client_email: customerInfo.email.trim(),
              new_square_booking_id: res.data.booking_id || null,
            },
            { timeout: 20000 }
          )
          .then((r) => {
            setClaimResult(r.data || {});
            if (r.data?.cancelled_booking_id) {
              toast.success(
                isFr
                  ? "Ancien rendez-vous annulé · +25 Crown Points 👑"
                  : "Old appointment auto-cancelled · +25 Crown Points 👑"
              );
            }
          })
          .catch((e) => {
            // Mark with an error result so the UI can show a graceful message
            setClaimResult({ error: errMsg(e, "claim_failed") });
          });
      }
      // Fire Fast Track conversion analytics if this booking came from a Fast Track pick.
      if (fastTrackCallId) {
        axios.post(
          `${API}/booking/fast-track-event`,
          { call_id: fastTrackCallId, event: "booked" },
          { timeout: 8000 }
        ).catch(() => {});
        setFastTrackCallId(null);
      }
      // Release the slot-hold now that booking is confirmed
      releaseHold();
    } catch (err) {
      const status = err.response?.status;
      const detail = errMsg(err);
      const _d = err.response?.data?.detail;
      if (status === 409 && _d && typeof _d === "object" && _d.code === "provider_off_schedule") {
        // Oct 15 2026 · Smart Redirect (Karine ask) — don't just refuse:
        // show the provider's next real openings as one-tap chips.
        const msg = _d.message || detail;
        setError(msg);
        setOffScheduleAlts({ provider: _d.provider || selectedProvider || "karine", list: Array.isArray(_d.alternatives) ? _d.alternatives : [] });
        try { toast.error(msg, { duration: 8000 }); } catch { /* noop */ }
        try {
          setTimeout(() => {
            const el = document.querySelector('[data-testid="booking-error"]');
            if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "center" });
          }, 80);
        } catch { /* noop */ }
        logBookingError({ code: "provider-off-schedule", message: msg, step: 5, location_id: selectedLocation?.id, service_ids: selectedServices.map(s => s.id), email: customerInfo?.email, phone: customerInfo?.phone, extra: { selected_date: selectedDate?.date || null, selected_time: selectedTime || null, alternatives: (_d.alternatives || []).length } });
        return;
      }
      
      // Oct 25 2026 · only a REAL time conflict gets the "just filled up"
      // treatment. Customer-profile / other Square errors tell the client
      // to call or text — never blame the slot.
      const _nonSlot409 = status === 409 && _d && typeof _d === "object" && ["square_rejection_other", "customer_create_failed"].includes(_d.code);
      if (status === 409 && !_nonSlot409) {
        // Iter 394 · Feb 20 2026 · Karine got a Booking Salvage alert
        // for mari_soul@yahoo.com — her 11:00 slot filled up between
        // page-load and confirm. The old handling flashed a toast that
        // Mari missed and she gave up. Now: persistent 10s toast +
        // scroll her back to the time grid + a friendlier follow-up.
        // Mar 4 2026 · Josee Bellerive FB DM — availability shows 10:00
        // as "open" but Square's real calendar has it blocked by an
        // overlapping team-member booking. Josee re-tapped 10:00 four
        // times and gave up. Fix: permanently blacklist the rejected
        // slot for this session so the grid greys it out and Josee
        // auto-lands on the next real opening.
        const _deadKey = `${selectedDate}T${selectedTime}`;
        setDeadSlots(prev => {
          const next = new Set(prev);
          next.add(_deadKey);
          return next;
        });
        // Compute the very next real opening on the same date so we
        // can auto-suggest it. Uses the live `availableTimeSlots` list
        // (already sorted by time), filters out anything ≤ the failed
        // time and anything already marked dead.
        let _nextTime = null;
        try {
          const failedT = selectedTime || "";
          const pool = (availableTimeSlots || [])
            .map(s => s.start_time)
            .filter(t => t && t > failedT && !deadSlots.has(`${selectedDate}T${t}`))
            .sort();
          _nextTime = pool[0] || null;
        } catch { /* noop */ }
        setSuggestedSlot(_nextTime ? { date: selectedDate, time: _nextTime } : null);
        const conflictMsg = isFr
          ? `${selectedTime} vient de se remplir — nous avons masqué cette heure. Choisissez le prochain créneau ✨`
          : `${selectedTime} just filled up — we've hidden that time. Pick the next open slot ✨`;
        try {
          toast.error(conflictMsg, { duration: 10000, position: "top-center" });
        } catch { /* noop */ }
        setSelectedTime(null);
        setRefreshKey(k => k + 1); // Force refresh of availability
        setStep(3);
        // Scroll the time picker into view so the client actually sees
        // that they're back at slot selection, not stuck on the summary.
        try {
          setTimeout(() => {
            const el = document.querySelector('[data-testid="time-picker"]')
                    || document.querySelector('[data-testid="booking-step-3"]');
            if (el?.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 120);
        } catch { /* noop */ }
      } else {
        const msg = (_nonSlot409 && _d.message) || detail || (isFr ? "Erreur de réservation" : "Booking failed. Please try again.");
        setError(msg);
        // Iter 353 · Feb 13 2026 · Marisol Simoes bug — the red error
        // banner was rendered at the TOP of the modal (line ~3296), but
        // on tall mobile modals the client has already scrolled down to
        // the summary. She kept tapping "Book Appointment", the request
        // kept failing, and she saw nothing. Fix: raise it as a toast
        // that's visible over anything, AND scroll the banner back into
        // view so the reason is unmissable.
        try {
          toast.error(msg, { duration: 8000 });
        } catch { /* noop */ }
        try {
          setTimeout(() => {
            const el = document.querySelector('[data-testid="booking-error"]');
            if (el?.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }, 80);
        } catch { /* noop */ }
        // Iter 353b · Booking concierge fallback — mirror the LMC alert.
        // After the SECOND consecutive failure, ping the team so they can
        // reach out and book manually before the client gives up.
        const newFailCount = bookFailCount + 1;
        setBookFailCount(newFailCount);
        if (newFailCount >= 2 && !conciergeNotified) {
          setConciergeNotified(true);
          try {
            fetch(`${API}/booking-fallback-notify`, {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              keepalive: true,
              body: JSON.stringify({
                client_name: `${customerInfo.first_name || ""} ${customerInfo.last_name || ""}`.trim(),
                client_email: customerInfo.email || "",
                client_phone: customerInfo.phone || "",
                location_id: selectedLocation?.id || "",
                location_name: selectedLocation?.name || "",
                service_name: (selectedServices.map(s => s.name).filter(Boolean).join(" + ")) || packageInfo?.name || "",
                date: selectedDate?.date || "",
                start_time: selectedTime || "",
                attempt_count: newFailCount,
                last_error: msg,
              }),
            }).catch(() => {});
          } catch { /* noop */ }
        }
      }
      // Iter 265 · Booking-submit failure — the highest-severity error we
      // capture. Fires for both slot conflicts (409) and any other reject
      // from /api/square/book so we spot broken booking journeys quickly.
      logBookingError({
        code: status === 409 ? "square-book-slot-conflict" : "square-book-failed",
        message: detail || "Square booking rejected",
        step: 5,
        location_id: selectedLocation?.id,
        service_ids: selectedServices.map(s => s.id),
        prize_code: prefillPrizeCode,
        prize_title: prefillPrizeTitle,
        email: customerInfo?.email,
        phone: customerInfo?.phone,
        extra: {
          status: status || null,
          selected_date: selectedDate?.date || null,
          selected_time: selectedTime || null,
          service_name: selectedServices.map(s => s.name).filter(Boolean).join(" + ") || packageInfo?.name || null,
          server_message: err?.response?.data?.message || err?.response?.data?.detail || null,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 0) return !!selectedLocation;
    if (step === 1) return selectedServices.length > 0;
    if (step === 2) return !!selectedDate;
    if (step === 3) return !!selectedTime;
    if (step === 4) return customerInfo.first_name?.trim() && customerInfo.last_name?.trim() && customerInfo.email?.trim() && phoneDigitsOk(customerInfo.phone);
    if (step === 5) return true;
    return false;
  };

  // Oct 2026 · Bianca Renaud — promo "Book now" fast lane. When the
  // treatment came pre-chosen (packageInfo) the client already said what
  // she wants, so a time tap goes straight on: skip "add another?", and
  // skip the details form too when we already know her.
  const promoFastLane = !!(packageInfo && (packageInfo.name || packageInfo.searchQuery));
  const fastLaneDoneRef = useRef(null);

  // Oct 2026 · Karine ask — Stuck Client Alert. If the client sits on one
  // step for 2 minutes we quietly tell the team so someone can call.
  const stuckSessionRef = useRef(null);
  const stuckAlertedRef = useRef(false);
  const stepStartedRef = useRef(Date.now());
  useEffect(() => { stepStartedRef.current = Date.now(); }, [step]);
  useEffect(() => {
    if (!isOpen) { stuckSessionRef.current = null; stuckAlertedRef.current = false; return; }
    if (!stuckSessionRef.current) stuckSessionRef.current = `bk_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
    const id = setInterval(() => {
      if (stuckAlertedRef.current || booking || step >= 6 || document.hidden) return;
      const secs = Math.round((Date.now() - stepStartedRef.current) / 1000);
      if (secs < 120) return;
      stuckAlertedRef.current = true;
      fetch(`${API}/booking-stuck-notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        keepalive: true,
        body: JSON.stringify({
          session_id: stuckSessionRef.current,
          client_name: `${customerInfo.first_name || ""} ${customerInfo.last_name || ""}`.trim(),
          client_email: customerInfo.email || rebookEmail || prefillEmail || "",
          client_phone: customerInfo.phone || "",
          step, step_label: STEPS[step] || String(step), seconds_on_step: secs,
          location_name: selectedLocation?.name || "",
          service_name: (selectedServices.map(s => s.name).filter(Boolean).join(" + ")) || packageInfo?.name || packageInfo?.searchQuery || "",
          date: selectedDate?.date || "",
          page: typeof window !== "undefined" ? window.location.pathname + window.location.search : "",
        }),
      }).catch(() => {});
    }, 10000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, step, booking, customerInfo, selectedLocation, selectedServices, selectedDate, rebookEmail]);
  useEffect(() => {
    if (!(step === 3 || (step === 2 && selectedDate)) || !selectedTime || !promoFastLane) return;
    if (fastLaneDoneRef.current === selectedTime) return;  // client came Back on purpose
    fastLaneDoneRef.current = selectedTime;
    const known = autoFilled && customerInfo.first_name?.trim() && customerInfo.last_name?.trim() && customerInfo.email?.trim() && customerInfo.phone?.trim();
    const t = setTimeout(() => setStep(known ? 5 : 4), 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, selectedTime, selectedDate, promoFastLane]);

  const goNext = () => {
    if (step === 4 && !canProceed()) {
      const missing = [];
      if (!customerInfo.first_name?.trim()) missing.push(isFr ? "prénom" : "first name");
      if (!customerInfo.last_name?.trim()) missing.push(isFr ? "nom" : "last name");
      if (!customerInfo.email?.trim()) missing.push(isFr ? "courriel" : "email");
      if (!customerInfo.phone?.trim()) missing.push(isFr ? "téléphone" : "phone");
      else if (!phoneDigitsOk(customerInfo.phone)) {
        toast.error(isFr ? "Le numéro de téléphone doit avoir 10 chiffres (ex. 613-850-0805)." : "Please enter a 10-digit phone number (e.g. 613-850-0805).");
        return;
      }
      toast.error(isFr ? `Veuillez remplir: ${missing.join(", ")}` : `Please fill in: ${missing.join(", ")}`);
      return;
    }
    if (step === 5) {
      // Iter 409 · Layer-1 confirmation guardrail. Instead of firing
      // handleBook() immediately, open the location-confirm modal.
      // The user must tap "Yes, book at <Location>" to proceed.
      setShowLocationConfirm(true);
    } else {
      setStep(s => s + 1);
    }
  };

  // ─── ADD-ON UPSELL: fetch catalog lazily when reaching step 5 ─────
  // We don't fetch on mount because most sessions never reach step 5
  // (drop-off curve). Lazy-load on the first time the review step is
  // shown so the modal stays fast on earlier steps.
  useEffect(() => {
    if (step !== 5 || addonCatalog !== null) return;
    let cancelled = false;
    // Filter add-ons by the client's chosen location so Ottawa-only
    // treatments like the Facial Lymphatic Drain never surface for a
    // Kanata / Carleton Place booking (Sherralee bug · Sept 4 2026).
    const _locId = String(selectedLocation?.id || lockedLocationId || "");
    const url = new URL(`${process.env.REACT_APP_BACKEND_URL}/api/addons`);
    if (_locId) url.searchParams.set("location_id", _locId);
    fetch(url.toString())
      .then((r) => r.json())
      .then((d) => { if (!cancelled) setAddonCatalog(d?.addons || []); })
      .catch(() => { if (!cancelled) setAddonCatalog([]); });
    return () => { cancelled = true; };
  }, [step, addonCatalog, selectedLocation, lockedLocationId]);

  // Map the selected services to add-on compatibility slugs so we only
  // surface enhancements that actually pair (RF clients shouldn't see
  // "Hydrafacial Extractions"). The mapping is intentionally generous —
  // we'd rather show 1 extra than hide a relevant one.
  const compatibleAddonSlugs = useMemo(() => {
    if (!Array.isArray(selectedServices) || selectedServices.length === 0) return [];
    const tags = new Set();
    for (const s of selectedServices) {
      const text = `${s?.category || ""} ${s?.name || ""} ${s?.package_label || ""}`.toLowerCase();
      if (/(rf|radio.?freq)/.test(text)) tags.add("rf-microneedling");
      if (/microneedl/.test(text)) tags.add("rf-microneedling");
      if (/hydra/.test(text)) tags.add("hydrafacial");
      if (/facial/.test(text)) tags.add("facial");
      if (/(laser|ipl|genesis|hair.?removal)/.test(text)) tags.add("laser");
      if (/botox|tox/.test(text)) tags.add("botox");
      if (/filler|sculptra|booster/.test(text)) tags.add("filler");
    }
    return Array.from(tags);
  }, [selectedServices]);

  // Oct 7 2026 · Karine ask — "look at every single add-on the
  // aestheticians have and offer them as a dropdown add-on." Detect
  // aesthetician services (facial, hydrafacial, chemical peel, laser,
  // microneedling, dermaplaning, lymphatic massage, LED, etc.) so we
  // can show the FULL add-on menu for those visits instead of a
  // compatibility-filtered subset. For injectables (botox, filler)
  // we keep the existing compat filter.
  const isAestheticianVisit = useMemo(() => {
    if (!Array.isArray(selectedServices) || selectedServices.length === 0) return false;
    return selectedServices.some((s) => {
      const t = `${s?.category || ""} ${s?.name || ""} ${s?.package_label || ""}`.toLowerCase();
      return /(hydrafacial|hydra.?facial|chemical.?peel|peel|laser|ipl|genesis|microneedl|dermaplan|facial|lymphatic|led|éminence|eminence|skin.?tightening|crown.?plat|crown.?deluxe|co2.?lift|hair.?removal)/.test(t)
          && !/(botox|filler|sculptra|radiesse|redensity|lip.?filler|neuromodulator|sculpt|boost)/.test(t);
    });
  }, [selectedServices]);

  // Filtered + sorted list — for AESTHETICIAN visits show every enabled
  // add-on in the catalog (so clients can pick ANY enhancement, not just
  // the compat-tagged ones); for injectable visits keep the compat filter
  // so Botox clients don't see "Lymphatic Drain" etc.
  const suggestedAddons = useMemo(() => {
    if (!Array.isArray(addonCatalog)) return [];
    const base = addonCatalog.filter((a) => a.enabled !== false);
    if (isAestheticianVisit) return base;
    if (compatibleAddonSlugs.length === 0) return [];
    return base.filter((a) => {
      const compat = Array.isArray(a.compatible_with) ? a.compatible_with : [];
      return compat.some((c) => compatibleAddonSlugs.includes(c));
    });
  }, [addonCatalog, compatibleAddonSlugs, isAestheticianVisit]);

  const addAddonToVisit = async (a) => {
    setAddonsAdded((s) => ({ ...s, [a.slug]: "saving" }));
    const noteSuffix = isFr
      ? `Veut ajouter "${a.name}" (${a.duration_min} min · $${a.price_from}${a.price_to ? `–$${a.price_to}` : ""}) à son rendez-vous.`
      : `Wants to add "${a.name}" (${a.duration_min} min · $${a.price_from}${a.price_to ? `–$${a.price_to}` : ""}) to their visit.`;
    try {
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: `${customerInfo.first_name} ${customerInfo.last_name}`.trim() || a.name,
          email: customerInfo.email || "",
          phone: customerInfo.phone || "",
          area: a.name,
          message: noteSuffix,
          source: `booking-upsell:${a.slug}`,
        }),
      });
      // Fire analytics ping (best-effort, never blocks UI)
      try {
        await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/commerce/upsells/click`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${localStorage.getItem("client_token") || ""}`,
          },
          body: JSON.stringify({
            addon_id: a.slug,
            addon_name: a.name,
            context: "booking-review",
            treatment_category: compatibleAddonSlugs[0] || "",
          }),
        });
      } catch { /* analytics best-effort */ }
      setAddonsAdded((s) => ({ ...s, [a.slug]: "sent" }));
      toast.success(isFr ? "Ajouté — votre fournisseur sera notifié" : "Added — your provider will be notified");
    } catch {
      setAddonsAdded((s) => {
        const next = { ...s }; delete next[a.slug]; return next;
      });
      toast.error(isFr ? "Impossible d'ajouter, réessayez" : "Couldn't add — please try again");
    }
  };

  // Auto-add an add-on when the booking is opened with `prefillAddonSlug`
  // — e.g. from the LaserAndSkinPage "+$45 Lymphatic Drain" toggle. Fires
  // exactly once per modal-open lifecycle, the first time the catalog is
  // loaded AND the matching slug is in the suggested list. Wrapped behind
  // `addonsAdded` check so it can't double-fire after a re-render.
  const [autoAddonFired, setAutoAddonFired] = useState(false);
  // Sept 4 2026 · Sherralee bug: if the caller wanted an Ottawa-only add-on
  // (e.g. facial-lymphatic-drain-addon) but this booking is Kanata / CP,
  // we can't attach it here — the catalog filters it out server-side. We
  // stash the slug so the confirmation-step can queue a reception alert
  // asking Karine to text the client about a separate Ottawa visit.
  const [pendingOttawaOnlyAddon, setPendingOttawaOnlyAddon] = useState(null);
  useEffect(() => {
    if (!isOpen) { setAutoAddonFired(false); setPendingOttawaOnlyAddon(null); return; }
  }, [isOpen]);
  useEffect(() => {
    if (autoAddonFired) return;
    if (!prefillAddonSlug) return;
    if (!Array.isArray(addonCatalog) || addonCatalog.length === 0) return;
    const match = addonCatalog.find((a) => a.slug === prefillAddonSlug);
    setAutoAddonFired(true);
    if (!match) {
      // Slug missing from the location-filtered catalog → Ottawa-only.
      // Remember it so the confirmation step can nudge reception.
      setPendingOttawaOnlyAddon(prefillAddonSlug);
      return;
    }
    if (addonsAdded[match.slug]) return;
    addAddonToVisit(match);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillAddonSlug, addonCatalog, autoAddonFired]);

  // Book-With-Prize (Feb 27, 2026) — when the client clicks "Book Now"
  // on an active Crown Spin prize in their portal, we auto-select a
  // matching qualifying treatment from the catalog so they never see
  // a "wait, does this treatment count?" moment. Fires once per modal
  // open. If no keyword matches, we just leave the picker empty.
  const [autoPrizeFired, setAutoPrizeFired] = useState(false);
  useEffect(() => {
    if (!isOpen) { setAutoPrizeFired(false); return; }
  }, [isOpen]);
  useEffect(() => {
    if (autoPrizeFired) return;
    if (!prefillPrizeCode) return;
    const qual = prefillQualifyingTreatments;
    if (!Array.isArray(catalogServices) || catalogServices.length === 0) return;
    // qual === null → any service. qual === [] → any service. Only
    // auto-pick when we have specific keywords to match against.
    if (!Array.isArray(qual) || qual.length === 0) { setAutoPrizeFired(true); return; }
    if ((selectedServices || []).length > 0) { setAutoPrizeFired(true); return; }

    // Iter 164 fix — RANKED matching so "FREE LED Therapy" doesn't
    // pre-select "AUGUST PROMO Laser Genesis" just because "facial"
    // was a substring. Iter 265 fix (Jessica Patterson · Oct 2026) —
    // relaxed the promo penalty so seasonal promos (e.g. "Summer 11s
    // and Forehead Promo Neuromodulator") can still be picked when
    // they legitimately match a qualifying keyword.
    // Scoring, higher wins:
    //   +100  service name matches a token from the prize TITLE
    //         (e.g. "LED" from "FREE LED Therapy" hits "LED Light Therapy")
    //   + 50  exact case-insensitive match with a qualifying keyword
    //   + 25  service name STARTS WITH a qualifying keyword
    //   + 20  service name CONTAINS a qualifying keyword
    //   - 15  service name looks like a marketing promo/bundle
    //         (contains PROMO / PACKAGE / SPECIAL / BUNDLE / DEAL)
    //   - 60  service name is a stale placeholder (starts with "-")
    const kws = qual.map((k) => (k || "").toLowerCase()).filter(Boolean);
    const titleTokens = (prefillPrizeTitle || "")
      .toLowerCase()
      .replace(/free|complimentary|off|\d+%|\$\d+/g, " ")
      .split(/[^a-z0-9]+/)
      .filter((t) => t.length >= 3);   // drop tiny stopwords

    const scoreService = (s) => {
      const nm = (s?.name || "").toLowerCase();
      if (!nm) return -999;
      let score = 0;
      for (const t of titleTokens) if (nm.includes(t)) score += 100;
      for (const k of kws) {
        if (!k) continue;
        if (nm === k) score += 50;
        else if (nm.startsWith(k)) score += 25;
        else if (nm.includes(k)) score += 20;
      }
      if (/\b(promo|package|special|bundle|deal)\b/.test(nm)) score -= 15;
      if ((s?.name || "").trim().startsWith("-")) score -= 60;
      return score;
    };

    let best = null;
    let bestScore = -Infinity;
    for (const s of catalogServices) {
      const sc = scoreService(s);
      if (sc > bestScore) { best = s; bestScore = sc; }
    }
    // Require a positive score — pure "contains" of a generic keyword
    // like "facial" without any title-token hit is not confident enough.
    if (best && bestScore >= 10) {
      setSelectedServices([best]);
      toast.success((isFr
        ? "Traitement admissible pré-sélectionné pour votre prix Crown Spin ✓"
        : "Qualifying treatment pre-selected for your Crown Spin prize ✓"));
    } else {
      // Iter 265 · Auto-select failed — the prize is likely a free
      // add-on (e.g. Brow Lift) whose qualifying keywords don't match
      // any bookable service directly. Nudge the client to pick their
      // paid treatment manually so they aren't stuck at an empty picker.
      toast((isFr
        ? `Choisissez votre traitement payant ci-dessous — votre bonus « ${prefillPrizeTitle || "Crown Spin"} » sera ajouté à l'arrivée.`
        : `Pick your paid treatment below — your "${prefillPrizeTitle || "Crown Spin"}" bonus will be added at check-in.`),
        { icon: "🎁", duration: 6000 });
      logBookingError({
        code: "auto-prize-select-failed",
        message: "No qualifying service matched prize keywords/title tokens",
        step: 1,
        prize_code: prefillPrizeCode,
        prize_title: prefillPrizeTitle,
        email: prefillEmail,
        extra: {
          catalog_size: catalogServices.length,
          best_score: bestScore,
          best_service_name: best?.name || null,
          qualifying_treatments: (prefillQualifyingTreatments || []).slice(0, 6).join("|"),
        },
      });
    }
    setAutoPrizeFired(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillPrizeCode, prefillPrizeTitle, prefillQualifyingTreatments, catalogServices]);

  // ====================================================================
  // BOOKING ENHANCEMENTS — Helpers
  // ====================================================================
  const LOCATION_NAMES = useMemo(() => ({ "1": "Carleton Place", "2": "Kanata", "3": "Ottawa" }), []);

  // Square direct-booking fallback URLs — when our internal flow fails for any
  // reason, clients can still book via Square's hosted site for each clinic.
  const SQUARE_DIRECT_URLS = useMemo(() => ({
    "1": "https://precision-nurse-injectionist.square.site/",
    "2": "https://karine-lauzon-rn.square.site/",
    "3": "https://karine-lauzon-rn-ottawa.square.site/",
  }), []);

  // Reusable backup-booking block. Shown inline whenever the internal flow
  // hits an error and as a permanent footer link in the modal. Lets clients
  // book directly via Square's hosted site for each clinic.
  const BookDirectFallback = useCallback(({ compact = false, contextLocationId = null }) => {
    const intro = isFr
      ? "Vous pouvez aussi réserver directement sur Square:"
      : "You can also book directly on Square:";
    // If we know which clinic the client picked, show just that one big button + the others below
    const primary = contextLocationId && SQUARE_DIRECT_URLS[contextLocationId];
    // When the flow is location-locked (Ottawa-only treatments like
    // HydraFacial / RF / Laser, or the Tanya flow), the Square fallback
    // should only surface the locked clinic too — otherwise the bypass
    // re-exposes Carleton Place + Kanata.
    const lockedSquareIds = (isTanya || isLocationLocked) ? [lockedLocationId] : ["1", "2", "3"];
    if (compact) {
      return (
        <div className="mt-3 p-3 rounded-xl border border-amber-500/30 bg-amber-500/5" data-testid="book-direct-fallback-compact">
          <p className="text-amber-300 text-xs font-semibold mb-2">{intro}</p>
          <div className="flex flex-col sm:flex-row gap-2">
            {lockedSquareIds.map((id) => (
              <a
                key={id}
                href={SQUARE_DIRECT_URLS[id]}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-amber-500/50 transition-all text-xs text-white"
                data-testid={`book-direct-${id}-compact`}
              >
                <span className="font-medium">{LOCATION_NAMES[id]}</span>
                <ExternalLink className="w-3 h-3 text-amber-400 flex-shrink-0" />
              </a>
            ))}
          </div>
        </div>
      );
    }
    return (
      <div className="mt-4 p-4 rounded-2xl border border-amber-500/40 bg-gradient-to-br from-amber-500/10 to-orange-500/5" data-testid="book-direct-fallback">
        <p className="text-amber-300 text-[10px] font-bold uppercase tracking-wider mb-1">
          {isFr ? "Solution de secours" : "Backup option"}
        </p>
        <p className="text-white text-sm font-semibold mb-1">
          {isFr ? "Réserver directement sur Square" : "Book direct on Square"}
        </p>
        <p className="text-zinc-400 text-xs leading-relaxed mb-3">
          {isFr
            ? "Si notre système réagit lentement, vous pouvez toujours réserver directement sur la page Square officielle de chaque clinique."
            : "If our system is slow, you can always book directly on each clinic's official Square page."}
        </p>
        {primary && (
          <a
            href={primary}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-between gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-zinc-900 font-bold text-sm mb-2 transition-all"
            data-testid={`book-direct-primary-${contextLocationId}`}
          >
            <span>
              {isFr ? "Réserver à" : "Book at"} {LOCATION_NAMES[contextLocationId]} →
            </span>
            <ExternalLink className="w-4 h-4 flex-shrink-0" />
          </a>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {lockedSquareIds.filter((id) => id !== contextLocationId).map((id) => (
            <a
              key={id}
              href={SQUARE_DIRECT_URLS[id]}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-between gap-2 px-3 py-2.5 rounded-xl bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 hover:border-amber-500/50 transition-all text-sm text-white"
              data-testid={`book-direct-${id}`}
            >
              <span className="font-medium">{LOCATION_NAMES[id]}</span>
              <ExternalLink className="w-3.5 h-3.5 text-amber-400 flex-shrink-0" />
            </a>
          ))}
        </div>
      </div>
    );
  }, [isFr, LOCATION_NAMES, SQUARE_DIRECT_URLS, isTanya, isLocationLocked, lockedLocationId]);

  // #3 Smart recommender removed — pairings UI was misleading and is no
  // longer rendered anywhere in the booking flow.

  // #1 Fastest Available Slot — single server-side call. Backend pulls the
  // cached available-date list across all 3 clinics, probes Square in
  // parallel, and returns the top 3 earliest open slots. Replaces the
  // previous fragile multi-call client flow that was failing on mobile.
  const findFastestSlot = useCallback(async () => {
    if (selectedServices.length === 0) {
      toast.error(isFr ? "Choisissez au moins un traitement" : "Pick at least one treatment first");
      logBookingError({
        code: "fastest-slot-no-service",
        message: "User clicked Find Fastest Slot without selecting a treatment",
        step: 1,
        location_id: selectedLocation?.id,
        prize_code: prefillPrizeCode,
        prize_title: prefillPrizeTitle,
        email: prefillEmail,
      });
      return;
    }
    setFindingFastest(true);
    setFastTrackOptions(null);
    setFastTrackProgress(isFr ? "Recherche en cours…" : "Searching live availability…");
    try {
      const duration = totalDurationMinutes || 15;
      const res = await axios.post(
        `${API}/booking/fast-track-search`,
        {
          service_duration_minutes: duration,
          location_ids: ["1", "2", "3"],
          max_options: 3,
        },
        { timeout: 35000 }
      );

      const opts = (res?.data?.options || []).map((o) => ({
        location: { id: o.location_id, name: o.location_name || LOCATION_NAMES[o.location_id] || o.location_id },
        dateObj: {
          date: o.date,
          day_name: o.day_name,
          formatted: o.formatted,
        },
        time: o.start_time,
      }));

      // Remember the call_id so we can log "picked" / "booked" events for conversion analytics.
      const callId = res?.data?.call_id || null;
      setFastTrackCallId(callId);

      if (opts.length === 0) {
        toast.error(
          isFr
            ? "Aucun créneau trouvé — réessayez ou choisissez manuellement"
            : "No openings right now — try again or pick manually"
        );
        return;
      }

      if (res?.data?.reason === "fallback_dates") {
        toast(
          isFr
            ? "Heure non verrouillée — sélectionnez une date pour voir les heures"
            : "Times couldn't be locked — pick a date to see open times",
          { duration: 5000 }
        );
      } else if (res?.data?.reason === "partial") {
        toast(
          isFr
            ? `${opts.length} créneau${opts.length > 1 ? "x" : ""} confirmé${opts.length > 1 ? "s" : ""} trouvé${opts.length > 1 ? "s" : ""}`
            : `Found ${opts.length} confirmed slot${opts.length > 1 ? "s" : ""}`,
          { duration: 4000 }
        );
      }

      setFastTrackOptions(opts);
    } catch (e) {
      const msg = errMsg(e, isFr ? "Erreur — réessayez" : "Search failed — please try again");
      toast.error(msg);
      logBookingError({
        code: "fast-track-search-failed",
        message: msg,
        step: 1,
        location_id: selectedLocation?.id,
        service_ids: selectedServices.map(s => s.id),
        prize_code: prefillPrizeCode,
        prize_title: prefillPrizeTitle,
        email: prefillEmail,
        extra: {
          status: e?.response?.status || null,
          server_message: e?.response?.data?.message || e?.response?.data?.detail || null,
        },
      });
    } finally {
      setFastTrackProgress(null);
      setFindingFastest(false);
    }
  }, [selectedServices, totalDurationMinutes, isFr, LOCATION_NAMES]);

  // User picks one of the 3 Fast Track options → snap location/date/time and jump to info step.
  const pickFastTrackOption = useCallback((opt) => {
    if (!opt) return;
    // Fire-and-forget analytics — never block the UI on this.
    if (fastTrackCallId) {
      axios.post(
        `${API}/booking/fast-track-event`,
        {
          call_id: fastTrackCallId,
          event: "picked",
          picked_location_id: opt.location.id,
          picked_date: opt.dateObj.date,
          picked_time: opt.time || null,
        },
        { timeout: 8000 }
      ).catch(() => { /* swallow — analytics shouldn't error to user */ });
    }
    setSelectedLocation(opt.location);
    setSelectedDate(opt.dateObj);
    if (opt.time) {
      setSelectedTime(opt.time);
      setFastTrackOptions(null);
      setStep(4);
      toast.success(
        isFr
          ? `Créneau réservé: ${opt.location.name} · ${opt.dateObj.day_name || opt.dateObj.date}`
          : `Slot selected: ${opt.location.name} · ${opt.dateObj.day_name || opt.dateObj.date}`
      );
    } else {
      setFastTrackOptions(null);
      setStep(3);
      toast(
        isFr
          ? `${opt.location.name} · choisissez une heure ci-dessous`
          : `${opt.location.name} · pick a time below`
      );
    }
  }, [isFr, fastTrackCallId]);

  // #4 Returning client one-tap rebook
  const lookupRebook = useCallback(async () => {
    if (!rebookEmail.trim()) return;
    setRebookLoading(true);
    setRebookProfile(null);
    setLastBooking(null);
    setLastBookingDismissed(false);
    try {
      // Oct 2026 — accept a phone number too (new phone / cleared browser).
      // Resolve phone → email first, then run the usual email lookups.
      let lookupEmail = rebookEmail.trim();
      const _digits = lookupEmail.replace(/\D/g, "");
      if (!lookupEmail.includes("@") && _digits.length >= 10) {
        const byPhone = await axios.get(`${API}/client/lookup?phone=${encodeURIComponent(_digits.slice(-10))}`, { timeout: 15000 }).catch(() => ({ data: { found: false } }));
        if (!byPhone.data?.found) {
          toast.error(isFr ? "Aucun client trouvé avec ce numéro" : "No client found with that phone number");
          return;
        }
        const c = byPhone.data.client || {};
        setCustomerInfo(p => ({ ...p, first_name: c.first_name || "", last_name: c.last_name || "", email: c.email || "", phone: c.phone || rebookEmail.trim() }));
        setAutoFilled(true);
        if (!c.email) { setRebookProfile({ found: true }); return; }
        lookupEmail = c.email;
      }
      // Fetch BOTH the legacy client-profile (visits + Crown eligibility)
      // AND the new last-booking endpoint (with service_segments for exact
      // re-creation). The new endpoint powers the champagne One-Tap card.
      const [profileRes, lastRes] = await Promise.all([
        axios.get(`${API}/booking/client-profile?email=${encodeURIComponent(lookupEmail)}`, { timeout: 15000 }),
        axios.get(`${API}/client/last-booking?email=${encodeURIComponent(lookupEmail)}`, { timeout: 15000 }).catch(() => ({ data: { found: false } })),
      ]);
      const data = profileRes.data || {};
      if (!data.found) {
        toast.error(isFr ? "Aucun client trouvé avec ce courriel" : "No client found with that email");
        return;
      }
      setRebookProfile(data);
      if (data.client) {
        setCustomerInfo(p => ({
          ...p,
          first_name: data.client.first_name || "",
          last_name: data.client.last_name || "",
          email: data.client.email || lookupEmail,
          phone: data.client.phone || "",
        }));
        setAutoFilled(true);
      }
      // Surface the One-Tap Rebook champagne card if a usable last booking exists.
      if (lastRes?.data?.found && lastRes.data?.last?.location_id) {
        setLastBooking(lastRes.data.last);
      }
    } catch {
      toast.error(isFr ? "Échec de la recherche" : "Lookup failed");
    } finally {
      setRebookLoading(false);
    }
  }, [rebookEmail, isFr]);

  const applyRebookLastVisit = useCallback(() => {
    const lb = rebookProfile?.last_booking;
    if (!lb) return;
    const id = lb.location_id;
    if (id && LOCATION_NAMES[id]) {
      setSelectedLocation({ id, name: LOCATION_NAMES[id] });
    }
    setStep(1);
    toast.success(
      isFr ? "Rendez-vous précédent chargé — confirmez les traitements" : "Last visit loaded — confirm your treatments"
    );
  }, [rebookProfile, isFr, LOCATION_NAMES]);

  // One-Tap Rebook — reconstructs the user's previous selection (location +
  // services) and jumps STRAIGHT to date selection. Uses service_variation_id
  // for exact matches; falls back to fuzzy name match for legacy bookings
  // that pre-date the service_segments field. Called from the Welcome Back
  // card on step 0.
  const usualAppliedRef = useRef(false);
  useEffect(() => {
    if (prefillBookingType !== "usual" || usualAppliedRef.current || !lastBooking || !isOpen) return;
    const locId = String(lastBooking.location_id || "");
    if (!LOCATION_NAMES[locId]) return;
    if (!selectedLocation || String(selectedLocation.id) !== locId) {
      setSelectedLocation({ id: locId, name: LOCATION_NAMES[locId] });
      setStep(1);
      return;
    }
    if (!catalogServices || catalogServices.length === 0) return;
    usualAppliedRef.current = true;
    applyLastBookingOneTap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prefillBookingType, lastBooking, catalogServices, isOpen, selectedLocation]);

  const applyLastBookingOneTap = useCallback(() => {
    if (!lastBooking) return;
    const locId = lastBooking.location_id;
    if (locId && LOCATION_NAMES[locId]) {
      setSelectedLocation({ id: locId, name: LOCATION_NAMES[locId] });
    }
    // Match services from the catalog using segments first, then names
    const segs = lastBooking.service_segments || [];
    const names = (lastBooking.services || []).map(s => (s?.name || "").toLowerCase().trim()).filter(Boolean);
    const picks = [];
    if (catalogServices && catalogServices.length > 0) {
      if (segs.length > 0) {
        for (const seg of segs) {
          const match = catalogServices.find(c => c.variation_id === seg.service_variation_id);
          if (match) picks.push(match);
        }
      }
      if (picks.length === 0 && names.length > 0) {
        for (const n of names) {
          const match = catalogServices.find(c => (c.name || "").toLowerCase().trim() === n);
          if (match && !picks.find(p => p.variation_id === match.variation_id)) picks.push(match);
        }
      }
    }
    if (picks.length > 0) {
      setSelectedServices(picks);
      // Jump to date selection — step 2 in code (= visual step 3)
      setStep(2);
      toast.success(
        isFr
          ? `Votre habituel chargé : ${picks.map(p => p.name).join(" + ")}`
          : `Loaded your usual: ${picks.map(p => p.name).join(" + ")}`
      );
    } else {
      // Couldn't match — at least set location and let the user pick services
      setStep(1);
      toast(isFr ? "Lieu chargé — choisissez vos soins" : "Location loaded — pick your treatments");
    }
    setLastBookingDismissed(true);
  }, [lastBooking, catalogServices, isFr, LOCATION_NAMES]);

  // #6 Crown Reserve upsell — fetched after email is set in step 4
  useEffect(() => {
    const email = customerInfo?.email?.trim();
    if (!email || step < 4) return;
    if (clientProfile?.email_lc === email.toLowerCase()) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/booking/client-profile?email=${encodeURIComponent(email)}`, { timeout: 12000 });
        if (!cancelled) {
          setClientProfile({ ...res.data, email_lc: email.toLowerCase() });
        }
      } catch { /* silent */ }
    })();
    return () => { cancelled = true; };
  }, [customerInfo?.email, step, clientProfile?.email_lc]);

  // 15-min pending point-redemption hold — look up the signed-in client's
  // active credit so we can auto-surface + auto-apply it at checkout.
  useEffect(() => {
    if (step < 4) return undefined;
    const token = typeof window !== "undefined" ? localStorage.getItem("client_token") : null;
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API}/api/client/credits`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const data = await res.json();
        const now = Date.now();
        const live = (data?.credits || [])
          .filter((c) => c?.pending_booking_until && Date.parse(c.pending_booking_until) > now)
          .sort((a, b) => Date.parse(a.pending_booking_until) - Date.parse(b.pending_booking_until));
        if (!cancelled) setPendingHold(live[0] || null);
      } catch { /* silent — anonymous or offline */ }
    })();
    return () => { cancelled = true; };
  }, [step, customerInfo?.email]);

  // === Treatment-step UX enhancements (#1, #3, #4) ===
  // Ref to the sticky "Your Selection" tray so we can smooth-scroll to it
  // the moment the client picks their FIRST treatment — gives a clear
  // "yes, that worked!" confirmation right next to their tap.
  const selectionTrayRef = useRef(null);
  const prevSelectedCountRef = useRef(0);
  useEffect(() => {
    const prev = prevSelectedCountRef.current;
    const curr = selectedServices.length;
    // Auto-scroll on the very FIRST selection (0 → 1), not on every add,
    // so subsequent picks don't yank the page around.
    if (prev === 0 && curr === 1 && selectionTrayRef.current) {
      try {
        selectionTrayRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch { /* legacy browsers — ignore */ }
    }
    prevSelectedCountRef.current = curr;
  }, [selectedServices.length]);

  // #4 — Smart category collapse: once a client picks something from a
  // category, fold that category so the list stays scannable. They can
  // re-expand with a tap. Categories with NO selections stay expanded.
  // Oct 20 2026 (Marc): categories start COLLAPSED — promos first, then
  // category headers; open one at a time. Search / quick filter expands all.
  const [openCategory, setOpenCategory] = useState(null);
  const [collapsedCategories, setCollapsedCategories] = useState(() => new Set());
  const toggleCategoryCollapse = useCallback((category) => {
    setOpenCategory((prev) => (prev === category ? null : category));
    setCollapsedCategories((prev) => { const next = new Set(prev); next.delete(category); return next; });
  }, []);
  const categoriesAutoExpanded = Boolean((serviceSearch || "").trim()) || quickFilter !== "all";
  const PROMO_PREVIEW = 13;  // the Winter flyer block (Makeovers 1-9 etc.) always visible
  const [showAllPromos, setShowAllPromos] = useState(false);
  // Oct 20 2026 (Marc's flow): location → search → promos → 6 big category tiles → one category's treatments
  // Oct 25 2026 · Karine: same photo-realistic look as the home-page boxes.
  const TILE_PHOTO = { botox: "/backdrops/botox.jpg", fillers: "/backdrops/fillers.jpg", collagen: "/backdrops/microneedling.jpg", facials: "/backdrops/facials.jpg", laser: "/backdrops/laser-hair.jpg", more: "/backdrops/glow-boosters.jpg" };
  const CATEGORY_TILES = [
    { key: "botox", en: "Botox & Neuromodulators", fr: "Botox & neuromodulateurs", sub_en: "Face, neck & targeted areas", sub_fr: "Visage, cou et zones ciblées", cats: ["Botox & Neuromodulators"] },
    { key: "fillers", en: "Fillers & Skin Boosters", fr: "Fillers & skin boosters", sub_en: "Lips, volume & hydration", sub_fr: "Lèvres, volume et hydratation", cats: ["Fillers, Skin Boosters & Regenerative"] },
    { key: "collagen", en: "Collagen & Skin Firming", fr: "Collagène & fermeté", sub_en: "Microneedling, RF, lesions & light", sub_fr: "Microneedling, RF, lésions et lumière", cats: ["Microneedling & RF", "Skin Tightening, Lesions & Light Therapy"] },
    { key: "facials", en: "Facials & Skin Treatments", fr: "Soins du visage", sub_en: "Éminence, HydraFacial, Genesis & Icon IPL", sub_fr: "Éminence, HydraFacial, Genesis et IPL Icon", cats: ["Facials, Hydrafacial & Peels", "Icon IPL (Tanya · Ottawa)", "Laser Genesis & IPL", "Brows & Lashes"] },
    { key: "laser", en: "Laser Hair Removal", fr: "Épilation laser", sub_en: "By area or unlimited plan", sub_fr: "Par zone ou forfait illimité", cats: ["Laser Hair Removal"] },
    { key: "more", en: "Add-ons & More", fr: "Extras & autres", sub_en: "Add-ons and other treatments", sub_fr: "Extras et autres soins", cats: ["Add-Ons", "Other Treatments"] },
  ];
  const [activeTile, setActiveTile] = useState(null);
  const tileCount = (tile) => tile.cats.reduce((n, c) => n + (groupedServices[c]?.length || 0), 0);
  // Map: which categories contain at least one selected service?
  const categoriesWithSelection = useMemo(() => {
    const set = new Set();
    for (const svc of selectedServices) {
      const c = svc.category || svc.category_name || "";
      if (c) set.add(c);
    }
    return set;
  }, [selectedServices]);
  // Auto-collapse a category when its first item is picked. Only fires on
  // the transition (we keep track of which categories we've already
  // auto-collapsed so the client can manually re-expand without it
  // immediately re-collapsing on their next selection in the same group).
  const autoCollapsedRef = useRef(new Set());
  useEffect(() => {
    setCollapsedCategories((prev) => {
      let mutated = false;
      const next = new Set(prev);
      categoriesWithSelection.forEach((c) => {
        if (!autoCollapsedRef.current.has(c)) {
          autoCollapsedRef.current.add(c);
          if (!next.has(c)) {
            next.add(c);
            mutated = true;
          }
        }
      });
      return mutated ? next : prev;
    });
  }, [categoriesWithSelection]);

  // #2 Slot hold + 10-minute countdown — starts when we enter step 4
  useEffect(() => {
    if (step !== 4 || !selectedLocation || !selectedDate || !selectedTime) return;
    if (holdToken) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.post(`${API}/booking/hold-slot`, {
          location_id: selectedLocation.id,
          date: selectedDate.date,
          start_time: selectedTime,
          duration_minutes: totalDurationMinutes || 15,
          email: customerInfo?.email || null,
        }, { timeout: 8000 });
        if (!cancelled && res.data?.token) {
          setHoldToken(res.data.token);
          setHoldSecondsLeft(res.data.ttl_seconds || 600);
        }
      } catch { /* graceful — no hold, just no countdown */ }
    })();
    return () => { cancelled = true; };
  }, [step, selectedLocation, selectedDate, selectedTime, totalDurationMinutes, holdToken, customerInfo?.email]);

  // Tick the countdown each second while in steps 4/5
  useEffect(() => {
    if (holdSecondsLeft === null || holdSecondsLeft <= 0) return;
    if (step < 4 || step > 5) return;
    const i = setInterval(() => {
      setHoldSecondsLeft((s) => {
        if (s === null) return s;
        if (s <= 1) {
          // Nov 2 2026 · Linda Descarie couldn't complete a booking: the soft hold is
          // cosmetic (nothing is reserved in Square), yet expiry used to throw the
          // client back to the date step and wipe her progress. Now it just hides the
          // timer — Square re-validates the slot at the final Book step anyway.
          setHoldToken(null);
          return null;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(i);
  }, [holdSecondsLeft, step, isFr]);

  const releaseHold = useCallback(async () => {
    if (!holdToken) return;
    try {
      await axios.post(`${API}/booking/release-hold`, { token: holdToken }, { timeout: 5000 });
    } catch { /* best effort */ }
    setHoldToken(null);
    setHoldSecondsLeft(null);
  }, [holdToken]);

  const formatHoldClock = (s) => {
    if (s === null || s === undefined) return "";
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${String(sec).padStart(2, "0")}`;
  };



  const formatTime = (t) => {
    if (!t || typeof t !== "string" || !t.includes(":")) return "";
    const [h, m] = t.split(":");
    const hour = parseInt(h);
    if (Number.isNaN(hour)) return "";
    const ampm = hour >= 12 ? "PM" : "AM";
    const h12 = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
    return `${h12}:${m} ${ampm}`;
  };

  const formatDuration = (mins) => {
    if (!mins) return "";
    if (mins < 60) return `${mins} min`;
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    return m > 0 ? `${h}h ${m}min` : `${h}h`;
  };

  // Group dates by month for display
  const hasLindaDates = useMemo(
    () => (!selectedProvider || selectedProvider === "karine") && availableDates.some(d => d.provider_hint === "linda"),
    [availableDates, selectedProvider]
  );
  const visibleDates = useMemo(
    () => (hasLindaDates ? availableDates.filter(d => dateMatchesProvider(d, providerView)) : availableDates),
    [availableDates, hasLindaDates, providerView]
  );
  const displayProvider = selectedDate?.provider_hint === "linda" ? "linda" : (selectedProvider || "karine");
  const displayProviderName = displayProvider === "tanya"
    ? (isFr ? "Tanya Jackson, esthéticienne médicale" : "Tanya Jackson, Medical Aesthetician")
    : displayProvider === "linda"
      ? (isFr ? "Linda Lafrance, IP" : "Linda Lafrance, NP")
      : (isFr ? "Karine Lauzon, IA" : "Karine Lauzon, RN");

  const pickDate = (d) => {
    setSelectedDate(d);
    setSelectedTime(null);
    setTimeSlots([]);
    setLastUpdated(null);
    setError(null);
    setTodFilter("all");
    const dur = totalDurationMinutes || 15;
    const ck = getCacheKey(selectedLocation.id, d.date, dur);
    if (!isCacheValid(ck) || refreshKey > 0) setLoading(true);
    setStep(3);
  };

  const switchClinic = (id, mappedServices) => {
    const names = { "1": "Carleton Place", "2": "Kanata", "3": "Ottawa" };
    setSelectedLocation({ id, name: names[id] });
    if (Array.isArray(mappedServices) && mappedServices.length) setSelectedServices(mappedServices);
    setSelectedDate(null);
    setSelectedTime(null);
    setTimeSlots([]);
    setAvailableDates([]);
    setError(null);
    setProviderView("any");
    setStep(2);
  };

  const datesByMonth = useMemo(() => {
    const groups = {};
    visibleDates.forEach(d => {
      const month = d.date.substring(0, 7);
      if (!groups[month]) groups[month] = [];
      groups[month].push(d);
    });
    return groups;
  }, [visibleDates]);

  if (!isOpen) return null;

  const stepLabels = [
    isFr ? "Clinique" : "Location",
    isFr ? "Soin" : "Treatment",
    isFr ? "Date" : "Date",
    isFr ? "Heure" : "Time",
    isFr ? "Infos" : "Info",
    isFr ? "Révision" : "Review",
    isFr ? "Confirmé" : "Confirmed"
  ];

  return (
    <div className={`fixed inset-0 z-[200] flex items-end sm:items-center justify-center p-0 sm:p-4 transition-opacity duration-200 ${isVisible ? "opacity-100" : "opacity-0"}`} data-testid="booking-flow-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={handleClose} />
      <div className={`relative bg-zinc-900 border border-zinc-700 rounded-t-xl sm:rounded-xl w-full sm:max-w-2xl max-h-[92vh] sm:max-h-[90vh] overflow-hidden flex flex-col transform transition-all duration-200 ${isVisible ? "scale-100" : "scale-95"}`}>
        {/* Fast Track Chooser Overlay — appears when findFastestSlot returns options */}
        {fastTrackOptions && fastTrackOptions.length > 0 && (
          <div
            className="absolute inset-0 z-30 bg-zinc-950/96 backdrop-blur-sm flex flex-col"
            data-testid="fast-track-chooser"
          >
            <div className="flex items-center justify-between p-4 sm:p-5 border-b border-zinc-800">
              <div className="flex items-center gap-2 min-w-0">
                <Zap className="w-5 h-5 text-amber-400 flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-amber-300 text-[10px] sm:text-xs font-bold uppercase tracking-wider">
                    {isFr ? "Mode rapide" : "Fast Track"}
                  </p>
                  <h3 className="text-white text-base sm:text-lg leading-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {isFr ? "3 prochains créneaux disponibles" : "3 earliest available slots"}
                  </h3>
                </div>
              </div>
              <button
                onClick={() => setFastTrackOptions(null)}
                className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full transition-colors"
                data-testid="fast-track-close"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-3">
              <p className="text-zinc-400 text-xs sm:text-sm mb-1">
                {isFr
                  ? "Choisissez le créneau qui vous convient le mieux:"
                  : "Pick the slot that works best for you:"}
              </p>
              {fastTrackOptions.map((opt, i) => (
                <button
                  key={`${opt.location.id}-${opt.dateObj.date}-${opt.time}`}
                  onClick={() => pickFastTrackOption(opt)}
                  className="w-full text-left rounded-xl border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-orange-500/5 hover:from-amber-500/20 hover:to-orange-500/10 hover:border-amber-500/60 transition-all p-3 sm:p-4 flex items-center gap-3 group"
                  data-testid={`fast-track-option-${i}`}
                >
                  <div className="flex items-center justify-center w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-amber-500/20 text-amber-300 font-bold text-sm flex-shrink-0">
                    {i + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-semibold text-sm sm:text-base truncate">
                      {opt.dateObj.day_name
                        ? `${opt.dateObj.day_name}, ${opt.dateObj.date}`
                        : opt.dateObj.formatted || opt.dateObj.date}
                    </p>
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-0.5 text-xs sm:text-sm">
                      <span className="flex items-center gap-1 text-amber-300 font-medium">
                        <Clock className="w-3.5 h-3.5" /> {opt.time ? formatTime(opt.time) : (isFr ? "Choisir l'heure" : "Pick a time")}
                      </span>
                      <span className="flex items-center gap-1 text-zinc-300">
                        <MapPin className="w-3.5 h-3.5" /> {opt.location.name}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-zinc-500 group-hover:text-amber-400 transition-colors flex-shrink-0" />
                </button>
              ))}
              <button
                onClick={() => setFastTrackOptions(null)}
                className="w-full mt-2 text-center text-xs text-zinc-400 hover:text-white py-2 transition-colors"
                data-testid="fast-track-cancel"
              >
                {isFr ? "Annuler et choisir manuellement" : "Cancel and pick manually"}
              </button>

              {/* "Notify me when sooner opens" — only when earliest is > 7 days out */}
              {(() => {
                const earliest = fastTrackOptions[0];
                if (!earliest?.dateObj?.date) return null;
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const slotDate = new Date(earliest.dateObj.date + "T00:00:00");
                const daysOut = Math.floor((slotDate - today) / (1000 * 60 * 60 * 24));
                if (daysOut <= 7) return null;
                if (soonerSaved) {
                  return (
                    <div
                      className="mt-3 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-3 flex items-start gap-2"
                      data-testid="fast-track-watch-saved"
                    >
                      <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                      <p className="text-emerald-300 text-xs leading-relaxed">
                        {isFr
                          ? "Parfait — nous vous enverrons un texto et un courriel dès qu'un créneau plus tôt s'ouvrira."
                          : "Got it — we'll text and email you the moment an earlier slot opens up."}
                      </p>
                    </div>
                  );
                }
                return (
                  <div
                    className="mt-3 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 to-orange-500/5 p-3"
                    data-testid="fast-track-watch-cta"
                  >
                    {!showSoonerForm ? (
                      <button
                        onClick={() => {
                          setShowSoonerForm(true);
                          // Prefill from customerInfo if the client already typed it in this flow
                          setSoonerWatch((p) => ({
                            first_name: p.first_name || customerInfo.first_name || "",
                            email: p.email || customerInfo.email || rebookEmail || "",
                            phone: p.phone || customerInfo.phone || "",
                          }));
                        }}
                        className="w-full flex items-center gap-2 text-left"
                        data-testid="fast-track-watch-open"
                      >
                        <RefreshCw className="w-4 h-4 text-amber-300 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-xs font-semibold leading-snug">
                            {isFr
                              ? `Le plus tôt est dans ${daysOut} jours — me prévenir si plus tôt?`
                              : `Earliest is ${daysOut} days out — notify me if sooner opens up?`}
                          </p>
                          <p className="text-zinc-400 text-[11px] mt-0.5">
                            {isFr
                              ? "Nous surveillons en direct et envoyons texto + courriel"
                              : "We watch live availability and ping you by text + email"}
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-amber-400 flex-shrink-0" />
                      </button>
                    ) : (
                      <div className="space-y-2">
                        <p className="text-amber-300 text-[10px] font-bold uppercase tracking-wider">
                          {isFr ? "Notifier-moi si plus tôt" : "Notify me if sooner"}
                        </p>
                        <input
                          type="text"
                          value={soonerWatch.first_name}
                          onChange={(e) => setSoonerWatch((p) => ({ ...p, first_name: e.target.value }))}
                          placeholder={isFr ? "Prénom" : "First name"}
                          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                          data-testid="fast-track-watch-first-name"
                        />
                        <input
                          type="email"
                          value={soonerWatch.email}
                          onChange={(e) => setSoonerWatch((p) => ({ ...p, email: e.target.value }))}
                          placeholder={isFr ? "Courriel" : "Email"}
                          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                          data-testid="fast-track-watch-email"
                        />
                        <input
                          type="tel"
                          value={soonerWatch.phone}
                          onChange={(e) => setSoonerWatch((p) => ({ ...p, phone: e.target.value }))}
                          placeholder={isFr ? "Téléphone (optionnel)" : "Phone (optional)"}
                          className="w-full bg-zinc-900 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                          data-testid="fast-track-watch-phone"
                        />
                        <div className="flex items-center gap-2 pt-1">
                          <Button
                            size="sm"
                            disabled={soonerSaving || !soonerWatch.email.trim()}
                            onClick={async () => {
                              if (!soonerWatch.email.trim()) return;
                              setSoonerSaving(true);
                              try {
                                await axios.post(
                                  `${API}/booking/fast-track-watch`,
                                  {
                                    client_email: soonerWatch.email.trim(),
                                    client_name: soonerWatch.first_name.trim() || null,
                                    client_phone: soonerWatch.phone.trim() || null,
                                    service_duration_minutes: totalDurationMinutes || 15,
                                    current_earliest_date: fastTrackOptions[0].dateObj.date,
                                    location_ids: fastTrackOptions.map((o) => o.location.id),
                                    language: isFr ? "fr" : "en",
                                  },
                                  { timeout: 15000 }
                                );
                                setSoonerSaved(true);
                                toast.success(
                                  isFr
                                    ? "Surveillance activée — nous vous préviendrons"
                                    : "Watch enabled — we'll notify you"
                                );
                              } catch (e) {
                                toast.error(
                                  isFr ? "Échec — réessayez" : "Couldn't save — try again"
                                );
                              } finally {
                                setSoonerSaving(false);
                              }
                            }}
                            className="bg-amber-600 hover:bg-amber-500 text-white text-xs flex-1"
                            data-testid="fast-track-watch-submit"
                          >
                            {soonerSaving ? (
                              <Loader2 className="w-3.5 h-3.5 animate-spin" />
                            ) : isFr ? (
                              "Surveiller pour moi"
                            ) : (
                              "Watch for me"
                            )}
                          </Button>
                          <button
                            onClick={() => setShowSoonerForm(false)}
                            className="text-zinc-400 hover:text-white text-xs px-2 py-1"
                            data-testid="fast-track-watch-cancel-form"
                          >
                            {isFr ? "Annuler" : "Cancel"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        )}
        {/* PROMINENT BOOK DIRECT BANNER moved INSIDE the scrollable area
            (was above the header — iOS Safari URL bar was clipping it). */}
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-zinc-700">
          <div>
            <h2 className="text-base sm:text-xl text-white" style={{ fontFamily: "'Playfair Display', serif" }}>
              {step < 6
                ? (isFr ? "Réserver un " : "Book an ")
                : ""}
              <span className="italic text-amber-400">
                {step < 6 ? (isFr ? "Rendez-vous" : "Appointment") : (isFr ? "Confirmé!" : "Confirmed!")}
              </span>
            </h2>
            {/* "Booking with Jenny" pill — only shows when the flow was
                opened with prefillProvider="jenny" (i.e. from /jenny page
                or via /book?provider=jenny). Makes it crystal clear to
                the patient which provider they'll see, especially before
                they get to the date picker. */}
            {/* "With Jenny St-Jean" badge REMOVED — Jenny is completely
                hidden from public booking UI. */}
            {isTanya && step < 6 && (
              <span
                className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-0.5 rounded-full bg-rose-500/15 border border-rose-400/40 text-rose-300 text-[11px] font-semibold"
                data-testid="booking-with-tanya-pill"
              >
                <MapPin className="w-3 h-3" />
                {isFr ? "Avec une esthéticienne médicale · Clinique d'Ottawa" : "With a Medical Aesthetician · Ottawa Clinic"}
              </span>
            )}
            {/* Crown Spin prize chip — shows when the flow opens from an
                active prize card in the client portal. Persistent, not
                a toast, so the client (and staff at check-in on the
                staff calendar note) can always see which prize is
                attached. Iter 164 fix. */}
            {prefillPrizeCode && step < 6 && (
              <div
                className="inline-flex items-center gap-1.5 mt-1 px-2.5 py-1 rounded-full bg-amber-500/15 border border-amber-400/40 text-amber-200 text-[11px] font-semibold"
                data-testid="booking-prize-chip"
              >
                <Sparkles className="w-3 h-3" />
                <span>
                  {isFr ? "Prix Crown Spin attaché" : "Crown Spin prize attached"}
                  {prefillPrizeTitle ? `: ${prefillPrizeTitle}` : ""}
                </span>
                <span className="font-mono text-[10px] opacity-80">({prefillPrizeCode})</span>
              </div>
            )}
          </div>
          <button onClick={handleClose} className="p-2 text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-full transition-colors" data-testid="close-booking-flow">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* "Booking with Tanya · Ottawa" flash banner — slides in for 5s
            when the modal opens from Tanya's landing pages so clients
            instantly know they're in the right provider's flow. */}
        {showAnnaIntro && (
          <div
            className="mx-3 sm:mx-5 mt-3 rounded-lg border border-rose-400/50 bg-gradient-to-r from-rose-500/15 via-rose-400/10 to-transparent px-3 py-2.5 flex items-center gap-2.5 animate-in fade-in slide-in-from-top-2 duration-500"
            data-testid="booking-tanya-intro-banner"
            role="status"
          >
            <div className="w-7 h-7 rounded-full bg-rose-500/25 border border-rose-400/60 flex items-center justify-center flex-shrink-0">
              <Check className="w-3.5 h-3.5 text-rose-200" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-rose-100 text-[13px] font-semibold leading-tight">
                {isFr ? "Vous réservez avec une esthéticienne médicale" : "You're booking with a Medical Aesthetician"}
              </div>
              <div className="text-rose-200/80 text-[11px] leading-tight mt-0.5">
                {isFr ? "Esthéticienne médicale · Clinique d'Ottawa uniquement" : "Medical Esthetician · Ottawa clinic only"}
              </div>
            </div>
          </div>
        )}

        {/* Matching "Booking with Karine" flash banner — amber/gold to
            match Karine's brand, fires from her injectable landing pages
            (Dermal Fillers, Sculptra, Botox, Skin Boosters, etc.). */}
        {showKarineIntro && (
          <div
            className="mx-3 sm:mx-5 mt-3 rounded-lg border border-amber-400/50 bg-gradient-to-r from-amber-500/15 via-amber-400/10 to-transparent px-3 py-2.5 flex items-center gap-2.5 animate-in fade-in slide-in-from-top-2 duration-500"
            data-testid="booking-karine-intro-banner"
            role="status"
          >
            <div className="w-7 h-7 rounded-full bg-amber-500/25 border border-amber-400/60 flex items-center justify-center flex-shrink-0">
              <Check className="w-3.5 h-3.5 text-amber-200" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-amber-100 text-[13px] font-semibold leading-tight">
                {isFr ? "Vous réservez avec Karine" : "You're booking with Karine"}
              </div>
              <div className="text-amber-200/80 text-[11px] leading-tight mt-0.5">
                {isFr ? "Infirmière injectrice de précision · 35+ ans d'expérience" : "Precision Injectionist · 35+ years of experience"}
              </div>
            </div>
          </div>
        )}

        {/* Iter 409 · Layer-2 sticky location pill — visible from the
            moment a clinic is picked until the booking commits. Tap
            "Change" to jump back to the location picker instantly. */}
        {selectedLocation && step > 0 && step < 6 && (
          <div className="mx-3 sm:mx-5 mt-3 flex items-center gap-2 rounded-full border border-amber-500/50 bg-amber-500/10 px-3 py-1.5">
            <MapPin className="w-3.5 h-3.5 text-amber-400 shrink-0" />
            <span className="text-[10px] uppercase tracking-[0.2em] text-amber-400/90 font-bold shrink-0">
              {isFr ? "Réservation à" : "Booking at"}
            </span>
            <span className="text-xs sm:text-sm font-semibold text-white truncate" data-testid="booking-sticky-location">
              {selectedLocation?.name || LOCATION_NAMES[selectedLocation?.id] || ""}
            </span>
            <button
              type="button"
              onClick={() => setStep(0)}
              data-testid="booking-sticky-location-change"
              className="ml-auto shrink-0 text-[10px] uppercase tracking-[0.2em] text-amber-300 hover:text-amber-200 underline underline-offset-2"
            >
              {isFr ? "Changer" : "Change"}
            </button>
          </div>
        )}

        {/* Progress Steps — Iter 453 · Feb 2026 · Karine ask (novice-friendly).
            Active step label now shows on mobile too so users always
            know exactly what they're doing next ("Treatment", "Date", …).
            Hidden inactive labels stay off-screen on small viewports so
            the bar doesn't wrap. */}
        {step < 6 && (
          <div className="flex items-center px-3 sm:px-5 pt-3 sm:pt-4 gap-0.5">
            {STEPS.slice(0, 6).map((s, i) => (
              <div key={s} className="flex items-center flex-1">
                <div className={`flex items-center justify-center w-5 h-5 sm:w-6 sm:h-6 rounded-full text-[9px] sm:text-[10px] font-bold transition-colors flex-shrink-0 ${
                  i < step ? "bg-amber-500 text-white" : i === step ? "bg-amber-500/20 text-amber-400 ring-2 ring-amber-500" : "bg-zinc-800 text-zinc-500"
                }`}>
                  {i < step ? <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3" /> : i + 1}
                </div>
                <span
                  className={`ml-1 text-[9px] sm:text-[10px] whitespace-nowrap ${
                    i === step
                      ? "text-amber-400 font-semibold inline"
                      : "text-zinc-500 hidden sm:inline"
                  }`}
                  data-testid={i === step ? "booking-progress-active-label" : undefined}
                >
                  {stepLabels[i]}
                </span>
                {i < 5 && <div className={`flex-1 h-0.5 mx-0.5 sm:mx-1 ${i < step ? "bg-amber-500" : "bg-zinc-700"}`} />}
              </div>
            ))}
          </div>
        )}

        {/* ── Tanya's FREE Facial Lymphatic Drainage event (Jun 19 Ottawa) ──
            Dynamic remaining-spots count. Auto-hides after the event day
            or when 0 spots remain. Tapping it deep-links to Tanya's Square
            Ottawa booking site so clients book this exact event there. */}
        {step < 6 && annaEvent && (
          <div
            data-testid="booking-event-banner-lymphatic"
            className="mx-3 sm:mx-5 mt-3 sm:mt-4 rounded-xl border-2 border-emerald-400/70 bg-gradient-to-br from-emerald-700/40 via-emerald-800/30 to-zinc-900/40 shadow-[0_0_24px_rgba(16,185,129,0.18)] overflow-hidden"
          >
            <a
              href={annaEvent.event?.external_booking_url || "https://karine-lauzon-rn-ottawa.square.site/"}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="booking-event-banner-lymphatic-cta"
              className="block p-3 sm:p-4 hover:bg-emerald-700/15 transition-colors"
            >
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-full bg-emerald-400/25 border border-emerald-300/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Sparkles className="w-4 h-4 text-emerald-200" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 mb-1">
                    <span className="text-emerald-300 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.18em]">
                      {isFr ? "Cette semaine · Ottawa · 10 h – 17 h" : "This Friday · Ottawa · 10 AM – 5 PM"}
                    </span>
                    <span className="text-[10px] sm:text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-400 text-emerald-950">
                      {annaEvent.remaining === 1
                        ? (isFr ? "1 seule place" : "Only 1 spot left")
                        : (isFr ? `Plus que ${annaEvent.remaining} places` : `Only ${annaEvent.remaining} spots left`)}
                    </span>
                  </div>
                  <div className="text-white text-sm sm:text-base font-bold leading-tight">
                    {isFr
                      ? "Drainage lymphatique facial GRATUIT avec une esthéticienne médicale"
                      : (annaEvent.event?.title || "FREE Facial Lymphatic Drainage with a Medical Aesthetician")}
                  </div>
                  <div className="text-emerald-100/85 text-[11px] sm:text-xs mt-0.5">
                    {isFr
                      ? "Créneaux d'1 h · drainage + protocole éclat · réservez avant qu'il n'y ait plus de place"
                      : (annaEvent.event?.subtitle || "1-hour spots · book before they're gone")}
                  </div>
                </div>
                <div className="hidden sm:flex items-center gap-1 text-emerald-200 text-xs font-semibold flex-shrink-0 self-center">
                  {isFr ? "Réserver" : "Book now"} <ExternalLink className="w-3.5 h-3.5" />
                </div>
              </div>
            </a>
            {/* Notify-me sub-strip — full booked? get a text when a spot opens. */}
            <div className="border-t border-emerald-500/30 bg-emerald-950/30 px-3 sm:px-4 py-2">
              {!notifyOpen && !notifyDone && (
                <button
                  type="button"
                  onClick={() => setNotifyOpen(true)}
                  className="flex items-center gap-2 text-emerald-200/90 hover:text-emerald-100 text-[11px] sm:text-xs"
                  data-testid="booking-event-notify-toggle"
                >
                  <Bell className="w-3.5 h-3.5" />
                  {isFr ? "Texte-moi dès qu'une place s'ouvre" : "Text me the moment a spot opens"}
                </button>
              )}
              {notifyDone && (
                <div className="text-emerald-200 text-[11px] sm:text-xs flex items-center gap-1.5">
                  <Check className="w-3.5 h-3.5" />
                  {isFr ? "Inscrit·e — on vous textera dès qu'une place se libère." : "You're on the list — we'll text you the moment a spot opens."}
                </div>
              )}
              {notifyOpen && !notifyDone && (
                <div className="space-y-2 py-1">
                  <div className="text-emerald-200/90 text-[11px] sm:text-xs">
                    {isFr
                      ? "Entrez votre nom + téléphone — on vous texte dès qu'une place se libère."
                      : "Drop your name + phone — we'll text you the second a spot opens up."}
                  </div>
                  <div className="grid grid-cols-2 gap-1.5">
                    <input
                      type="text"
                      placeholder={isFr ? "Prénom" : "First name"}
                      value={notifyForm.name}
                      onChange={(e) => setNotifyForm({ ...notifyForm, name: e.target.value })}
                      className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-xs text-white placeholder-zinc-500 focus:border-emerald-400 focus:outline-none"
                      data-testid="booking-event-notify-name"
                    />
                    <input
                      type="tel"
                      placeholder={isFr ? "Votre cellulaire" : "Your mobile number"}
                      value={notifyForm.phone}
                      onChange={(e) => setNotifyForm({ ...notifyForm, phone: e.target.value })}
                      className="bg-zinc-950 border border-zinc-700 rounded px-2 py-1.5 text-xs text-white placeholder-zinc-500 focus:border-emerald-400 focus:outline-none font-mono"
                      data-testid="booking-event-notify-phone"
                    />
                  </div>
                  <div className="flex gap-1.5">
                    <button
                      type="button"
                      onClick={() => { setNotifyOpen(false); setNotifyForm({ name: "", phone: "" }); }}
                      className="flex-1 px-2 py-1.5 rounded text-zinc-300 hover:text-white text-[11px] font-semibold"
                    >
                      {isFr ? "Annuler" : "Cancel"}
                    </button>
                    <button
                      type="button"
                      onClick={submitNotifySignup}
                      disabled={notifyBusy}
                      className="flex-1 px-2 py-1.5 rounded bg-emerald-500 hover:bg-emerald-400 text-emerald-950 text-[11px] font-bold disabled:opacity-50"
                      data-testid="booking-event-notify-submit"
                    >
                      {notifyBusy
                        ? (isFr ? "Inscription…" : "Saving…")
                        : (isFr ? "M'inscrire" : "Notify me")}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* PROMINENT BOOK DIRECT BANNER — moved from "always visible" to
            "only when the client actually needs an escape hatch". Was
            confusing novice users by shouting a duplicate CTA at the
            same time as the primary Treatment picker (Karine feedback
            Feb 2026 · Iter 453). Now surfaces only if:
              • the catalog failed to load AND we're not loading, OR
              • the client has been waiting ≥15s on step 1/2.
            Otherwise the primary flow stays clean. Same escape hatch
            still lives at the bottom footer strip on every step. */}
        {step >= 1 && step < 6 && (
          (!loading && !catalogLoading && catalogServices.length === 0) ||
          ((step === 1 || step === 2) && (loading || catalogLoading) && loadingElapsed >= 15)
        ) && (
          <div
            className="mx-3 sm:mx-5 mt-3 sm:mt-4 rounded-xl border-2 border-emerald-500/50 bg-gradient-to-br from-emerald-500/15 via-emerald-600/10 to-zinc-900/40 shadow-[0_0_24px_rgba(16,185,129,0.15)] p-3 sm:p-4"
            data-testid="square-direct-banner-top"
          >
            <div className="flex items-start gap-2 mb-2.5">
              <div className="w-7 h-7 rounded-full bg-emerald-500/25 flex items-center justify-center flex-shrink-0 mt-0.5">
                <ExternalLink className="w-3.5 h-3.5 text-emerald-300" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-emerald-300 text-[10px] sm:text-xs font-bold uppercase tracking-[0.18em]">
                  {isFr ? "Réservation directe — Square" : "Book Direct — Square"}
                </p>
                <p className="text-emerald-100 text-xs sm:text-sm font-semibold leading-tight mt-0.5">
                  {isFr
                    ? "Le plus fiable. Choisissez votre clinique :"
                    : "The most reliable way to book. Pick your clinic:"}
                </p>
              </div>
            </div>

            <div className={`grid gap-1.5 sm:gap-2 ${(isTanya || isLocationLocked) ? "grid-cols-1" : "grid-cols-3"}`}>
              {Object.entries(SQUARE_DIRECT_URLS)
                .filter(([id]) => !(isTanya || isLocationLocked) || id === lockedLocationId)
                .map(([id, url]) => (
                <a
                  key={`top-banner-${id}`}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex flex-col items-center justify-center gap-0.5 px-2 py-2.5 sm:py-3 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-zinc-950 text-[11px] sm:text-xs font-bold transition-all shadow-md hover:shadow-lg hover:-translate-y-0.5 text-center leading-tight"
                  data-testid={`square-direct-top-${id}`}
                >
                  <span className="opacity-90 text-[9px] sm:text-[10px] font-medium uppercase tracking-wider">
                    Square
                  </span>
                  <span>{LOCATION_NAMES[id]}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Content */}
        <div className="p-3 sm:p-5 overflow-y-auto flex-1 min-h-0" ref={contentScrollRef}>
          {error && step >= 1 && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-300 px-4 py-3 rounded-lg mb-4 text-sm flex flex-col gap-2" data-testid="booking-error">
              <div>{error}</div>
              {offScheduleAlts && offScheduleAlts.list.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-1" data-testid="off-schedule-alternatives">
                  {offScheduleAlts.list.map((alt) => (
                    <button
                      key={`${alt.location_id}-${alt.date}-${alt.start_time}`}
                      type="button"
                      onClick={() => jumpToAlternative(alt)}
                      className="px-3 py-2 rounded-full bg-amber-400 text-stone-950 text-xs font-bold hover:bg-amber-300 transition"
                      data-testid={`off-schedule-alt-${alt.location_id}-${alt.date}-${alt.start_time}`}
                    >
                      {alt.weekday?.slice(0, 3)} {new Date(`${alt.date}T12:00:00`).toLocaleDateString(isFr ? "fr-CA" : "en-CA", { month: "short", day: "numeric" })} · {alt.start_time} · {alt.location_name}
                    </button>
                  ))}
                </div>
              )}
              {/* Quick tap-to-text/call escape hatch so a stuck client
                  (Carole Brown · Sep 23 2026) never has to bounce off
                  our platform via a third-party message. */}
              {(/850-0805|Text us|texto au/i).test(error || "") && (
                <div className="flex gap-2 pt-1">
                  <a
                    href="sms:+16138500805"
                    data-testid="booking-error-text-us"
                    className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 border border-red-500/40 hover:bg-red-500/30 px-3 py-1.5 text-red-200 font-medium text-xs no-underline"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    {isFr ? "Envoyer un texto" : "Text us"}
                  </a>
                  <a
                    href="tel:+16138500805"
                    data-testid="booking-error-call-us"
                    className="inline-flex items-center gap-1.5 rounded-full bg-red-500/20 border border-red-500/40 hover:bg-red-500/30 px-3 py-1.5 text-red-200 font-medium text-xs no-underline"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
                    (613) 850-0805
                  </a>
                </div>
              )}
            </div>
          )}

          {/* Iter 353b · Booking Concierge — surfaces after we've alerted the
              team so the client knows help is on the way and doesn't abandon. */}
          {conciergeNotified && step >= 4 && !booking && (
            <div className="bg-gradient-to-r from-emerald-500/10 to-emerald-600/10 border border-emerald-500/40 rounded-lg px-4 py-3 mb-4 flex items-start gap-3" data-testid="booking-concierge-banner">
              <div className="w-8 h-8 rounded-full bg-emerald-500/25 border border-emerald-400/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-300"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>
              </div>
              <div className="flex-1">
                <div className="text-emerald-200 font-bold text-sm leading-tight">
                  {isFr ? "Notre équipe a été alertée — on s'occupe de vous" : "Our team has been alerted — we've got you covered"}
                </div>
                <div className="text-emerald-100/90 text-[12px] leading-snug mt-1">
                  {isFr
                    ? "Karine ou un membre de l'équipe vous contactera dans les 15 minutes pour confirmer votre rendez-vous manuellement. Vous pouvez fermer cette fenêtre en toute sécurité."
                    : "Karine or a team member will reach out within 15 minutes to confirm your appointment personally. You can safely close this window."}
                </div>
              </div>
            </div>
          )}

          {/* Persistent help strip — Messenger + Email link on every step */}
          {step < 6 && <BookingHelpStrip className="mb-4" />}

          {/* Package Info Banner - shown when booking a specific package */}
          {!(packageInfo && (packageInfo.name || packageInfo.searchQuery)) && (step === 2 || step === 3) && selectedServices.length > 0 && (
            <div className="bg-zinc-800/70 border border-amber-500/30 rounded-xl px-4 py-3 mb-4 flex items-center justify-between gap-3" data-testid="booking-selected-strip">
              <div className="min-w-0">
                <p className="text-amber-300/80 text-[10px] font-bold uppercase tracking-[0.18em]">{isFr ? "Vous réservez" : "You're booking"}</p>
                <p className="text-white text-base font-semibold leading-snug truncate">{selectedServices.map((s) => s.name).join(" + ")}</p>
                <p className="text-zinc-400 text-sm">{formatDuration(totalDurationMinutes)}{selectedServices.length === 1 && selectedServices[0].price && selectedServices[0].price !== "$0" ? ` · ${selectedServices[0].price}` : ""} · {displayProviderName}</p>
              </div>
              <button type="button" onClick={() => setStep(1)} className="text-amber-400 text-sm font-semibold underline underline-offset-2 min-h-[44px] px-2 flex-shrink-0" data-testid="booking-selected-change">
                {isFr ? "Changer" : "Change"}
              </button>
            </div>
          )}
          {packageInfo && step < 6 && (packageInfo.name || packageInfo.searchQuery) && (
            <div className="bg-gradient-to-r from-amber-500/10 to-amber-600/10 border border-amber-500/30 rounded-lg p-3 mb-4" data-testid="package-info-banner">
              <div className="flex items-start gap-3">
                {packageInfo.num != null && (
                  <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold text-sm">#{packageInfo.num}</span>
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {!packageInfo.name && packageInfo.searchQuery && (
                    <p className="text-amber-300/80 text-[10px] font-bold uppercase tracking-[0.18em]">{isFr ? "Vous réservez" : "You're booking"}</p>
                  )}
                  <p className="text-amber-400 font-semibold text-sm">{packageInfo.name || packageInfo.searchQuery}</p>
                  {packageInfo.price && <p className="text-white text-lg font-bold">{packageInfo.price}</p>}
                  {packageInfo.description && (
                    <p className="text-zinc-400 text-xs mt-1">{packageInfo.description}</p>
                  )}
                  {packageInfo.badges && packageInfo.badges.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {packageInfo.badges.map((badge, i) => (
                        <span key={i} className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${
                          badge === 'REDENSITY 1' ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30' :
                          badge === 'BOTOX' ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' :
                          'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                        }`}>
                          {badge}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Helpful loading notice — shown only while we're actively
              fetching Square data on the Treatment (1) and Date (2) steps.
              The location step (0) doesn't need any Square data, so the
              notice would just look like a scary warning before the user
              has even picked a clinic.

              Progressive messaging:
                0-4s  → calm "Loading from Square" message
                4-8s  → "Square is being slow — almost there"
                8s+   → reveal a visible "Refresh slots" button so the user
                        can self-recover instead of staring at a spinner.

              On step 2 (dates) we've already promoted a big green "Book on
              Square" panel after 3s — showing this duplicate slow-notice +
              "Try again now" button on top of it confused clients (multiple
              client reports). So we hide this notice on step 2 once we're
              past the 12s threshold and the escape-hatch panel is visible.
              Iter 453 · Feb 2026 · Karine ask (novice-friendly) — also
              wait until at least 4s of loading before rendering the
              notice at all. Instant amber warnings on <1s Square calls
              were scaring first-time bookers before they'd even seen
              the treatment picker skeleton. */}
          {step >= 1 && step <= 2 && loading && loadingElapsed >= 4 && !(step === 2 && loadingElapsed >= 20 && selectedLocation && SQUARE_DIRECT_URLS[selectedLocation.id]) && (
            <div
              className="bg-amber-500/5 border border-amber-500/20 rounded-lg px-3 py-2 mb-4 flex items-start gap-2"
              data-testid="booking-loading-notice"
            >
              <svg className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <div className="flex-1">
                <p className="text-zinc-300 text-xs leading-relaxed">
                  {loadingElapsed < 3 ? (
                    isFr
                      ? "Connexion à votre calendrier…"
                      : "Connecting to your calendar…"
                  ) : loadingElapsed < 8 ? (
                    isFr
                      ? "Nous chargeons les disponibilités en direct…"
                      : "Loading live availability…"
                  ) : loadingElapsed < 18 ? (
                    isFr
                      ? "Encore quelques secondes — presque prêt…"
                      : "Just a few more seconds — almost there…"
                  ) : (
                    isFr
                      ? "Récupération automatique…"
                      : "Auto-recovering…"
                  )}
                </p>
                {/* "Try again now" button REMOVED — auto-recovery handles
                    all retry / cache-bust / hard-reload silently. Clients
                    never have to tap anything. */}
              </div>
            </div>
          )}

          {/* STEP 0: Select Location */}
          {step === 0 && (
            <div className="space-y-3" data-testid="step-location">
              {/* One-Tap Rebook — only shown when prefillEmail brought back a
                  real prior booking. Champagne styling so it's THE primary
                  CTA, sitting above the manual email box and location picker. */}
              {lastBooking && !lastBookingDismissed && !(packageInfo && (packageInfo.name || packageInfo.searchQuery)) && (
                <div
                  className="relative overflow-hidden bg-gradient-to-br from-amber-900/40 via-zinc-900 to-amber-950/40 border border-amber-500/40 rounded-xl p-4 mb-3 shadow-[0_0_24px_rgba(252,211,77,0.08)]"
                  data-testid="one-tap-rebook-card"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-full bg-amber-500/20 border border-amber-400/40 flex items-center justify-center flex-shrink-0">
                      <Crown className="w-4 h-4 text-amber-300" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-amber-300 text-[10px] font-bold uppercase tracking-[0.18em] mb-0.5">
                        {isFr ? "Bon retour" : "Welcome back"}
                        {lastBooking.first_name ? `, ${lastBooking.first_name.split(" ")[0]}` : ""}
                      </p>
                      <p className="text-white text-sm font-semibold leading-tight">
                        {isFr ? "Réservez à nouveau votre visite habituelle ?" : "Book your usual visit again?"}
                      </p>
                      <p className="text-zinc-300 text-xs mt-1.5 leading-relaxed">
                        <span className="text-amber-200/90">{lastBooking.location_name}</span>
                        {" · "}
                        <span className="text-zinc-200">
                          {(lastBooking.services || []).map(s => s?.name).filter(Boolean).slice(0, 3).join(" + ")}
                          {(lastBooking.services || []).length > 3 ? " …" : ""}
                        </span>
                        {lastBooking.days_ago != null && (
                          <span className="text-zinc-500"> · {lastBooking.days_ago}{isFr ? "j" : "d"} {isFr ? "passés" : "ago"}</span>
                        )}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2 mt-3">
                    <Button
                      onClick={applyLastBookingOneTap}
                      className="flex-1 bg-amber-500 hover:bg-amber-400 text-zinc-950 text-sm font-bold shadow-md"
                      data-testid="one-tap-rebook-confirm"
                    >
                      <Zap className="w-4 h-4 mr-1" />
                      {isFr ? "Réserver à nouveau" : "Book the same"}
                    </Button>
                    <button
                      type="button"
                      onClick={() => setLastBookingDismissed(true)}
                      className="px-3 py-2 text-xs text-zinc-400 hover:text-zinc-200 transition-colors"
                      data-testid="one-tap-rebook-dismiss"
                    >
                      {isFr ? "Différent" : "Different"}
                    </button>
                  </div>
                </div>
              )}
              {/* #4 Returning client one-tap rebook */}
              {autoFilled && customerInfo.email ? (
                <div className="flex items-center gap-2 bg-emerald-950/40 border border-emerald-700/40 rounded-xl px-3 py-2 mb-3" data-testid="returning-client-known">
                  <Check className="w-4 h-4 text-emerald-400 shrink-0" />
                  <p className="text-xs text-zinc-200 truncate">
                    <span className="text-emerald-300 font-bold">{isFr ? "Bon retour" : "Welcome back"}{customerInfo.first_name ? `, ${customerInfo.first_name}` : ""}</span>
                    {" · "}{isFr ? "vos coordonnées sont déjà remplies" : "your details are already filled in"}
                  </p>
                  <button
                    type="button"
                    onClick={() => { setAutoFilled(false); setCustomerInfo({ first_name: "", last_name: "", email: "", phone: "", send_reminders: true }); setRebookEmail(""); try { localStorage.removeItem("kl_booking_email"); } catch { /* noop */ } }}
                    className="ml-auto shrink-0 text-[11px] text-zinc-400 hover:text-white underline underline-offset-2"
                    data-testid="returning-client-not-you"
                  >
                    {isFr ? "Pas vous ?" : "Not you?"}
                  </button>
                </div>
              ) : (
              <div className="bg-gradient-to-br from-emerald-950/40 to-zinc-900 border border-emerald-700/40 rounded-xl p-3 mb-3" data-testid="returning-client-banner">
                <div className="flex items-center gap-2 mb-2">
                  <RotateCcw className="w-4 h-4 text-emerald-400" />
                  <p className="text-emerald-300 text-xs font-bold uppercase tracking-wider">
                    {isFr ? "Client de retour ?" : "Returning client?"}
                  </p>
                </div>
                <p className="text-zinc-300 text-xs mb-2">
                  {isFr
                    ? "Entrez votre courriel ou votre numéro de téléphone — nous remplissons tout pour vous."
                    : "Enter your email or phone number — we'll fill everything in for you."}
                </p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    inputMode="email"
                    autoComplete="email"
                    value={rebookEmail}
                    onChange={(e) => setRebookEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && lookupRebook()}
                    placeholder={isFr ? "courriel ou téléphone" : "email or phone number"}
                    className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-emerald-500 focus:outline-none placeholder-zinc-500"
                    data-testid="rebook-email-input"
                  />
                  <Button
                    size="sm"
                    onClick={lookupRebook}
                    disabled={rebookLoading || !rebookEmail.trim()}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white text-xs whitespace-nowrap"
                    data-testid="rebook-lookup-btn"
                  >
                    {rebookLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (isFr ? "Trouver" : "Find me")}
                  </Button>
                </div>
                {rebookProfile?.last_booking && (
                  <div className="mt-2.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-2.5" data-testid="rebook-result">
                    <p className="text-emerald-300 text-xs font-semibold mb-0.5">
                      {isFr ? "Dernière visite trouvée" : "Last visit found"}
                    </p>
                    <p className="text-white text-xs">
                      {rebookProfile.last_booking.location_name}
                      {(rebookProfile.last_booking.services || []).slice(0, 2).map((s) => ` · ${s.name}`).join("")}
                    </p>
                    <Button
                      size="sm"
                      onClick={applyRebookLastVisit}
                      className="mt-2 w-full bg-emerald-600 hover:bg-emerald-500 text-white text-xs"
                      data-testid="rebook-apply-btn"
                    >
                      {isFr ? "Refaire ce rendez-vous →" : "Rebook this visit →"}
                    </Button>
                  </div>
                )}
                {rebookProfile?.found && !rebookProfile.last_booking && (
                  <p className="mt-2 text-zinc-400 text-xs italic">
                    {isFr ? "Compte trouvé — vos coordonnées seront pré-remplies." : "Account found — your contact info will be pre-filled."}
                  </p>
                )}
              </div>
              )}

              <p className="text-zinc-400 text-sm mb-4">
                {isTanya
                  ? (isFr ? "Notre esthéticienne médicale reçoit ses clients à notre clinique d'Ottawa :" : "Our Medical Aesthetician sees clients at our Ottawa clinic:")
                  : isLocationLocked && lockedLocationId === "3"
                    ? (isFr ? "Ce traitement n'est offert qu'à notre clinique d'Ottawa :" : "This treatment is only offered at our Ottawa clinic:")
                    : (isFr ? "Choisissez votre clinique:" : "Choose your clinic:")}
              </p>
              {((isTanya || isLocationLocked) ? [lockedLocationId] : ["1", "2", "3"]).map(id => {
                const names = { "1": "Carleton Place", "2": "Kanata", "3": "Ottawa" };
                const isSelected = selectedLocation?.id === id;
                return (
                  <div
                    key={id}
                    onClick={() => {
                      setSelectedLocation({ id, name: names[id] });
                      // Auto-advance to service selection — clients shouldn't
                      // have to hunt for a "Continue" button that may be
                      // below the fold on small phones (banners pushed it
                      // further down). Same pattern as the date click → step 3.
                      setStep(1);
                    }}
                    className={`flex items-center gap-4 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                      isSelected ? "border-amber-500 bg-amber-500/10" : "border-zinc-700 hover:border-zinc-500"
                    }`}
                    data-testid={`select-location-${id}`}
                  >
                    <img src={LOCATION_IMAGES[id]} alt={names[id]} className="w-20 h-16 object-cover rounded-md" />
                    <div className="flex-1">
                      <h3 className="text-white font-medium">{names[id]}</h3>
                      <p className="text-zinc-400 text-xs flex items-center gap-1 mt-0.5">
                        <MapPin className="w-3 h-3" /> {LOCATION_ADDRESSES[id]}
                      </p>
                    </div>
                    {isSelected && <Check className="w-5 h-5 text-amber-500" />}
                  </div>
                );
              })}
            </div>
          )}

          {/* STEP 1: Select Treatment/Service */}
          {step === 1 && (
            <div data-testid="step-service">
              {/* Iter 453 · Feb 2026 · Karine ask (novice-friendly) — big
                  clear headline so first-timers know exactly what this
                  step is asking of them. Subtitle explains the multi-
                  select behavior without hiding it in tiny grey text. */}
              <div className="mb-2 flex items-baseline justify-between gap-2">
                <h3 className="text-white text-lg sm:text-2xl font-semibold leading-tight" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="step-service-title">
                  {isFr ? "Que souhaitez-vous réserver ?" : "What would you like to book?"}
                </h3>
                <p className="text-zinc-400 text-[11px] sm:text-sm leading-snug text-right">
                  {isFr ? "Touchez un soin · plusieurs possibles" : "Tap one · add more if you like"}
                </p>
              </div>

              {/* Oct 21 2026 · Karine (Thuy Le stuck 2 min on this step):
                  one tap for the undecided — pick the free consult and go. */}
              {selectedServices.length === 0 && !serviceSearch && !packageInfo && !isTanya && !isLinda && (() => {
                const consult = (catalogServices || [])
                  .filter((s) => !s.tanya_only && !s.linda_only && !/tanya|linda/i.test(s.name || ""))
                  .find((s) => /consultation/i.test(s.name || "") && !/follow/i.test(s.name || ""));
                return consult ? (
                  <button
                    type="button"
                    onClick={() => { setSelectedServices([consult]); setStep(2); }}
                    className="w-full mb-3 rounded-2xl border border-emerald-400/50 bg-emerald-500/10 hover:bg-emerald-500/20 px-4 py-3 flex items-center gap-3 text-left min-h-[60px] touch-manipulation"
                    data-testid="undecided-consult-shortcut"
                  >
                    <HelpCircle className="w-6 h-6 text-emerald-300 flex-shrink-0" />
                    <span className="min-w-0 flex-1">
                      <span className="block text-white font-bold text-base leading-tight">{isFr ? "Pas certain(e) ? Consultation gratuite" : "Not sure what to pick? Free consultation"}</span>
                      <span className="block text-emerald-100/80 text-xs">{isFr ? `${consult.duration_minutes || 15} min · sans frais · Karine vous guide` : `${consult.duration_minutes || 15} min · no cost · Karine guides you`}</span>
                    </span>
                    <ChevronRight className="w-5 h-5 text-emerald-300 flex-shrink-0" />
                  </button>
                ) : null;
              })()}

              {/* #1 Fastest Available Slot — appears once at least one treatment is picked */}
              {selectedServices.length > 0 && (
                <div className="mb-3 bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30 rounded-xl p-3 flex items-center justify-between gap-3" data-testid="fastest-slot-banner">
                  <div className="flex items-center gap-2 min-w-0">
                    <Zap className="w-5 h-5 text-amber-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-amber-300 text-xs font-bold uppercase tracking-wider">
                        {isFr ? "Mode rapide" : "Fast track"}
                      </p>
                      <p className="text-zinc-300 text-xs truncate">
                        {isFr
                          ? `Trouve le créneau libre le plus tôt (${formatDuration(totalDurationMinutes)}) parmi les 3 cliniques`
                          : `Finds the earliest open slot (${formatDuration(totalDurationMinutes)}) across all 3 clinics`}
                      </p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={findFastestSlot}
                    disabled={findingFastest}
                    className="bg-amber-600 hover:bg-amber-500 text-white text-xs whitespace-nowrap flex-shrink-0"
                    data-testid="fastest-slot-btn"
                  >
                    {findingFastest ? (
                      <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> {isFr ? "Recherche…" : "Searching…"}</>
                    ) : (
                      <>{isFr ? "Trouver le plus tôt →" : "Find earliest →"}</>
                    )}
                  </Button>
                </div>
              )}
              {findingFastest && fastTrackProgress && (
                <p className="text-[11px] text-amber-300/80 -mt-2 mb-3 ml-1" data-testid="fast-track-progress">
                  {fastTrackProgress}
                </p>
              )}
              {(loading || catalogLoading) ? (
                // Skeleton loader for services
                <div className="space-y-4">
                  <div className="relative mb-4">
                    <div className="w-full h-10 bg-zinc-800 rounded-lg animate-pulse" />
                  </div>
                  <div className="space-y-1.5">
                    {[...Array(5)].map((_, i) => <SkeletonServiceItem key={i} />)}
                  </div>
                </div>
              ) : catalogServices.length === 0 ? (
                <div className="space-y-3">
                  <p className="text-zinc-500 text-center py-6">
                    {error || (isFr ? "Aucun service disponible" : "No services available")}
                  </p>
                  <BookDirectFallback contextLocationId={selectedLocation?.id} />
                </div>
              ) : (
                <>
                  {/* Oct 2026 · Fall marketing header — matches the ad
                      copy exactly. Visible above the service list for
                      every provider (Karine + Linda) so the booking
                      screen mirrors the newsletter / Instagram promo. */}
                  <div
                    className="mb-2 rounded-lg border border-amber-400/30 bg-amber-500/10 px-3 py-1.5 hidden sm:flex items-center gap-2 text-[11px] text-amber-100"
                    data-testid="booking-fall-marketing-banner"
                  >
                    <Sparkles className="w-3.5 h-3.5 text-amber-300 shrink-0" />
                    <span className="truncate">
                      {isFr
                        ? "Consultation gratuite · Points Crown sur chaque soin · Promotions en premier"
                        : "Free consultation · Crown points on every treatment · Promotions listed first"}
                    </span>
                  </div>

                  {/* Iter 265 · Crown Spin prize helper — persistent hint
                      inside the service picker so clients booking with an
                      attached prize (like Jessica Patterson's brow lift)
                      never see an empty picker without knowing what to do.
                      Renders only when a prize is attached AND no service
                      has been auto-selected yet. */}
                  {prefillPrizeCode && selectedServices.length === 0 && (
                    <div
                      className="mb-4 rounded-xl border border-amber-400/50 bg-gradient-to-r from-amber-500/15 via-amber-500/8 to-transparent p-3 flex items-start gap-3"
                      data-testid="booking-prize-picker-hint"
                    >
                      <div className="w-8 h-8 rounded-full bg-amber-500/25 border border-amber-400/60 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Sparkles className="w-4 h-4 text-amber-200" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-amber-100 text-[13px] font-semibold leading-tight">
                          {isFr
                            ? `Votre bonus « ${prefillPrizeTitle || "Crown Spin"} » est verrouillé sur cette réservation`
                            : `Your "${prefillPrizeTitle || "Crown Spin"}" bonus is locked to this booking`}
                        </p>
                        <p className="text-amber-200/80 text-[11px] leading-snug mt-0.5">
                          {isFr
                            ? "Choisissez un traitement ci-dessous — votre bonus sera ajouté par notre équipe à l'arrivée. Aucun code à saisir."
                            : "Pick any treatment below — our team will add your bonus at check-in. No code to enter."}
                        </p>
                      </div>
                    </div>
                  )}
                  {/* Search bar */}
                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                    <input
                      type="text"
                      value={serviceSearch}
                      onChange={e => setServiceSearch(e.target.value)}
                      placeholder={isFr ? "Rechercher un soin ou un numéro (#12)…" : "Search treatments or a number (#12)…"}
                      className="w-full bg-zinc-800 border border-zinc-700 rounded-lg pl-9 pr-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none placeholder-zinc-500"
                      data-testid="service-search-input"
                    />
                  </div>
                  <div className="flex items-center gap-1.5 mb-3 -mt-2" data-testid="service-sort-toggle">
                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mr-1">{isFr ? "Trier" : "Sort"}</span>
                    {[["category", isFr ? "Par catégorie" : "By category"], ["az", "A → Z"]].map(([k, l]) => (
                      <button key={k} type="button" onClick={() => setServiceSort(k)}
                        className={`px-3 py-1 rounded-full text-xs font-bold border transition ${serviceSort === k ? "bg-amber-500 text-zinc-900 border-amber-400" : "bg-zinc-800 text-zinc-300 border-zinc-700 hover:bg-zinc-700"}`}
                        data-testid={`service-sort-${k}`}>{l}</button>
                    ))}
                    <a
                      href={`/book/menu?location=${selectedLocation?.id || lockedLocationId || "3"}`}
                      className="ml-auto px-3 py-1 rounded-full text-xs font-bold bg-white text-zinc-900 hover:bg-amber-100 transition"
                      data-testid="full-menu-book-link"
                    >
                      {isFr ? "Menu complet →" : "Full menu →"}
                    </a>
                  </div>
                  <div className="flex gap-1.5 overflow-x-auto sm:flex-wrap pb-2 mb-2 -mx-1 px-1 [scrollbar-width:none]" data-testid="service-quick-filters">
                    {QUICK_FILTERS.map((f) => (
                      <button key={f.id} type="button" onClick={() => setQuickFilter(f.id)}
                        className={`shrink-0 px-3 py-1 rounded-full text-xs font-bold border transition ${quickFilter === f.id ? "bg-white text-zinc-900 border-white" : "bg-zinc-800/80 text-zinc-300 border-zinc-700 hover:bg-zinc-700"}`}
                        data-testid={`service-filter-${f.id}`}>{isFr ? f.fr : f.en}</button>
                    ))}
                  </div>

                  {/* Sticky "Your Selection" tray — keeps the user oriented as
                      they browse the long category list. Renders ABOVE the
                      services so it's always visible immediately after the
                      first tap, and remains sticky when they scroll. Empty
                      state shows a soft prompt encouraging the first pick. */}
                  <div
                    ref={selectionTrayRef}
                    className={`sticky top-0 z-10 mb-3 rounded-xl border transition-all duration-300 ${
                      selectedServices.length > 0
                        ? "bg-gradient-to-r from-amber-500/15 via-amber-500/10 to-emerald-500/10 border-amber-500/40 shadow-lg shadow-amber-500/10 backdrop-blur-sm"
                        : "hidden"
                    }`}
                    data-testid="your-selection-tray"
                  >
                    {selectedServices.length === 0 ? null : (
                      <div className="p-3">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-amber-300 text-[11px] font-bold uppercase tracking-wider flex items-center gap-1.5">
                            <Check className="w-3.5 h-3.5" />
                            {isFr
                              ? `Votre sélection (${selectedServices.length})`
                              : `Your selection (${selectedServices.length})`}
                          </p>
                          <button
                            type="button"
                            onClick={() => setSelectedServices([])}
                            className="text-[11px] text-zinc-400 hover:text-rose-300 transition-colors underline-offset-2 hover:underline"
                            data-testid="your-selection-clear"
                          >
                            {isFr ? "Tout effacer" : "Clear all"}
                          </button>
                        </div>
                        <div className="space-y-1.5">
                          {selectedServices.map((s, idx) => (
                            <div
                              key={s.variation_id}
                              className="flex items-center justify-between gap-2 bg-zinc-950/40 rounded-lg px-2.5 py-1.5 animate-in fade-in slide-in-from-top-1 duration-200"
                              data-testid={`your-selection-item-${idx}`}
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="text-[10px] font-mono text-amber-300 bg-amber-500/20 rounded px-1.5 py-0.5 flex-shrink-0">
                                  {idx + 1}
                                </span>
                                <p className="text-white text-xs truncate">{s.name}</p>
                              </div>
                              <div className="flex items-center gap-2 flex-shrink-0">
                                <span className="text-zinc-400 text-[11px]">{formatDuration(s.duration_minutes)}</span>
                                {s.price && (
                                  <span className="text-amber-300 text-[11px] font-medium">
                                    {typeof s.price === "number" ? `$${s.price}` : s.price}
                                  </span>
                                )}
                                <button
                                  type="button"
                                  onClick={() => toggleService(s)}
                                  className="text-zinc-500 hover:text-rose-400 transition-colors p-0.5 -m-0.5"
                                  aria-label={isFr ? "Retirer" : "Remove"}
                                  data-testid={`your-selection-remove-${idx}`}
                                >
                                  <X className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="mt-2.5 pt-2 border-t border-amber-500/20 flex items-center justify-between text-[11px]">
                          <span className="text-zinc-400">
                            {isFr ? "Durée totale" : "Total time"}:{" "}
                            <span className="text-white font-semibold">{formatDuration(totalDurationMinutes)}</span>
                          </span>
                          {totalPriceCents > 0 && (
                            <span className="text-zinc-400">
                              {isFr ? "Total" : "Total"}:{" "}
                              <span className="text-amber-300 font-bold">${(totalPriceCents / 100).toFixed(2)}</span>
                            </span>
                          )}
                        </div>

                        {/* "Often added together" pairings removed per request — was confusing rather than helpful. */}
                      </div>
                    )}
                  </div>

                  {/* Services: Promos → Consultations → Featured → Categories */}
                  <div className="space-y-4 max-h-[45vh] overflow-y-auto pr-1">
                    {/* ── PROMOS · Always FIRST (Mar 4 2026 · Karine + Lesley Quinn) ── */}
                    {/* Every Square service whose name contains the word
                        "promo" — pinned so Crown members never miss the
                        $75 Laser Genesis + Lymphatic Facial Drainage
                        (and any other active seasonal offer). Amber
                        gradient + Sparkles icon differentiate from the
                        emerald consultation block below. */}
                    {promoServices.length > 0 && (
                      <div data-testid="booking-promo-pinned-section">
                        <h4 className="text-amber-300 text-xs font-black uppercase tracking-widest mb-2 flex items-center gap-1.5">
                          <Sparkles className="w-3 h-3" />
                          {isFr ? "Promotions & forfaits du moment" : "Promotions & packages · this month"}
                          <span className="ml-1 text-[9px] font-black bg-amber-500 text-zinc-900 px-1.5 py-0.5 rounded-full">
                            {promoServices.length}
                          </span>
                        </h4>
                        <div className="space-y-1.5">
                          {(showAllPromos || categoriesAutoExpanded ? promoServices : promoServices.slice(0, PROMO_PREVIEW)).map(svc => {
                            const isSelected = isServiceSelected(svc);
                            return (
                              <div
                                key={svc.variation_id}
                                onClick={() => toggleService(svc)}
                                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all bg-gradient-to-r ${
                                  isSelected
                                    ? "from-amber-500/25 via-amber-500/15 to-amber-500/5 border-amber-400"
                                    : "from-amber-500/10 via-amber-500/5 to-transparent border-amber-500/40 hover:border-amber-400/70 hover:from-amber-500/20"
                                }`}
                                data-testid={`select-promo-${svc.variation_id}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    {codeBadge(svc)}
                                    <p className={`text-sm font-semibold ${isSelected ? "text-amber-200" : "text-white"}`}>
                                      {splitName(svc.name).title}
                                    </p>
                                    <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-amber-500 text-zinc-900">
                                      Promo
                                    </span>
                                    {splitName(svc.name).detail && (
                                      <p className="w-full text-[11px] text-zinc-300 leading-snug">{splitName(svc.name).detail}</p>
                                    )}
                                    {svc.badge && (
                                      <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-widest bg-amber-500/20 text-amber-200 border border-amber-500/40">
                                        {String(svc.badge).replace(/_/g, " ")}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-2 mt-0.5 text-[11px] text-zinc-400">
                                    <span>{formatDuration(svc.duration_minutes)}</span>
                                    {svc.price && (
                                      <span className="text-amber-300 font-semibold">
                                        · {typeof svc.price === "number" ? `$${svc.price}` : svc.price}
                                      </span>
                                    )}
                                  </div>
                                </div>
                                {isSelected ? (
                                  <div className="w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                                    <Check className="w-3.5 h-3.5 text-zinc-900" />
                                  </div>
                                ) : (
                                  <div className="w-6 h-6 rounded-full border-2 border-amber-500/40 flex-shrink-0" />
                                )}
                              </div>
                            );
                          })}
                          {!showAllPromos && !categoriesAutoExpanded && promoServices.length > PROMO_PREVIEW && (
                            <button type="button" onClick={() => setShowAllPromos(true)}
                              className="w-full py-2.5 rounded-lg border border-dashed border-amber-500/50 text-amber-300 text-xs font-bold hover:bg-amber-500/10"
                              data-testid="show-all-promos">
                              {isFr ? `Voir les ${promoServices.length - PROMO_PREVIEW} autres promotions` : `Show ${promoServices.length - PROMO_PREVIEW} more promotions`}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Consultations — Always First */}
                    {consultationServices.length > 0 && (
                      <div>
                        <h4 className="text-emerald-400 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                          <Calendar className="w-3 h-3" />
                          {isFr ? "Consultations" : "Consultations"}
                        </h4>
                        <div className="space-y-1.5">
                          {consultationServices.map(svc => {
                            const isSelected = isServiceSelected(svc);
                            return (
                              <div
                                key={svc.variation_id}
                                onClick={() => toggleService(svc)}
                                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                                  isSelected
                                    ? "border-emerald-500 bg-emerald-500/10"
                                    : "border-emerald-700/30 bg-emerald-900/10 hover:border-emerald-500/50"
                                }`}
                                data-testid={`select-service-${svc.variation_id}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className={`text-sm font-medium truncate ${isSelected ? "text-emerald-300" : "text-white"}`}>
                                      {svc.name}
                                    </p>
                                    {svc.ottawa_only && (
                                      <span
                                        className={
                                          svc.linda_only
                                            ? "flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-teal-500/20 text-teal-300 border border-teal-500/40"
                                            : svc.tanya_only
                                            ? "flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-500/40"
                                            : "flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-sky-500/20 text-sky-300 border border-sky-500/40"
                                        }
                                        data-testid={`ottawa-only-chip-${svc.variation_id}`}
                                        title={
                                          svc.linda_only
                                            ? (isFr ? "Offert à Ottawa uniquement · Linda le jeudi" : "Ottawa clinic only · Linda on Thursdays")
                                            : svc.tanya_only
                                            ? (isFr ? "Offert à Ottawa uniquement · Tanya" : "Ottawa clinic only · Tanya")
                                            : (isFr ? "Offert à Ottawa uniquement" : "Ottawa clinic only")
                                        }
                                      >
                                        <MapPin className="w-2.5 h-2.5" />
                                        {svc.linda_only
                                          ? (isFr ? "Ottawa · Linda" : "Ottawa Only · Linda")
                                          : svc.tanya_only
                                          ? (isFr ? "Ottawa · Tanya" : "Ottawa Only · Tanya")
                                          : (isFr ? "Ottawa" : "Ottawa Only")}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mt-0.5">
                                    <span className="text-zinc-400 text-xs flex items-center gap-1">
                                      <Clock className="w-3 h-3" /> {formatDuration(svc.duration_minutes)}
                                    </span>
                                    {svc.price ? (
                                      <span className="text-emerald-400/70 text-xs font-medium">{svc.price}</span>
                                    ) : (
                                      <span className="text-emerald-400 text-[10px] font-semibold uppercase">{isFr ? "Gratuit" : "Free"}</span>
                                    )}
                                  </div>
                                </div>
                                {isSelected && <Check className="w-4 h-4 text-emerald-500 flex-shrink-0" />}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Featured / Most Popular & Most Booked */}
                    {featuredServices.length > 0 && (
                      <div>
                        <h4 className="text-amber-400 text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5">
                          <Flame className="w-3 h-3" />
                          {isFr ? "Populaires et promotions" : "Popular & Promotions"}
                        </h4>
                        <div className="space-y-1.5">
                          {featuredServices.map(svc => {
                            const isSelected = isServiceSelected(svc);
                            const isMostPopular = svc.badge === "most_popular";
                            const isPopular = svc.badge === "popular";
                            return (
                              <div
                                key={svc.variation_id}
                                onClick={() => toggleService(svc)}
                                className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                                  isSelected
                                    ? "border-amber-500 bg-amber-500/10"
                                    : isMostPopular
                                      ? "border-amber-500/40 bg-amber-500/5 hover:border-amber-400"
                                      : "border-zinc-700/50 hover:border-zinc-500 bg-zinc-800/30"
                                }`}
                                data-testid={`select-service-${svc.variation_id}`}
                              >
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <p className={`text-sm font-medium truncate ${isSelected ? "text-amber-300" : "text-white"}`}>
                                      {svc.name}
                                    </p>
                                    {isMostPopular ? (
                                      <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500 text-black">
                                        <Flame className="w-2.5 h-2.5" /> {isFr ? "#1 POPULAIRE" : "#1 POPULAR"}
                                      </span>
                                    ) : isPopular ? (
                                      <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-purple-500/20 text-purple-300 border border-purple-500/30">
                                        <Star className="w-2.5 h-2.5" /> {isFr ? "POPULAIRE" : "POPULAR"}
                                      </span>
                                    ) : (
                                      <span className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                        <TrendingUp className="w-2.5 h-2.5" /> {isFr ? "PLUS RÉSERVÉ" : "MOST BOOKED"}
                                      </span>
                                    )}
                                    {svc.ottawa_only && (
                                      <span
                                        className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-sky-500/20 text-sky-300 border border-sky-500/40"
                                        data-testid={`ottawa-only-chip-${svc.variation_id}`}
                                        title={isFr ? "Offert à Ottawa uniquement" : "Ottawa clinic only"}
                                      >
                                        <MapPin className="w-2.5 h-2.5" /> {isFr ? "Ottawa" : "Ottawa Only"}
                                      </span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-3 mt-0.5">
                                    <span className="text-zinc-400 text-xs flex items-center gap-1">
                                      <Clock className="w-3 h-3" /> {formatDuration(svc.duration_minutes)}
                                    </span>
                                    {svc.price && (
                                      <span className="text-amber-400/70 text-xs font-medium">{svc.price}</span>
                                    )}
                                  </div>
                                  {/* Smart recommender chip removed per request */}
                                </div>
                                {isSelected && <Check className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Regular services by category — collapsible once a
                        client picks something from a category, so the list
                        stays scannable. Categories with no selections stay
                        open by default. */}
                    {serviceSort === "az" && (
                      <div data-testid="service-az-list">
                        <h4 className="text-zinc-400 text-xs font-black uppercase tracking-widest mb-2">{isFr ? "Tous les soins · A → Z" : "All treatments · A → Z"} <span className="text-zinc-600">({azServices.length})</span></h4>
                        <div className="space-y-1.5">
                          {azServices.map((svc) => (
                            <button key={svc.variation_id || svc.id || svc.name} type="button" onClick={() => toggleService(svc)}
                              className={`w-full text-left rounded-lg border px-3 py-2 text-sm transition ${isServiceSelected(svc) ? "bg-amber-500/15 border-amber-500/60 text-white" : "bg-zinc-800/60 border-zinc-700 text-zinc-200 hover:bg-zinc-800"}`}
                              data-testid={`service-az-${svc.variation_id || svc.id}`}>
                              {codeBadge(svc)} <span className="font-semibold">{cleanName(svc.name)}</span>
                              <span className="ml-2 text-[10px] uppercase tracking-wider text-zinc-500">{menuCategory(svc)}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {serviceSort === "category" && !categoriesAutoExpanded && !activeTile && (
                      <div className="mb-3" data-testid="category-tiles">
                        <p className="text-[10px] font-black uppercase tracking-[0.2em] text-zinc-400 mb-2">{isFr ? "Ou choisissez une catégorie" : "Or pick a category"}</p>
                        <div className="grid grid-cols-2 gap-2">
                          {CATEGORY_TILES.filter((t) => tileCount(t) > 0).map((t) => (
                            <button key={t.key} type="button" onClick={() => setActiveTile(t.key)}
                              className="group relative overflow-hidden text-left rounded-xl border border-zinc-700/70 bg-zinc-800/50 hover:border-amber-400 transition min-h-[104px]"
                              data-testid={`category-tile-${t.key}`}>
                              <img src={TILE_PHOTO[t.key]} alt={isFr ? t.fr : t.en} loading="lazy" className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid={`category-tile-photo-${t.key}`} />
                              <span className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-black/20" />
                              <span className="relative block p-3">
                                <span className="block text-sm font-bold text-white leading-tight drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">{isFr ? t.fr : t.en}</span>
                                <span className="block text-[11px] text-zinc-200 mt-0.5 leading-snug drop-shadow-[0_1px_3px_rgba(0,0,0,0.9)]">{isFr ? t.sub_fr : t.sub_en}</span>
                                <span className="block text-[10px] text-amber-300 mt-1 font-bold">{tileCount(t)} {isFr ? (tileCount(t) === 1 ? "soin" : "soins") : (tileCount(t) === 1 ? "treatment" : "treatments")}</span>
                              </span>
                            </button>
                          ))}
                        </div>
                        {consultationServices.length > 0 && (
                          <button type="button" onClick={() => { const c = consultationServices.find((x) => /free|no cost/i.test(x.name || "") && !/linda|tanya|virtual/i.test(x.name || "")) || consultationServices.find((x) => /free|no cost/i.test(x.name || "")) || consultationServices[0]; if (c && !isServiceSelected(c)) toggleService(c); }}
                            className="mt-2 w-full text-left rounded-xl border border-emerald-500/40 bg-emerald-500/10 px-3 py-2.5 text-sm font-semibold text-emerald-200 hover:bg-emerald-500/20"
                            data-testid="not-sure-consult">
                            {isFr ? "Pas sûr(e) de quoi réserver ? Réservez une consultation gratuite →" : "Not sure what to book? Book a free consultation →"}
                          </button>
                        )}
                      </div>
                    )}
                    {serviceSort === "category" && activeTile && !categoriesAutoExpanded && (
                      <div className="flex items-center justify-between mb-2" data-testid="active-tile-header">
                        <button type="button" onClick={() => setActiveTile(null)} className="text-xs text-amber-300 font-bold inline-flex items-center gap-1" data-testid="back-to-categories">
                          <ChevronLeft className="w-3.5 h-3.5" /> {isFr ? "Toutes les catégories" : "All categories"}
                        </button>
                        <span className="text-sm font-bold text-white">{(() => { const t = CATEGORY_TILES.find((x) => x.key === activeTile); return t ? (isFr ? t.fr : t.en) : ""; })()}</span>
                      </div>
                    )}
                    {serviceSort === "category" && activeTile === "laser" && !categoriesAutoExpanded && (
                      <LaserAreaPicker services={groupedServices["Laser Hair Removal"] || []} isSelected={isServiceSelected} onToggle={toggleService} codeOf={(svc) => codeOf(svc, treatmentCodes)} isFr={isFr} />
                    )}
                    {serviceSort === "category" && Object.entries(groupedServices).filter(([category]) => {
                      if (categoriesAutoExpanded) return true;
                      if (!activeTile || activeTile === "laser") return false;
                      const t = CATEGORY_TILES.find((x) => x.key === activeTile);
                      return !!t && t.cats.includes(category);
                    }).map(([category, services]) => {
                      const pickedInCat = services.filter(s => isServiceSelected(s)).length;
                      const collapsed = categoriesAutoExpanded ? collapsedCategories.has(category) : (activeTile ? false : (openCategory !== category && pickedInCat === 0));
                      return (
                        <div key={category}>
                          {category && (() => {
                            const info = menuCategoryInfo(category, isFr ? "fr" : "en");
                            return (
                            <button
                              type="button"
                              onClick={() => toggleCategoryCollapse(category)}
                              className="w-full flex items-center gap-3 text-left mb-2 rounded-xl border border-zinc-700/60 bg-zinc-800/40 hover:border-amber-500/60 transition-colors p-2 pr-3"
                              data-testid={`category-toggle-${category.replace(/\s+/g, '-').toLowerCase()}`}
                              aria-expanded={!collapsed}
                            >
                              <img src={info.src} alt={info.key}
                                width={56}
                                height={56}
                                loading="lazy"
                                decoding="async"
                                className="w-14 h-14 rounded-xl object-cover flex-shrink-0 border-2 border-amber-500/50"
                                data-testid={`category-thumb-${info.key}`}
                              />
                              <span className="flex-1 min-w-0">
                                <span className="flex items-center gap-1.5 text-amber-300 text-sm font-bold">
                                  {category}
                                  {pickedInCat > 0 && (
                                    <span className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-500/25 text-amber-200 text-[10px] font-bold">
                                      {pickedInCat} {isFr ? "choisi" : "picked"}
                                    </span>
                                  )}
                                </span>
                                <span className="block text-zinc-300 text-xs leading-snug mt-0.5" data-testid={`category-blurb-${info.key}`}>{info.blurb}</span>
                              </span>
                              <span className="flex items-center gap-1 text-zinc-400 text-[10px] font-medium flex-shrink-0">
                                {collapsed
                                  ? (isFr ? `Afficher ${services.length}` : `Show ${services.length}`)
                                  : (isFr ? "Masquer" : "Hide")}
                                {collapsed ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronUp className="w-3.5 h-3.5" />}
                              </span>
                            </button>
                            );
                          })()}
                          {!collapsed && (
                            <div className="space-y-1.5">
                              {services.map(svc => {
                                const isSelected = isServiceSelected(svc);
                                return (
                                  <div
                                    key={svc.variation_id}
                                    onClick={() => toggleService(svc)}
                                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-all ${
                                      isSelected
                                        ? "border-amber-500 bg-amber-500/10"
                                        : "border-zinc-700/50 hover:border-zinc-500 bg-zinc-800/30"
                                    }`}
                                    data-testid={`select-service-${svc.variation_id}`}
                                  >
                                    {eminencePhotoForVariation(svc.variation_id) && (
                                      <img src={eminencePhotoForVariation(svc.variation_id)} alt={svc.name || "Éminence facial"}
                                        width={56}
                                        height={56}
                                        loading="lazy"
                                        className="w-14 h-14 rounded-lg object-cover flex-shrink-0 ring-1 ring-white/15"
                                        data-testid={`service-photo-${svc.variation_id}`}
                                      />
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-2">
                                        {codeBadge(svc)}
                                        <p className={`text-sm font-medium truncate ${isSelected ? "text-amber-300" : "text-white"}`}>
                                          {cleanName(svc.name)}
                                        </p>
                                        {svc.ottawa_only && (
                                          <span
                                            className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider bg-sky-500/20 text-sky-300 border border-sky-500/40"
                                            data-testid={`ottawa-only-chip-${svc.variation_id}`}
                                            title={isFr ? "Offert à Ottawa uniquement" : "Ottawa clinic only"}
                                          >
                                            <MapPin className="w-2.5 h-2.5" /> {isFr ? "Ottawa" : "Ottawa Only"}
                                          </span>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-3 mt-0.5">
                                        <span className="text-zinc-400 text-xs flex items-center gap-1">
                                          <Clock className="w-3 h-3" /> {formatDuration(svc.duration_minutes)}
                                        </span>
                                        {svc.price && (
                                          <span className="text-amber-400/70 text-xs font-medium">{svc.price}</span>
                                        )}
                                      </div>
                                      {/* Smart recommender chip removed per request */}
                                    </div>
                                    {isSelected && <Check className="w-4 h-4 text-amber-500 flex-shrink-0" />}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {/* Bottom summary panel removed — replaced by the
                      sticky "Your Selection" tray above the services list,
                      which is more discoverable and stays in view. */}
                </>
              )}
            </div>
          )}

          {/* STEP 2: Select Date */}
          {step === 2 && (
            <div data-testid="step-date">
              {/* Oct 26 2026 · Karine: "they already told you what they want — ask only
                  'add any other treatments? yes/no' and then book." Shown once when the
                  treatment was pre-selected (promo / menu / card tap). */}
              {hydraPromoPicked && !dermaAddOnPicked && !dermaUpsellDismissed && (
                <div className="mb-3 rounded-xl border border-teal-400/50 bg-teal-500/10 p-3 sm:p-4" data-testid="derma-upsell-prompt">
                  <p className="text-teal-200 text-xs font-bold uppercase tracking-wider mb-1">{isFr ? "Petit extra populaire" : "Popular add-on"}</p>
                  <p className="text-white text-base font-bold" data-testid="derma-upsell-question">{isFr ? "Ajouter un Dermaplaning pour 49 $ ?" : "Add Dermaplaning for $49?"}</p>
                  <p className="text-zinc-300 text-sm mt-1">{isFr ? "15 min de plus · peau ultra-lisse, maquillage parfait · même visite avec Tanya." : "15 extra minutes · baby-smooth skin, flawless makeup · same visit with Tanya."}</p>
                  <div className="flex gap-2 mt-2">
                    <button type="button" onClick={addDermaAddOn} className="flex-1 rounded-full bg-teal-400 hover:bg-teal-300 text-black text-sm font-black py-2.5" data-testid="derma-upsell-yes">
                      {isFr ? "Oui, ajouter (+49 $)" : "Yes, add it (+$49)"}
                    </button>
                    <button type="button" onClick={() => setDermaUpsellDismissed(true)} className="flex-1 rounded-full border border-zinc-600 hover:border-teal-300 text-zinc-100 text-sm font-bold py-2.5" data-testid="derma-upsell-no">
                      {isFr ? "Non merci" : "No thanks"}
                    </button>
                  </div>
                </div>
              )}
              {showMakeoverUpsell && (
                <div className="mb-3 rounded-xl border border-amber-400/50 bg-amber-500/10 p-3 sm:p-4" data-testid="makeover-upsell-prompt">
                  <p className="text-amber-200 text-xs font-bold uppercase tracking-wider mb-1">{isFr ? "Petit extra populaire · 79 $" : "Popular add-on · $79"}</p>
                  <p className="text-white text-base font-bold" data-testid="makeover-upsell-question">
                    {isFr ? "Ajouter un Lip Flip, Lifting Sourcils ou Lifting Sourire pour 79 $ ?" : "Add a Lip Flip, Brow Lift or Smile Lift for $79?"}
                  </p>
                  <p className="text-zinc-300 text-sm mt-1">{isFr ? "Rég. 125 $ · même visite, 15 min de plus." : "Reg. $125 · same visit, 15 extra minutes."}</p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
                    {makeoverAddOnOptions.map((o) => (
                      <button key={o.label} type="button" onClick={() => addMakeoverAddOn(o)} className="rounded-full bg-amber-400 hover:bg-amber-300 text-black text-sm font-black py-2.5" data-testid={`makeover-upsell-${o.label.toLowerCase().replace(/\s+/g, "-")}`}>
                        {isFr ? o.fr : o.label} <span className="font-semibold">+$79</span>
                      </button>
                    ))}
                    <button type="button" onClick={() => setMakeoverUpsellDismissed(true)} className="rounded-full border border-zinc-600 hover:border-amber-300 text-zinc-100 text-sm font-bold py-2.5" data-testid="makeover-upsell-no">
                      {isFr ? "Non merci" : "No thanks"}
                    </button>
                  </div>
                </div>
              )}
              {selectedServices.length > 0 && !addAnotherAnswered && !showMakeoverUpsell && !(hydraPromoPicked && !dermaAddOnPicked && !dermaUpsellDismissed) && (
                <div className="mb-3 rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 sm:p-4" data-testid="prefill-add-another-prompt">
                  <p className="text-amber-200 text-xs font-bold uppercase tracking-wider mb-1">{isFr ? "Votre soin" : "Your treatment"}</p>
                  <p className="text-white text-sm font-semibold" data-testid="prefill-selected-name">{selectedServices.map((s) => s.name).join(" + ")}</p>
                  {(totalDurationMinutes > 0 || totalPriceCents > 0) && (
                    <p className="text-amber-100 text-xs mt-1 tabular-nums" data-testid="prefill-selected-meta">
                      {[formatDuration(totalDurationMinutes), totalPriceCents > 0 ? `$${(totalPriceCents / 100).toFixed(0)} ${isFr ? "+ taxes" : "+ tax"}` : ""].filter(Boolean).join(" · ")}
                    </p>
                  )}
                  <p className="text-zinc-300 text-sm mt-2">{isFr ? "Souhaitez-vous ajouter un autre soin ?" : "Would you like to add any other treatments?"}</p>
                  <div className="flex gap-2 mt-2">
                    <button type="button" onClick={() => setAddAnotherAnswered(true)} className="flex-1 rounded-full bg-amber-500 hover:bg-amber-400 text-black text-sm font-black py-2.5" data-testid="prefill-add-another-no">
                      {isFr ? "Non — choisir une date" : "No — pick my date"}
                    </button>
                    <button type="button" onClick={() => { setAddAnotherAnswered(true); setServiceSearch(""); setStep(1); }} className="flex-1 rounded-full border border-zinc-600 hover:border-amber-400 text-zinc-100 text-sm font-bold py-2.5" data-testid="prefill-add-another-yes">
                      {isFr ? "Oui, ajouter" : "Yes, add another"}
                    </button>
                  </div>
                </div>
              )}
              {any3PromoPicked && <Any3AreasPicker value={any3Areas} onChange={setAny3Areas} isFr={isFr} />}
              {/* Earliest Anywhere = Karine's 3 clinics only. Tanya/Linda
                  treatments are Ottawa-only, so hide it when the chosen
                  treatment is theirs even if the URL forgot `provider=`. */}
              {!isTanya && !isLinda && !lockLocationId && !selectedServices.some((s) => providerOf(s) !== "karine") && (
                <EarliestAnywhereToggle mode={dateMode} onChange={setDateMode} isFr={isFr} clinicName={selectedLocation?.name || ""}
                  prefetch={{ duration: totalDurationMinutes || 15, provider: (effectiveProvider || "karine").toLowerCase(), variationId: pickedVidRef.current }} />
              )}
              {dateMode === "anywhere" && !isTanya && !isLinda && !lockLocationId && !selectedServices.some((s) => providerOf(s) !== "karine") && (
                <EarliestAnywherePanel
                  duration={totalDurationMinutes || 15}
                  provider={(effectiveProvider || "karine").toLowerCase()}
                  variationId={pickedVidRef.current}
                  services={selectedServices}
                  prefill={{ name: customerInfo?.firstName || customerInfo?.first_name || "", email: customerInfo?.email || prefillEmail || rebookEmail || "", phone: customerInfo?.phone || "" }}
                  isFr={isFr}
                  onPick={(o, t) => {
                    setDateMode("clinic");
                    if (String(o.location_id) !== String(selectedLocation?.id) && Array.isArray(o.mapped) && o.mapped.length) setSelectedServices(o.mapped);
                    pickFastTrackOption({
                      location: { id: String(o.location_id), name: o.location_name || LOCATION_NAMES[o.location_id] || o.location_id },
                      dateObj: { date: o.date, day_name: o.day_name, formatted: o.formatted },
                      time: t,
                    });
                  }}
                />
              )}
              <div className="flex items-center justify-between mb-4">
                <p className="text-zinc-200 text-base">
                  {dateMode === "anywhere"
                    ? (isFr ? `Ou choisissez un jour à ${selectedLocation?.name} :` : `Or pick a day at ${selectedLocation?.name}:`)
                    : (isFr ? `Choisissez un jour à ${selectedLocation?.name} :` : `Pick a day at ${selectedLocation?.name}:`)}
                </p>
                {/* Preloading indicator — hidden once we've crossed the
                    8s "Square is slow" threshold so we don't show a
                    "loading" hint at the same time as the escape hatch. */}
                {(datesRefreshing || (preloadingDates.size > 0 && loadingElapsed < 20)) && availableDates.length > 0 && (
                  <span className="text-sm text-amber-300/90 flex items-center gap-1.5" data-testid="preload-indicator">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {isFr ? "Vérification d'autres jours…" : "Checking more days…"}
                  </span>
                )}
              </div>
              {loading && availableDates.length === 0 ? (
                // Skeletons ONLY when there's truly nothing to show.
                // If we have ANY dates (cached, snapshot, or fresh), render
                // them immediately even while a background fetch is in
                // flight. True stale-while-revalidate — the user never
                // sees skeletons when usable data is already in memory.
                <div>
                  <div className="h-4 w-24 bg-zinc-700 rounded mb-2 animate-pulse" />
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                    {[...Array(6)].map((_, i) => <SkeletonDateButton key={i} />)}
                  </div>
                  {/* Auto-recovery is fully transparent — see the
                      `dates-step auto-recovery` effect below. The user
                      never sees a "tap to reload" button; the page
                      recovers itself in the background. The status text
                      below is informational only. */}
                  {loadingElapsed >= 1 && (
                    <p className="text-zinc-500 text-xs text-center mt-4 flex items-center justify-center gap-2" data-testid="auto-recovering">
                      <Loader2 className="w-3 h-3 animate-spin text-amber-400" />
                      {loadingElapsed < 3
                        ? (isFr ? "Connexion à votre calendrier…" : "Connecting to your calendar…")
                        : loadingElapsed < 9
                          ? (isFr ? "Vérification…" : "Checking…")
                          : loadingElapsed < 15
                            ? (isFr ? "Nouvelle tentative…" : "Retrying…")
                            : (isFr ? "Récupération automatique…" : "Auto-recovering…")}
                    </p>
                  )}
                </div>
              ) : availableDates.length === 0 ? (
                <div className="space-y-4" data-testid="no-dates-state">
                  <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl px-4 py-4">
                    <p className="text-amber-100 text-base font-semibold">{isFr ? "Aucune journée libre ici pour le moment" : "No open days here right now"}</p>
                    <p className="text-zinc-300 text-sm mt-1">{isFr ? "Pas de souci — voici d'autres façons d'obtenir votre rendez-vous :" : "No problem — here are other ways to get your appointment:"}</p>
                  </div>
                  <OtherClinicsCard currentId={selectedLocation?.id} onPick={switchClinic} isFr={isFr} locked={isTanya || isLinda || Boolean(lockLocationId)} services={selectedServices} />
                  {/* Sep 28 2026 · Karine ask — "If a client is trying to
                      book with Karine but she is booked up, ask them if
                      they'd like to book with Linda Lafrance NP for
                      Botox / Filler." The card auto-hides for
                      non-Botox/Filler services and non-Ottawa clients,
                      so it only ever appears in the right context. */}
                  {(!selectedProvider || selectedProvider === "karine") && (
                    <LindaCrossSellCard
                      serviceName={(selectedServices && selectedServices[0] && selectedServices[0].name)
                        || primaryService?.name
                        || ""}
                      serviceVariationId={(selectedServices && selectedServices[0] && selectedServices[0].variation_id) || null}
                      locationId={selectedLocation?.id}
                      preferredDate={null}
                      isFr={isFr}
                      onPickLindaSlot={(slot) => {
                        const params = new URLSearchParams({
                          provider: "linda",
                          location: slot.location_id || "3",
                          date: slot.date,
                          time: slot.start_time,
                        });
                        if (slot.variation_id) params.set("variation_id", slot.variation_id);
                        window.location.href = `/book?${params.toString()}`;
                      }}
                    />
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  {hasLindaDates && <ProviderToggle value={providerView} onChange={setProviderView} isFr={isFr} />}
                  {visibleDates.length === 0 && (
                    <p className="text-zinc-300 text-base text-center py-3" data-testid="no-dates-for-provider">
                      {isFr ? "Aucune journée pour ce choix — essayez « Première dispo »." : "No days for this choice — try “First available”."}
                    </p>
                  )}
                  {Object.entries(datesByMonth).slice(0, showAllMonths ? undefined : 2).map(([month, dates]) => {
                    // Parse "YYYY-MM" as a LOCAL date (not UTC) to avoid the
                    // timezone-shift bug where "2026-06" rendered as
                    // "May 2026" because `new Date("2026-06-01")` is parsed
                    // as UTC midnight which is the previous day in any
                    // North American timezone.
                    const [yr, mo] = month.split("-");
                    const monthDate = new Date(parseInt(yr, 10), parseInt(mo, 10) - 1, 1);
                    const monthName = monthDate.toLocaleString(isFr ? "fr-CA" : "en-US", { month: "long", year: "numeric" });
                    return (
                      <div key={month}>
                        <h4 className="text-amber-400 text-sm font-medium mb-2 capitalize">{monthName}</h4>
                        {/* iPad-tightened (Feb 24 2026): went 3 → 4 → 5
                            columns at md / lg so a full month of dates
                            fits without excess white-space on iPad
                            portrait (~768 px) and landscape (~1024 px). */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                          {dates.map(d => {
                            const isSelected = selectedDate?.date === d.date;
                            return (
                              <button
                                key={d.date}
                                onClick={() => pickDate(d)}
                                className={`min-h-[56px] px-3 py-2.5 rounded-xl text-base text-left whitespace-nowrap transition-all ${
                                  isSelected
                                    ? "bg-amber-500 text-zinc-950 font-semibold"
                                    : "bg-zinc-800 text-zinc-100 hover:bg-zinc-700"
                                }`}
                                data-testid={`date-${d.date}`}
                              >
                                <Calendar className="w-4 h-4 inline mr-1.5 opacity-60" />
                                {d.day_name.substring(0, 3)} {parseInt(d.date.substring(8), 10)} {new Date(2000, parseInt(d.date.substring(5, 7), 10) - 1, 1).toLocaleString(isFr ? "fr-CA" : "en-US", { month: "short" })}
                                {/* Oct 2 2026 · Karine ask — Linda Pill on
                                    Thursday chips. Only shown when the
                                    backend's dates response tagged this
                                    date with provider_hint="linda" (i.e.,
                                    the picked service is Linda-eligible
                                    and the date is a Thursday Linda works). */}
                                {d.provider_hint === "linda" && (
                                  <span
                                    className={`block mt-0.5 text-xs font-medium leading-tight ${
                                      isSelected
                                        ? "text-zinc-900"
                                        : "text-emerald-400"
                                    }`}
                                    data-testid={`date-${d.date}-linda-pill`}
                                  >
                                    {isFr ? "avec Linda · IP" : "with Linda · NP"}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                  {!showAllMonths && Object.keys(datesByMonth).length > 2 && (
                    <button
                      type="button"
                      onClick={() => setShowAllMonths(true)}
                      className="w-full min-h-[52px] rounded-xl border border-amber-500/50 text-amber-300 text-base font-semibold hover:bg-amber-500/10 inline-flex items-center justify-center gap-2"
                      data-testid="show-more-months"
                    >
                      <ChevronDown className="w-5 h-5" />
                      {isFr ? "Voir plus de jours" : "Show more days"}
                      <span className="text-zinc-400 font-normal text-sm">
                        {(() => {
                          const rest = Object.keys(datesByMonth).slice(2);
                          const lab = (m) => new Date(parseInt(m.slice(0, 4), 10), parseInt(m.slice(5), 10) - 1, 1).toLocaleString(isFr ? "fr-CA" : "en-US", { month: "short", year: "numeric" });
                          return rest.length > 2 ? `(${lab(rest[0])} → ${lab(rest[rest.length - 1])})` : `(${rest.map(lab).join(", ")})`;
                        })()}
                      </span>
                    </button>
                  )}
                </div>
              )}
              {/* Date fallback — Messenger / Email / Waitlist CTAs */}
              <div className="mt-4">
                <DateFallbackHelp variant="dark" />
              </div>
            </div>
          )}

          {/* STEP 3: Select Time */}
          {step === 3 && (
            <div data-testid="step-time">
              <DayStrip dates={visibleDates} selectedDate={selectedDate} onPick={pickDate} onShowAll={() => setStep(2)} isFr={isFr} />
              <div className="flex items-center justify-between mb-1">
                <p className="text-zinc-200 text-base">
                  {isFr
                    ? `Heures libres le ${selectedDate?.formatted || selectedDate?.date} avec ${displayProviderName} :`
                    : `Open times on ${selectedDate?.formatted || selectedDate?.date} with ${displayProviderName}:`}
                </p>
                {/* Real-time availability indicator with cache status */}
                <div className="flex items-center gap-2">
                  {lastUpdated && !loading && (
                    <button
                      onClick={() => setRefreshKey(k => k + 1)}
                      className="flex items-center gap-1.5 text-xs text-zinc-500 hover:text-amber-400 transition-colors"
                      data-testid="refresh-availability"
                    >
                      <RefreshCw className="w-3 h-3" />
                      <span>{timeSinceUpdate}</span>
                    </button>
                  )}
                </div>
              </div>
              <p className="text-zinc-300 text-sm mb-4">
                {isFr
                  ? `Chaque créneau réserve ${formatDuration(totalDurationMinutes)} ${selectedServices.length > 1 ? `pour ${selectedServices.length} traitements` : `pour ${primaryService?.name || ""}`}`
                  : `Each slot reserves ${formatDuration(totalDurationMinutes)} ${selectedServices.length > 1 ? `for ${selectedServices.length} treatments` : `for ${primaryService?.name || ""}`}`}
              </p>
              {loading || (!lastUpdated && !error) ? (
                // Skeleton loader grid + "Connecting…" friendly status so
                // the user always knows we're reaching Square, never sees
                // a bare/blank panel during the post-deploy reconnect.
                <div>
                  <p className="text-zinc-400 text-xs text-center mb-3 flex items-center justify-center gap-2" data-testid="time-slots-connecting">
                    <Loader2 className="w-3 h-3 animate-spin text-amber-400" />
                    {loadingElapsed < 3
                      ? (isFr ? "Connexion à votre calendrier…" : "Connecting to your calendar…")
                      : loadingElapsed < 9
                        ? (isFr ? "Chargement des créneaux…" : "Loading available times…")
                        : (isFr ? "Récupération automatique…" : "Auto-recovering…")}
                  </p>
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 gap-2">
                    {[...Array(12)].map((_, i) => <SkeletonTimeSlot key={i} />)}
                  </div>
                </div>
              ) : error ? (
                // Distinct retry-friendly error block (timeout/network/server)
                <div className="text-center py-6 space-y-3">
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4">
                    <p className="text-red-300 text-sm leading-relaxed">{error}</p>
                  </div>
                  <Button
                    onClick={() => setRefreshKey(k => k + 1)}
                    className="bg-amber-600 hover:bg-amber-500 text-white text-sm"
                    data-testid="retry-availability"
                  >
                    <RefreshCw className="w-4 h-4 mr-1" /> {isFr ? "Réessayer" : "Try again"}
                  </Button>
                  <BookDirectFallback contextLocationId={selectedLocation?.id} />
                </div>
              ) : availableTimeSlots.length === 0 ? (
                <div className="text-center py-6 space-y-4">
                  {/* Feb 2026 · Karine panic — the old alarmist "No
                      available time slots" banner was making clients
                      think the site was broken on cold-cache misses
                      (where NearestSlotsCard below would then find
                      slots on the SAME day). Reworded to a friendly
                      "we're looking at today — pick one below" tone
                      and dropped the red/zinc panic styling. */}
                  <div className="bg-amber-500/10 border border-amber-500/40 rounded-lg px-4 py-3">
                    <p className="text-amber-200 text-sm leading-relaxed">
                      {isFr
                        ? <>Cette journée est complète à <b>{selectedLocation?.name}</b>. Voici ce que nous pouvons vous offrir à la place&nbsp;↓</>
                        : <>That day is full at <b>{selectedLocation?.name}</b>. Here's what we can offer instead&nbsp;↓</>}
                    </p>
                  </div>

                  {/* Feb 2026 · Karine ask — Auto-suggest nearest slot when
                      the picked date is fully booked. Same day + next day
                      fallback so clients tap-and-go instead of hunting. */}
                  <NearestSlotsCard
                    locationId={selectedLocation?.id}
                    date={selectedDate?.date}
                    durationMinutes={totalDurationMinutes || 30}
                    provider={(selectedProvider || "karine").toLowerCase()}
                    variationId={(selectedServices && selectedServices[0] && selectedServices[0].variation_id) || null}
                    maxDays={14}
                    limit={6}
                    isFr={isFr}
                    onPick={(picked) => {
                      const targetDate = (availableDates || []).find(d => d.date === picked.date)
                        || { date: picked.date, day_name: picked.day_name };
                      setSelectedDate(targetDate);
                      setSelectedTime(picked.start_time);
                      setRefreshKey(k => k + 1);
                    }}
                    waitlist={{
                      treatmentName: (selectedServices && selectedServices[0] && selectedServices[0].name)
                        || primaryService?.name
                        || "Requested treatment",
                      locationName: selectedLocation?.name || "",
                      client: {
                        name: `${customerInfo.first_name || ""} ${customerInfo.last_name || ""}`.trim(),
                        email: customerInfo.email || prefillEmail || "",
                        phone: customerInfo.phone || "",
                      },
                    }}
                  />

                  {/* Iter 514 · Sep 26 2026 · Karine ask —
                      When Karine is booked solid on her Mon/Fri
                      injectable days for Botox/Filler, offer Linda's
                      Thursday openings starting Oct 8 2026. Renders
                      NULL when service isn't Botox/Filler or Karine
                      has openings within 7 days. Supersedes the older
                      "never surface another provider" rule for those
                      two treatments only. */}
                  <LindaCrossSellCard
                    serviceName={(selectedServices && selectedServices[0] && selectedServices[0].name)
                      || primaryService?.name
                      || ""}
                    serviceVariationId={(selectedServices && selectedServices[0] && selectedServices[0].variation_id) || null}
                    locationId={selectedLocation?.id}
                    preferredDate={selectedDate?.date}
                    isFr={isFr}
                    onPickLindaSlot={(slot) => {
                      const params = new URLSearchParams({
                        provider: "linda",
                        location: slot.location_id || "3",
                        date: slot.date,
                        time: slot.start_time,
                      });
                      if (slot.variation_id) params.set("variation_id", slot.variation_id);
                      window.location.href = `/book?${params.toString()}`;
                    }}
                  />

                  {/* Cross-provider suggestion banner — only shows when the
                      backend found a compatible slot with the OTHER provider.
                      Quiet inline note per Karine's spec (May 28 2026):
                        "Karine is full this week — Jenny has Monday at
                         11am if you'd like →"
                      Plus up to 2 alternative dates as compact chips. */}
                  {/* Alternate-provider (Jenny) suggestion block has been
                      REMOVED per Karine's explicit request. We never want
                      to surface another provider in public booking UI. The
                      generic "Different date / Different location" panel
                      below is the only fallback shown to clients. */}
                  
                  <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                    <p className="text-amber-400 font-medium text-sm mb-2">
                      {isFr ? "💡 Suggestion:" : "💡 Suggestion:"}
                    </p>
                    <p className="text-zinc-300 text-sm mb-3">
                      {(() => {
                        const provName = selectedProvider === "tanya"
                          ? (isFr ? "Tanya Jackson (esthéticienne médicale)" : "Tanya Jackson (Medical Aesthetician)")
                          : selectedProvider === "linda"
                          ? (isFr ? "Linda Lafrance, IPS" : "Linda Lafrance, NP")
                          : (isFr ? "Karine Lauzon, IA" : "Karine Lauzon, RN");
                        if (selectedProvider === "tanya") {
                          return isFr
                            ? `${provName} reçoit à Ottawa les lundis et vendredis. Essayez une autre date :`
                            : `${provName} sees clients in Ottawa on Mondays and Fridays. Try another date:`;
                        }
                        return isFr
                          ? `${provName} peut être disponible à un autre emplacement ou à une date différente. Essayez:`
                          : `${provName} may be available at another location or on a different date. Try:`;
                      })()}
                    </p>
                    <div className="flex flex-col sm:flex-row gap-2 justify-center">
                      <Button 
                        variant="outline" 
                        onClick={() => setStep(2)} 
                        className="text-base min-h-[48px] border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
                        data-testid="no-slots-different-date"
                      >
                        <Calendar className="w-4 h-4 mr-1" /> {isFr ? "Autre date" : "Different date"}
                      </Button>
                    </div>
                    <div className="mt-3">
                      <OtherClinicsCard currentId={selectedLocation?.id} onPick={switchClinic} isFr={isFr} locked={isTanya || isLinda || Boolean(lockLocationId)} services={selectedServices} />
                    </div>
                    {/* Feb 2026 · One-tap waitlist auto-book — when the
                        client wants THIS exact slot, save it server-side
                        and auto-book them the second a Square cancel frees it. */}
                    {selectedTime && <div className="mt-3">
                      <SaveMySlotButton
                        API={API}
                        locationId={selectedLocation?.id}
                        date={selectedDate?.date}
                        startTime={selectedTime}
                        provider={(selectedProvider && selectedProvider !== "karine") ? selectedProvider : null}
                        serviceName={(selectedServices?.[0]?.name) || primaryService?.name || null}
                        serviceVariationId={primaryService?.variation_id || null}
                        serviceDurationMinutes={totalDurationMinutes || null}
                        services={selectedServices.map(s => ({ name: s.name }))}
                        serviceSegments={selectedServices.map(s => ({
                          name: s.name,
                          service_variation_id: s.variation_id,
                          duration_minutes: s.duration_minutes || 15,
                        }))}
                        customer={{
                          first_name: customerInfo.first_name,
                          last_name: customerInfo.last_name,
                          email: customerInfo.email,
                          phone: customerInfo.phone,
                        }}
                        isFr={isFr}
                      />
                    </div>}
                  </div>
                  <BookDirectFallback compact contextLocationId={selectedLocation?.id} />
                  
                  <p className="text-zinc-600 text-xs">
                    {isFr 
                      ? "Les disponibilités varient selon l'emplacement. Carleton Place, Kanata et Ottawa ont des horaires différents." 
                      : "Availability varies by location. Carleton Place, Kanata, and Ottawa have different schedules."}
                  </p>
                </div>
              ) : (
                <>
                  {/* ── AUTO-SUGGEST NEXT SLOT (Mar 4 2026 · Josee Bellerive) ──
                      When Square 409s a slot we compute the next real
                      opening on the same day and surface it here so the
                      client doesn't have to guess where to tap next. */}
                  {suggestedSlot && suggestedSlot.date === selectedDate && (
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedTime(suggestedSlot.time);
                        setSuggestedSlot(null);
                      }}
                      className="w-full mb-3 flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-gradient-to-r from-amber-500/25 via-amber-400/15 to-amber-500/5 border-2 border-amber-400 hover:from-amber-500/40 transition-all shadow-lg shadow-amber-500/20"
                      data-testid="booking-next-slot-suggestion"
                    >
                      <div className="text-left">
                        <p className="text-[10px] uppercase tracking-[2px] font-black text-amber-300">
                          {isFr ? "Prochain créneau libre" : "Next available"}
                        </p>
                        <p className="text-white text-lg font-black tabular-nums">
                          {formatTime(suggestedSlot.time)}
                        </p>
                      </div>
                      <span className="text-[11px] font-black uppercase tracking-widest text-zinc-900 bg-amber-400 rounded-full px-3 py-1.5">
                        {isFr ? "Réserver → " : "Lock it in → "}
                      </span>
                    </button>
                  )}
                  {/* ITER 293o · Smart Suggest · Sort so ⚡ Best-Fit slots
                      (start immediately after a previous booking) show
                      first in a gold-outlined chip. Everything else follows
                      in normal time order. */}
                  <TimeOfDayFilter slots={availableTimeSlots} value={todFilter} onChange={setTodFilter} isFr={isFr} />
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                    {[...availableTimeSlots]
                      .filter(slot => todFilter === "all" || todBucket(slot.start_time) === todFilter)
                      // Mar 4 2026 · Josee Bellerive DM — hide any slot
                      // this session has already been rejected by
                      // Square with a 409, so clients never re-tap the
                      // same broken time and loop back to Confirm.
                      .filter(slot => !deadSlots.has(`${selectedDate}T${slot.start_time}`))
                      .sort((a, b) => {
                        if (a.best_fit && !b.best_fit) return -1;
                        if (!a.best_fit && b.best_fit) return 1;
                        return a.start_time.localeCompare(b.start_time);
                      }).map(slot => {
                      const isSelected = selectedTime === slot.start_time;
                      const isBestFit = !!slot.best_fit;
                      return (
                        <button
                          key={slot.start_time}
                          onClick={() => {
                            setSelectedTime(slot.start_time);
                            // Manual pick clears the auto-suggest banner
                            // so it doesn't linger once Josee has chosen.
                            if (suggestedSlot) setSuggestedSlot(null);
                          }}
                          className={`relative min-h-[52px] px-3 py-2.5 rounded-xl text-base font-semibold transition-all ${
                            isSelected
                              ? "bg-amber-500 text-zinc-950 ring-2 ring-amber-300"
                              : isBestFit
                              ? "bg-gradient-to-br from-amber-400/30 to-amber-600/30 border-2 border-amber-400 text-amber-100 hover:from-amber-400/50 hover:to-amber-600/50 shadow-lg shadow-amber-500/30"
                              : "bg-zinc-800 text-zinc-100 hover:bg-zinc-700"
                          }`}
                          data-testid={`time-${slot.start_time}${isBestFit ? "-bestfit" : ""}`}
                          title={isBestFit ? "Best fit — no gap after the previous booking" : ""}
                        >
                          {isBestFit && !isSelected && (
                            <span className="absolute -top-1.5 -right-1.5 text-[9px] font-black bg-amber-400 text-black rounded-full px-1.5 py-0.5 shadow leading-tight">
                              ⚡ BEST
                            </span>
                          )}
                          <Clock className="w-3.5 h-3.5 inline mr-1 opacity-60" />
                          {formatTime(slot.start_time)}
                        </button>
                      );
                    })}
                  </div>
                  {/* Refresh hint if sitting on page for a while */}
                  {lastUpdated && (Date.now() - lastUpdated > 60000) && (
                    <p className="text-center text-xs text-zinc-600 mt-4">
                      {isFr 
                        ? "Ces créneaux peuvent changer. Cliquez sur l'icône de rafraîchissement pour voir les dernières disponibilités." 
                        : "Slots may change. Tap the refresh icon above to see latest availability."}
                    </p>
                  )}

                  {/* Iter 454 · Feb 2026 · Karine ask — after picking a
                      time, ask ONE simple novice-friendly question:
                      "Book another treatment?" Yes goes back to the
                      service picker (existing selections preserved);
                      No advances to customer info. Replaces the huge
                      scrolling menu that used to overwhelm first-time
                      bookers. */}
                  {selectedTime && (
                    <div
                      className="mt-6 p-4 sm:p-5 rounded-xl border-2 border-amber-500/50 bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent shadow-lg shadow-amber-500/10"
                      data-testid="add-another-prompt"
                    >
                      <p className="text-amber-100 text-center text-sm sm:text-base font-semibold mb-3 leading-snug">
                        {isFr
                          ? "Souhaitez-vous réserver un autre traitement dans la même visite ?"
                          : "Would you like to book another treatment in the same visit?"}
                      </p>
                      <div className="grid grid-cols-2 gap-2 sm:gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            // Iter 454 · Karine ask — clear date+time so
                            // the re-picked service triggers a fresh
                            // dates → times sequence with the new total
                            // duration (avoids stale time slots).
                            setSelectedTime(null);
                            setSelectedDate(null);
                            setStep(1);
                            try {
                              toast(
                                isFr
                                  ? "Choisissez un autre traitement à ajouter"
                                  : "Pick another treatment to add",
                                { duration: 3000 }
                              );
                            } catch (_) { /* toast optional */ }
                          }}
                          className="border-amber-500/60 bg-zinc-900/40 text-amber-200 hover:bg-amber-500/10 hover:text-amber-100 text-sm sm:text-base font-semibold py-3 sm:py-3.5"
                          data-testid="add-another-yes"
                        >
                          <Plus className="w-4 h-4 mr-1.5" />
                          {isFr ? "Oui, en ajouter un" : "Yes, add another"}
                        </Button>
                        <Button
                          type="button"
                          onClick={() => setStep(4)}
                          className="bg-amber-500 hover:bg-amber-400 text-zinc-950 text-sm sm:text-base font-bold py-3 sm:py-3.5 shadow-md"
                          data-testid="add-another-no"
                        >
                          {isFr ? "Non, continuer" : "No, continue"}
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
              {/* Date fallback — Messenger / Email / Waitlist CTAs */}
              <div className="mt-4">
                <DateFallbackHelp variant="dark" />
              </div>
            </div>
          )}

          {/* STEP 4: Customer Info */}
          {step === 4 && (
            <div className="space-y-4" data-testid="step-info">
              {/* #2 Slot hold countdown */}
              {holdSecondsLeft !== null && holdSecondsLeft > 0 && (
                <div
                  className={`flex items-center justify-between gap-3 px-3 py-2 rounded-lg border ${
                    holdSecondsLeft < 60
                      ? "bg-red-500/10 border-red-500/40"
                      : "bg-amber-500/10 border-amber-500/30"
                  }`}
                  data-testid="slot-hold-countdown"
                >
                  <div className="flex items-center gap-2">
                    <Timer className={`w-4 h-4 ${holdSecondsLeft < 60 ? "text-red-400" : "text-amber-400"}`} />
                    <p className={`text-xs font-medium ${holdSecondsLeft < 60 ? "text-red-300" : "text-amber-300"}`}>
                      {isFr
                        ? "Créneau réservé pour vous"
                        : "Slot reserved for you"}
                    </p>
                  </div>
                  <p className={`text-sm font-mono font-bold tabular-nums ${holdSecondsLeft < 60 ? "text-red-300" : "text-amber-300"}`}>
                    {formatHoldClock(holdSecondsLeft)}
                  </p>
                </div>
              )}
              <p className="text-zinc-400 text-sm mb-2">{isFr ? "Vos informations:" : "Your information:"}</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">{isFr ? "Prénom" : "First Name"} *</label>
                  <input
                    type="text"
                    value={customerInfo.first_name}
                    onChange={e => setCustomerInfo(p => ({ ...p, first_name: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                    data-testid="input-first-name"
                  />
                </div>
                <div>
                  <label className="text-zinc-400 text-xs mb-1 block">{isFr ? "Nom" : "Last Name"} *</label>
                  <input
                    type="text"
                    value={customerInfo.last_name}
                    onChange={e => setCustomerInfo(p => ({ ...p, last_name: e.target.value }))}
                    className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                    data-testid="input-last-name"
                  />
                </div>
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{isFr ? "Courriel" : "Email"} *</label>
                <input
                  type="email"
                  value={customerInfo.email}
                  onChange={e => setCustomerInfo(p => ({ ...p, email: e.target.value }))}
                  onBlur={e => {
                    const em = e.target.value.trim();
                    if (em && em.includes("@") && !autoFilled) {
                      fetch(`${API}/client/lookup?email=${encodeURIComponent(em)}`)
                        .then(r => r.json())
                        .then(data => {
                          if (data.found && data.client) {
                            setCustomerInfo(p => ({
                              ...p,
                              first_name: p.first_name || data.client.first_name || "",
                              last_name: p.last_name || data.client.last_name || "",
                              phone: p.phone || data.client.phone || ""
                            }));
                            setAutoFilled(true);
                          }
                        })
                        .catch(() => {});
                    }
                  }}
                  className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none"
                  data-testid="input-email"
                />
                {autoFilled && <p className="text-amber-500/60 text-xs mt-1">{isFr ? "Rempli automatiquement" : "Auto-filled from your profile"}</p>}
              </div>
              <div>
                <label className="text-zinc-400 text-xs mb-1 block">{isFr ? "Téléphone" : "Phone"} *</label>
                <input
                  type="tel"
                  value={customerInfo.phone}
                  onChange={e => setCustomerInfo(p => ({ ...p, phone: e.target.value }))}
                  onBlur={e => {
                    const d = (e.target.value || "").replace(/\D/g, "");
                    if (d.length >= 10 && !autoFilled && !(customerInfo.first_name && customerInfo.email)) {
                      fetch(`${API}/client/lookup?phone=${encodeURIComponent(d.slice(-10))}`)
                        .then(r => r.json())
                        .then(data => {
                          if (data.found && data.client) {
                            setCustomerInfo(p => ({
                              ...p,
                              first_name: p.first_name || data.client.first_name || "",
                              last_name: p.last_name || data.client.last_name || "",
                              email: p.email || data.client.email || "",
                            }));
                            setAutoFilled(true);
                          }
                        })
                        .catch(() => {});
                    }
                  }}
                  placeholder={isFr ? "Votre numéro de cellulaire" : "Your mobile number"}
                  className={`w-full bg-zinc-800 border rounded-lg px-3 py-2 text-white text-sm focus:border-amber-500 focus:outline-none ${customerInfo.phone?.trim() && !phoneDigitsOk(customerInfo.phone) ? "border-red-500/70" : "border-zinc-700"}`}
                  data-testid="input-phone"
                />
                {customerInfo.phone?.trim() && !phoneDigitsOk(customerInfo.phone) && (
                  <p className="text-red-400 text-xs mt-1" data-testid="phone-invalid-hint">
                    {isFr ? "Entrez un numéro à 10 chiffres, ex. 613-850-0805" : "Please enter a 10-digit number, e.g. 613-850-0805"}
                  </p>
                )}
              </div>

              {/* Nov 2 2026 · tell the client exactly what still blocks the Review button */}
              {!canProceed() && (() => {
                const missing = [];
                if (!customerInfo.first_name?.trim()) missing.push(isFr ? "prénom" : "first name");
                if (!customerInfo.last_name?.trim()) missing.push(isFr ? "nom" : "last name");
                if (!customerInfo.email?.trim()) missing.push(isFr ? "courriel" : "email");
                if (!phoneDigitsOk(customerInfo.phone)) missing.push(isFr ? "numéro de téléphone à 10 chiffres" : "10-digit phone number");
                return missing.length ? (
                  <p className="text-amber-300/90 text-xs -mt-1" data-testid="info-missing-hint">
                    {isFr ? "Pour continuer, ajoutez : " : "To continue, add your "}{missing.join(isFr ? ", " : ", ")}.
                  </p>
                ) : null;
              })()}

              {/* Pre-care reminder opt-in */}
              <label
                className="mt-1 flex items-start gap-3 p-3 rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-500/[0.07] via-amber-500/[0.04] to-transparent hover:border-amber-500/50 hover:bg-amber-500/[0.09] cursor-pointer transition-all group"
                data-testid="reminder-optin-label"
              >
                <input
                  type="checkbox"
                  checked={customerInfo.send_reminders !== false}
                  onChange={e => setCustomerInfo(p => ({ ...p, send_reminders: e.target.checked }))}
                  className="mt-0.5 w-4 h-4 rounded border-amber-600 text-amber-500 focus:ring-amber-500 bg-zinc-800 accent-amber-500 flex-shrink-0"
                  data-testid="reminder-optin-checkbox"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-amber-200 text-sm font-medium group-hover:text-amber-100 transition-colors leading-tight">
                    {isFr
                      ? "Envoyez-moi un rappel de rendez-vous avec des conseils de préparation"
                      : "Send me an appointment reminder with pre-care tips"}
                  </p>
                  <p className="text-zinc-400 text-[11px] mt-1 leading-snug">
                    {isFr
                      ? "Un rappel par courriel 7 jours et 48 heures avant — comprenant des conseils de préparation spécifiques à votre traitement."
                      : "Friendly email reminder 7 days and 48 hours before — includes a pre-care checklist tailored to your treatment."}
                  </p>
                </div>
              </label>

              {/* Professional Referral Code — always visible, elegant card */}
              <div
                className="mt-2 rounded-xl border bg-gradient-to-br from-amber-950/30 to-zinc-900/50"
                style={{ borderColor: proReferralInfo ? "rgba(16,185,129,0.4)" : "rgba(217,167,99,0.30)" }}
                data-testid="pro-referral-section"
              >
                <div className="px-4 py-3 border-b border-amber-900/20 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 10.5V19a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-8.5"/><polyline points="3.5 7 12 12 20.5 7"/><polyline points="12 12 12 21"/></svg>
                    <p className="text-amber-200 text-sm font-semibold">
                      {isFr ? "Code de Référence Pro" : "Pro Referral Code"}
                    </p>
                  </div>
                  <span className="text-amber-400 text-[10px] uppercase tracking-wider font-bold">
                    {isFr ? "25 $ de rabais" : "$25 OFF"}
                  </span>
                </div>
                <div className="px-4 py-3">
                  <p className="text-zinc-400 text-[11px] mb-3 leading-snug">
                    {isFr
                      ? "Avez-vous été référé par un professionnel? Entrez leur code pour recevoir 25 $ de rabais sur ce rendez-vous."
                      : "Were you referred by a beauty or wellness professional? Enter their partner code to receive $25 off this appointment."}
                  </p>
                  {proReferralInfo ? (
                    <div className="flex items-center justify-between gap-3 p-3 rounded-lg bg-emerald-500/10 border border-emerald-500/30" data-testid="pro-referral-applied">
                      <div className="min-w-0">
                        <p className="text-emerald-300 text-sm font-semibold">
                          ✓ {isFr ? "Code appliqué" : "Code applied"} · <span className="font-mono">{proReferralInfo.code}</span>
                        </p>
                        {proReferralInfo.is_client_referral ? (
                          <p className="text-emerald-400/90 text-xs mt-1 leading-snug">
                            {proReferralInfo.success_message ||
                              (isFr
                                ? `✨ Super ! Vous ET ${proReferralInfo.referrer_display} recevrez 50 $ en points Crown après votre premier traitement.`
                                : `✨ Great! You AND ${proReferralInfo.referrer_display} will each get $${proReferralInfo.client_discount || 50} in Crown points after your first treatment.`)}
                          </p>
                        ) : (
                          <p className="text-emerald-400/80 text-xs mt-0.5">
                            {isFr ? `${proReferralInfo.client_discount || 25} $ de rabais grâce à ` : `$${proReferralInfo.client_discount || 25} off thanks to `}
                            <span className="font-medium">{proReferralInfo.referrer_display}</span>
                          </p>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => { setProReferralInfo(null); setProReferralCode(""); setProReferralError(null); }}
                        className="text-emerald-300 hover:text-emerald-100 text-xs underline flex-shrink-0"
                        data-testid="pro-referral-remove"
                      >
                        {isFr ? "Retirer" : "Remove"}
                      </button>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-stretch gap-2">
                        <input
                          type="text"
                          value={proReferralCode}
                          onChange={(e) => { setProReferralCode(e.target.value.toUpperCase()); setProReferralError(null); }}
                          placeholder={isFr ? "KL-AB7X  ou  KATE" : "KL-AB7X  or  KATE"}
                          className="flex-1 bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-white text-base font-mono uppercase tracking-wider focus:border-amber-500 focus:outline-none placeholder:text-zinc-600"
                          data-testid="pro-referral-input"
                        />
                        <button
                          type="button"
                          disabled={!proReferralCode.trim() || proReferralValidating}
                          onClick={async () => {
                            setProReferralValidating(true);
                            setProReferralError(null);
                            try {
                              const res = await axios.get(`${API}/referrals/professional/validate-code`, {
                                params: { code: proReferralCode.trim() },
                              });
                              if (res.data?.ok) {
                                setProReferralInfo({
                                  code: res.data.code,
                                  referrer_display: res.data.referrer_display,
                                  client_discount: res.data.client_discount,
                                });
                                setProReferralValidating(false);
                                return;
                              }
                            } catch (err) {
                              // Professional code failed — fall through to
                              // the client-to-client referral endpoint
                              // (Feb 2026 Karine simplification: one input
                              // handles both KL-XXXX pro codes AND
                              // friend-first-name codes like KATE, ISABEL).
                            }
                            try {
                              const codeClean = proReferralCode.trim().toUpperCase();
                              const r2 = await axios.get(`${API}/referral/validate/${encodeURIComponent(codeClean)}`);
                              if (r2.data?.valid) {
                                setProReferralInfo({
                                  code: r2.data.code,
                                  referrer_display: r2.data.referrer_first_name,
                                  client_discount: r2.data.credit_dollars,
                                  is_client_referral: true,
                                  success_message: isFr ? r2.data.message_fr : r2.data.message_en,
                                });
                              } else {
                                setProReferralError(isFr ? "Code non reconnu" : "Code not recognized");
                              }
                            } catch (err2) {
                              setProReferralError(isFr ? "Erreur de validation" : "Validation error");
                            }
                            setProReferralValidating(false);
                          }}
                          className="px-5 py-2.5 rounded-lg bg-amber-500 hover:bg-amber-400 disabled:opacity-50 disabled:hover:bg-amber-500 text-black text-sm font-semibold transition-colors whitespace-nowrap"
                          data-testid="pro-referral-apply"
                        >
                          {proReferralValidating ? "…" : (isFr ? "Appliquer" : "Apply")}
                        </button>
                      </div>
                      {proReferralError && (
                        <p className="text-red-400 text-xs mt-2" data-testid="pro-referral-error">{proReferralError}</p>
                      )}
                      <p className="text-zinc-500 text-[10px] mt-2 italic">
                        {isFr
                          ? "Code pro (KL-XXXX) ou prénom d'une amie qui vous a recommandé"
                          : "Pro code (KL-XXXX) or first name of the friend who referred you"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* STEP 5: Review & Confirm */}
          {step === 5 && (
            <div className="space-y-4" data-testid="step-review">
              <p className="text-zinc-200 text-base mb-2">{isFr ? "Vérifiez que tout est exact, puis appuyez sur le bouton doré pour réserver :" : "Check everything is right, then tap the gold button to book:"}</p>

              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg overflow-hidden">
                {/* ── BOOKING WITH · Provider (Mar 4 2026 · Karine ask) ─────
                    Show the provider up front so clients confirm they're
                    booking with the right person and know what services
                    that provider actually offers before they hit Confirm. */}
                <ProviderReviewCard providerSlug={displayProvider} isFr={isFr} />

                {/* Location */}
                <div className="flex items-center gap-3 p-4 border-b border-zinc-700/50">
                  <div className="w-10 h-10 bg-amber-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs">{isFr ? "Clinique" : "Clinic"}</p>
                    <p className="text-white font-medium">{selectedLocation?.name}</p>
                    <p className="text-zinc-400 text-xs">{LOCATION_ADDRESSES[selectedLocation?.id]}</p>
                  </div>
                  <button onClick={() => setStep(0)} className="ml-auto text-amber-400 hover:text-amber-300 text-sm font-semibold underline px-2 py-2" data-testid="review-edit-location">
                    {isFr ? "Modifier" : "Edit"}
                  </button>
                </div>

                {/* Treatment(s) — supports stacked multi-treatment bookings */}
                <div className="flex items-start gap-3 p-4 border-b border-zinc-700/50">
                  <div className="w-10 h-10 bg-amber-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Sparkles className="w-5 h-5 text-amber-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-zinc-500 text-xs">
                      {selectedServices.length > 1
                        ? (isFr ? `Traitements (${selectedServices.length})` : `Treatments (${selectedServices.length})`)
                        : (isFr ? "Traitement" : "Treatment")}
                    </p>
                    <ul className="mt-0.5 space-y-0.5">
                      {selectedServices.map((s) => (
                        <li key={s.variation_id} className="text-white text-sm font-medium leading-tight">
                          • {s.package_label || s.name}
                          <span className="text-zinc-500 text-xs font-normal ml-2">
                            ({formatDuration(s.duration_minutes)}{s.price_label || s.price ? ` · ${s.price_label || s.price}` : ""})
                          </span>
                        </li>
                      ))}
                    </ul>
                    {selectedServices.length > 1 && (
                      <p className="text-amber-400 text-xs font-semibold mt-1.5">
                        {isFr ? "Durée totale" : "Total"}: {formatDuration(totalDurationMinutes)}
                        {totalPriceCents > 0 ? ` · $${(totalPriceCents / 100).toFixed(2)}` : ""}
                      </p>
                    )}
                  </div>
                  <button onClick={() => setStep(1)} className="text-amber-400 hover:text-amber-300 text-sm font-semibold underline px-2 py-2" data-testid="review-edit-service">
                    {isFr ? "Modifier" : "Edit"}
                  </button>
                </div>

                {/* Date & Time */}
                <div className="flex items-center gap-3 p-4 border-b border-zinc-700/50">
                  <div className="w-10 h-10 bg-amber-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <Calendar className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs">{isFr ? "Date & Heure" : "Date & Time"}</p>
                    <p className="text-white font-medium">{selectedDate?.formatted}</p>
                    <p className="text-amber-400 text-sm font-medium">{selectedTime && formatTime(selectedTime)}</p>
                  </div>
                  <button onClick={() => setStep(2)} className="ml-auto text-amber-400 hover:text-amber-300 text-sm font-semibold underline px-2 py-2" data-testid="review-edit-date">
                    {isFr ? "Modifier" : "Edit"}
                  </button>
                </div>

                {/* Iter 449 · Sep 21 2026 · Karine ask — "Add another
                    treatment?" nudge inserted right before the customer
                    info so clients can stack a facial add-on / lash lift
                    / combo without abandoning the flow. Reuses the
                    existing selectedServices array (multi-select was
                    already supported end-to-end). */}
                <button
                  type="button"
                  onClick={() => setStep(1)}
                  className="w-full flex items-center justify-center gap-2 p-3.5 border-b border-zinc-700/50 bg-emerald-500/5 hover:bg-emerald-500/10 transition text-emerald-300 hover:text-emerald-200 text-sm font-semibold group"
                  data-testid="review-add-another-treatment"
                >
                  <span className="w-6 h-6 rounded-full border border-emerald-400/50 flex items-center justify-center text-lg leading-none group-hover:border-emerald-300">+</span>
                  {isFr
                    ? "Ajouter un autre soin avant de confirmer"
                    : "Would you like to add another treatment?"}
                </button>

                {/* Customer Info */}
                <div className="flex items-center gap-3 p-4">
                  <div className="w-10 h-10 bg-amber-500/10 rounded-full flex items-center justify-center flex-shrink-0">
                    <User className="w-5 h-5 text-amber-400" />
                  </div>
                  <div>
                    <p className="text-zinc-500 text-xs">{isFr ? "Client" : "Client"}</p>
                    <p className="text-white font-medium">{customerInfo.first_name} {customerInfo.last_name}</p>
                    <p className="text-zinc-400 text-xs">{customerInfo.email}</p>
                    <p className="text-zinc-400 text-xs">{customerInfo.phone}</p>
                  </div>
                  <button onClick={() => setStep(4)} className="ml-auto text-amber-400 hover:text-amber-300 text-sm font-semibold underline px-2 py-2" data-testid="review-edit-info">
                    {isFr ? "Modifier" : "Edit"}
                  </button>
                </div>

                {/* Loyalty credit auto-apply row — appears when the signed-in
                    client has an active 15-min point-redemption hold. */}
                {pendingHold && (
                  <div
                    className="flex items-center gap-3 p-4 border-t border-zinc-700/50 bg-emerald-900/15"
                    data-testid="review-loyalty-credit"
                  >
                    <div className="w-10 h-10 bg-emerald-500/15 rounded-full flex items-center justify-center flex-shrink-0">
                      <Gift className="w-5 h-5 text-emerald-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-emerald-300/80 text-xs font-semibold uppercase tracking-wider">
                        {isFr ? "Crédit fidélité appliqué" : "Loyalty credit applied"}
                      </p>
                      <p className="text-emerald-200 font-medium text-sm">
                        −${pendingHold.dollar_value}
                        <span className="text-emerald-400/70 font-mono text-xs ml-2">
                          {pendingHold.credit_code}
                        </span>
                      </p>
                      <p className="text-emerald-500/70 text-[11px] mt-0.5">
                        {isFr
                          ? `Vos ${pendingHold.points} points seront utilisés à la caisse`
                          : `Your ${pendingHold.points} points will be applied at checkout`}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Crown Points reminder — only renders for signed-in clients
                  with a redeemable balance. Selection is local until they
                  tap Confirm, at which point we call /api/client/redeem
                  and inject the credit code into the booking notes. */}
              {!pendingHold && (
                <CrownPointsBanner
                  value={selectedRedemption}
                  onChange={setSelectedRedemption}
                />
              )}

              {/* "Pairs well with…" add-on recommendations — uses the chosen
                  treatment slug to fetch 1-3 add-ons whose compatible_with
                  list matches. Selections drop into the booking notes so
                  reception rings them up at checkout. */}
              {primaryService && (
                <AddonRecommendations
                  service={primaryService.name || primaryService.variation_id || ""}
                  selected={selectedRecAddons}
                  onChange={setSelectedRecAddons}
                />
              )}
              
              {/* ── #5a Facial Lymphatic Drain spotlight upsell ──────────
                  Specifically promotes the $45 lymphatic drain add-on
                  (vs. $75 standalone) any time it's eligible. Placed
                  ABOVE the general suggested add-ons list because (a)
                  it pairs with literally any treatment, (b) lymphatic
                  drain is a high-impulse last-minute "look snatched
                  before this event" upsell. */}
              {(() => {
                const lymph = suggestedAddons.find((a) => a.slug === "facial-lymphatic-drain-addon");
                if (!lymph) return null;
                const state = addonsAdded[lymph.slug];
                if (state === "sent") return null;  // hide once added
                return (
                  <div
                    className="relative overflow-hidden bg-gradient-to-br from-sky-500/15 via-cyan-500/10 to-sky-500/15 border-2 border-sky-400/40 rounded-xl p-4"
                    data-testid="booking-lymphatic-spotlight"
                  >
                    <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-sky-400 text-zinc-900 text-[9px] font-bold uppercase tracking-wider">
                      {isFr ? "Économisez 30 $" : "Save $30"}
                    </div>
                    <div className="flex items-start gap-3 pr-16">
                      <div className="w-10 h-10 rounded-full bg-sky-400/20 flex items-center justify-center flex-shrink-0">
                        <Droplets className="w-5 h-5 text-sky-300" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sky-300 text-[10px] font-bold uppercase tracking-[2px]">
                          {isFr ? "Combo populaire" : "Popular pairing"}
                        </p>
                        <p className="text-white text-sm font-semibold leading-snug mt-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>
                          {isFr
                            ? "Drainage Lymphatique Facial — seulement 45 $ de plus"
                            : "Add Facial Lymphatic Drain — only $45 extra"}
                        </p>
                        <p className="text-zinc-300 text-[12px] mt-1.5 leading-snug">
                          {isFr
                            ? "Sculptez la mâchoire et les pommettes, réduisez les gonflements et amplifiez vos résultats. Habituellement 75 $ seul — 45 $ avec votre traitement."
                            : "Sculpts your jawline & cheekbones, reduces puffiness and amplifies your results. Normally $75 standalone — just $45 when paired with your treatment."}
                        </p>
                        <button
                          type="button"
                          onClick={() => addAddonToVisit(lymph)}
                          disabled={state === "saving"}
                          className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[12px] font-bold uppercase tracking-wider bg-sky-400 hover:bg-sky-300 disabled:opacity-50 text-zinc-900 transition-colors"
                          data-testid="booking-lymphatic-add-btn"
                        >
                          {state === "saving"
                            ? (isFr ? "Ajout..." : "Adding…")
                            : (<><Plus className="w-3.5 h-3.5" /> {isFr ? "Ajouter pour 45 $" : "Add for $45"}</>)}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })()}

              {/* ── #5b Add-On Suggestions ───────────────────────────────
                  Lazy-fetched + filtered to add-ons compatible with the
                  chosen services. Tapping an item posts an inquiry to
                  Karine/Tanya's admin inbox so they can text the client
                  to confirm + slot it in physically during the visit.
                  Hidden when no suggestions or when the catalog hasn't
                  loaded yet. */}
              {suggestedAddons.length > 0 && (
                <div
                  className="bg-gradient-to-br from-emerald-500/10 via-teal-500/5 to-emerald-500/10 border border-emerald-500/30 rounded-xl p-4"
                  data-testid="booking-addon-upsell"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <Sparkles className="w-4 h-4 text-emerald-400" />
                    <p className="text-emerald-300 text-[10px] font-bold uppercase tracking-[2px]">
                      {isFr
                        ? (isAestheticianVisit ? "Tous les ajouts esthétiques" : "Améliorez votre visite")
                        : (isAestheticianVisit ? "All aesthetician add-ons" : "Enhance your visit")}
                    </p>
                  </div>
                  <p className="text-zinc-300 text-xs mb-3 leading-snug">
                    {isFr
                      ? (isAestheticianVisit
                          ? `Choisissez un ou plusieurs ajouts de notre menu complet (${suggestedAddons.length}). Votre prestataire les ajoutera sur place.`
                          : "Suggestions compatibles avec votre traitement. Votre prestataire vous textera pour les ajouter sur place.")
                      : (isAestheticianVisit
                          ? `Pick any enhancement from the full menu of ${suggestedAddons.length}. Your provider will slot it in during your visit.`
                          : "Pairs perfectly with what you're booking. Your provider will text you to slot them in during your visit.")}
                  </p>
                  {/* Ottawa-only add-on notice — Sherralee bug fix ---------- */}
                  {pendingOttawaOnlyAddon && (
                    <div
                      className="mb-3 p-3 rounded-lg bg-amber-500/10 border border-amber-500/30"
                      data-testid="booking-ottawa-only-addon-notice"
                    >
                      <p className="text-amber-100 text-xs leading-relaxed">
                        {isFr
                          ? "Vous avez demandé notre drainage lymphatique du visage — il est offert à notre clinique d'Ottawa uniquement. Réservez ce rendez-vous ici et Karine vous textera pour planifier séparément l'ajout à 45 $ à Ottawa."
                          : "You asked for the Facial Lymphatic Drain — it's offered at our Ottawa clinic only. Book this appointment here and Karine will text you to schedule the $45 add-on visit in Ottawa separately."}
                      </p>
                    </div>
                  )}
                  {/* Oct 7 2026 · Karine ask — scrollable dropdown-feel
                      list that fits the FULL add-on menu on one screen
                      (max-h capped at 50vh so it never pushes Sign off
                      the page). */}
                  <div className="space-y-2 max-h-[50vh] overflow-y-auto pr-1" data-testid="booking-addon-list">
                    {suggestedAddons.map((a) => {
                      const state = addonsAdded[a.slug];
                      return (
                        <div
                          key={a.slug}
                          className="flex items-center gap-3 bg-zinc-900/40 rounded-lg p-2.5 border border-emerald-500/20"
                          data-testid={`booking-upsell-${a.slug}`}
                        >
                          <div className="flex-1 min-w-0">
                            <p className="text-white text-sm font-medium leading-tight truncate">{a.name}</p>
                            <p className="text-zinc-400 text-[11px] mt-0.5">
                              {a.duration_min} min · ${a.price_from}{a.price_to && a.price_to > a.price_from ? `–$${a.price_to}` : ""}
                            </p>
                          </div>
                          {state === "sent" ? (
                            <span className="text-emerald-400 text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                              <Check className="w-3.5 h-3.5" /> {isFr ? "Ajouté" : "Added"}
                            </span>
                          ) : (
                            <button
                              type="button"
                              onClick={() => addAddonToVisit(a)}
                              disabled={state === "saving"}
                              className="px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-emerald-500 hover:bg-emerald-400 disabled:opacity-50 text-zinc-900 transition-colors flex-shrink-0"
                              data-testid={`booking-upsell-add-${a.slug}`}
                            >
                              {state === "saving"
                                ? (isFr ? "..." : "...")
                                : (isFr ? "+ Ajouter" : "+ Add")}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <p className="text-zinc-500 text-[10px] mt-2.5 leading-snug">
                    {isFr
                      ? "Aucun engagement — votre prestataire confirmera avant l'application."
                      : "No commitment — your provider will confirm before adding."}
                  </p>
                </div>
              )}

              {/* #6 Crown Reserve upsell — appears for high-frequency / high-spend clients */}
              {clientProfile?.eligible_crown_reserve && (
                <div className="bg-gradient-to-br from-amber-500/15 via-yellow-500/10 to-amber-500/15 border border-amber-500/40 rounded-xl p-4" data-testid="crown-reserve-upsell">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0">
                      <Crown className="w-5 h-5 text-zinc-900" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-amber-300 text-[10px] font-bold uppercase tracking-[2px]">
                        {isFr ? "Vous êtes éligible" : "You're eligible"}
                      </p>
                      <p className="text-white text-sm font-semibold leading-snug mt-0.5" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {isFr
                          ? "Rejoignez Crown Reserve et économisez chaque mois"
                          : "Join Crown Reserve and save every month"}
                      </p>
                      <p className="text-zinc-300 text-xs mt-1">
                        {isFr
                          ? `Vous avez ${clientProfile.visits_90d} visites${clientProfile.total_spend_90d > 0 ? ` ($${clientProfile.total_spend_90d})` : ""} ces 90 derniers jours — un plan mensuel vous fera économiser jusqu'à 30%.`
                          : `You've had ${clientProfile.visits_90d} visits${clientProfile.total_spend_90d > 0 ? ` ($${clientProfile.total_spend_90d})` : ""} in the last 90 days — a monthly plan would save you up to 30%.`}
                      </p>
                      <button
                        type="button"
                        onClick={() => {
                          window.open("/crown-reserve", "_blank", "noopener");
                        }}
                        className="mt-2.5 text-amber-300 text-xs font-semibold underline hover:text-amber-200"
                        data-testid="crown-reserve-cta"
                      >
                        {isFr ? "Voir les plans Crown Reserve →" : "Explore Crown Reserve plans →"}
                      </button>
                    </div>
                  </div>
                </div>
              )}

              <p className="text-zinc-500 text-xs text-center">
                {isFr ? "En confirmant, votre rendez-vous sera réservé dans notre système." : "By confirming, your appointment will be booked in our system."}
              </p>
            </div>
          )}

          {/* STEP 6: Confirmation */}
          {step === 6 && booking && (
            <div className="text-center py-6" data-testid="step-confirm">
              {/* Show green check for confirmed, yellow clock for pending */}
              <div className={`w-16 h-16 ${booking.success ? 'bg-green-500/20' : 'bg-amber-500/20'} rounded-full flex items-center justify-center mx-auto mb-4`}>
                {booking.success 
                  ? <Check className="w-8 h-8 text-green-400" />
                  : <Clock className="w-8 h-8 text-amber-400" />
                }
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${booking.success ? 'text-white' : 'text-amber-400'}`} style={{ fontFamily: "'Playfair Display', serif" }}>
                {booking.success
                  ? (isFr ? "Rendez-vous confirmé!" : "Appointment Confirmed!")
                  : (isFr ? "Demande reçue" : "Request Received")}
              </h3>
              <p className="text-zinc-400 text-sm mb-4">{booking.message}</p>
              {!booking.success && (
                <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 mb-4 max-w-sm mx-auto">
                  <p className="text-amber-400 text-sm">
                    {isFr 
                      ? "Ce créneau nécessite une confirmation. Notre équipe vous contactera sous peu." 
                      : "This time slot requires confirmation. Our team will contact you shortly."}
                  </p>
                </div>
              )}
              {booking.confirmation_number && booking.success && (
                <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-4 inline-block mb-4">
                  <p className="text-zinc-500 text-xs mb-1">{isFr ? "Numéro de confirmation" : "Confirmation #"}</p>
                  <p className="text-amber-400 text-2xl font-mono font-bold tracking-wider" data-testid="confirmation-number">
                    {booking.confirmation_number}
                  </p>
                </div>
              )}

              {/* Éminence add-ons echo — Karine ask (Feb 28): show the
                  client which upgrades they picked so nothing feels
                  lost between the menu page and the confirmation. */}
              {addOnsInBooking.length > 0 && (
                <div className="max-w-sm mx-auto mb-4 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-left" data-testid="booking-addons-echo">
                  <p className="text-amber-300 text-[11px] uppercase tracking-widest font-bold mb-2">
                    {isFr ? "Suppléments inclus" : "Add-ons included"}
                  </p>
                  <ul className="space-y-1.5">
                    {addOnsInBooking.map((a) => (
                      <li key={a.key} className="flex items-center gap-2 text-[12.5px] text-zinc-200">
                        <span className="w-5 h-5 rounded-full bg-amber-500/25 text-amber-300 text-[9px] font-bold flex items-center justify-center shrink-0">{a.number}</span>
                        <span className="flex-1">{isFr ? a.fr : a.en}</span>
                        <span className="text-amber-300 font-bold tabular-nums">${a.price}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-2 pt-2 border-t border-amber-500/25 flex items-center justify-between text-[12px]">
                    <span className="text-zinc-400 uppercase tracking-widest text-[10px] font-bold">{isFr ? "Total suppléments" : "Add-on total"}</span>
                    <span className="text-amber-300 font-extrabold tabular-nums">+${addOnsTotal}</span>
                  </div>
                  <p className="mt-2 text-[10.5px] text-zinc-400 leading-snug">
                    {isFr
                      ? "Votre esthéticienne les ajoutera à votre soin. Vous les verrez aussi dans votre courriel de confirmation."
                      : "Your aesthetician will stack these onto your treatment. You'll also see them in your confirmation email."}
                  </p>
                </div>
              )}

              {/* Claim result — only shown when the client came from an
                  "earlier slot opened" email and just successfully rebooked. */}
              {claimWatcherId && booking.success && claimResult && (
                <div className="max-w-sm mx-auto mb-4" data-testid="claim-result-panel">
                  {claimResult.error ? (
                    <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-left">
                      <p className="text-amber-300 text-xs font-medium mb-1">
                        {isFr ? "Action manuelle requise" : "Heads up"}
                      </p>
                      <p className="text-zinc-300 text-[11px] leading-relaxed">
                        {isFr
                          ? "Votre nouveau rendez-vous est confirmé, mais nous n'avons pas pu annuler automatiquement l'ancien. Notre équipe le fera sous peu."
                          : "Your new appointment is confirmed, but we couldn't auto-cancel the old one. Our team will handle it shortly."}
                      </p>
                    </div>
                  ) : (
                    <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 text-left" data-testid="claim-result-success">
                      <div className="flex items-center gap-1.5 mb-1">
                        <Check className="w-3.5 h-3.5 text-emerald-300" />
                        <p className="text-emerald-300 text-xs font-semibold">
                          {claimResult.already_claimed
                            ? (isFr ? "Déjà réclamé" : "Already claimed")
                            : (isFr ? "Tout est réglé" : "All sorted")}
                        </p>
                      </div>
                      <p className="text-zinc-300 text-[11px] leading-relaxed">
                        {claimResult.cancelled_booking_id
                          ? (isFr
                              ? "Votre ancien rendez-vous a été annulé automatiquement."
                              : "Your previous appointment has been auto-cancelled.")
                          : (isFr
                              ? "Votre nouveau rendez-vous est confirmé."
                              : "Your new appointment is confirmed.")}
                        {!!claimResult.points_awarded && (
                          <>
                            {" "}
                            <span className="text-amber-300 font-semibold">
                              {isFr
                                ? `+${claimResult.points_awarded} Crown Points 👑`
                                : `+${claimResult.points_awarded} Crown Points 👑`}
                            </span>
                          </>
                        )}
                      </p>
                    </div>
                  )}
                </div>
              )}
              <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-4 text-left max-w-sm mx-auto">
                <div className="text-sm text-zinc-300 space-y-1.5">
                  <p><MapPin className="w-3.5 h-3.5 inline mr-1.5 text-zinc-500" />{selectedLocation?.name}</p>
                  {selectedServices.map((s) => (
                    <p key={s.variation_id}>
                      <Sparkles className="w-3.5 h-3.5 inline mr-1.5 text-zinc-500" />
                      {s.name}
                    </p>
                  ))}
                  <p><Calendar className="w-3.5 h-3.5 inline mr-1.5 text-zinc-500" />{selectedDate?.formatted}</p>
                  <p><Clock className="w-3.5 h-3.5 inline mr-1.5 text-zinc-500" />{selectedTime && formatTime(selectedTime)} <span className="text-zinc-500">({formatDuration(totalDurationMinutes)})</span></p>
                  <p><User className="w-3.5 h-3.5 inline mr-1.5 text-zinc-500" />{customerInfo.first_name} {customerInfo.last_name}</p>
                </div>
              </div>

              {/* Earlier-opening alert opt-in — one-tap "Yes" CTA. Only shown
                  when the booking succeeded AND we have everything the
                  fast-track-watcher endpoint needs. Doing nothing is the
                  implicit "no" — the card just sits there politely.
                  Skip this card if the booking was itself a claim — the client
                  just took an earlier slot, no need to ask again. */}
              {!claimWatcherId && booking.success && selectedDate?.date && selectedLocation?.id && customerInfo.email && (
                <div
                  className="mt-4 mx-auto max-w-sm bg-gradient-to-br from-amber-950/40 to-zinc-900 border border-amber-700/40 rounded-xl p-4 text-left"
                  data-testid="earlier-alert-card"
                >
                  <div className="flex items-start gap-2.5 mb-2.5">
                    <div className="w-8 h-8 rounded-full bg-amber-500/15 border border-amber-500/40 flex items-center justify-center shrink-0">
                      <Bell className="w-4 h-4 text-amber-300" />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <h4 className="text-amber-200 text-sm font-semibold leading-snug">
                          {isFr
                            ? "Vous voulez un rendez-vous plus tôt ?"
                            : "Want to come in sooner?"}
                        </h4>
                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full bg-amber-500/15 border border-amber-500/40 text-amber-300 text-[10px] font-bold tracking-wider">
                          <Crown className="w-2.5 h-2.5" /> +25 PTS
                        </span>
                      </div>
                      <p className="text-zinc-400 text-[11px] leading-relaxed mt-0.5">
                        {isFr
                          ? `Si une annulation libère un créneau plus tôt à ${selectedLocation?.name || "cette clinique"}, nous vous le ferons savoir par courriel ${customerInfo.phone ? "et texto" : ""}. Si vous prenez la nouvelle date, nous annulons l'ancienne automatiquement et vous gagnez 25 Crown Points 👑.`
                          : `If a cancellation opens up an earlier slot at ${selectedLocation?.name || "this clinic"}, we'll ping you by email${customerInfo.phone ? " and text" : ""}. Claim it and we'll auto-cancel your old slot — plus you earn 25 Crown Points 👑.`}
                      </p>
                    </div>
                  </div>

                  {earlierAlertSaved ? (
                    <div
                      className="flex items-center gap-2 text-emerald-300 text-xs font-medium px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30"
                      data-testid="earlier-alert-saved"
                    >
                      <Check className="w-3.5 h-3.5" />
                      {isFr
                        ? "Surveillance activée — votre rendez-vous est gardé"
                        : "You're on the list — your booked time stays put"}
                    </div>
                  ) : (
                    <Button
                      onClick={async () => {
                        if (earlierAlertSaving) return;
                        setEarlierAlertSaving(true);
                        setEarlierAlertError(null);
                        try {
                          await axios.post(
                            `${API}/booking/fast-track-watch`,
                            {
                              client_email: customerInfo.email.trim(),
                              client_name: `${customerInfo.first_name || ""} ${customerInfo.last_name || ""}`.trim() || null,
                              client_phone: customerInfo.phone?.trim() || null,
                              service_duration_minutes: totalDurationMinutes || 15,
                              current_earliest_date: selectedDate.date,
                              // Only watch the SAME clinic the client just booked — they
                              // chose it for a reason.
                              location_ids: [selectedLocation.id],
                              language: isFr ? "fr" : "en",
                              // Pass the Square booking id so the claim endpoint can
                              // auto-cancel it when the client takes an earlier slot.
                              original_square_booking_id: booking.booking_id || null,
                            },
                            { timeout: 15000 }
                          );
                          setEarlierAlertSaved(true);
                          toast.success(
                            isFr
                              ? "On vous préviendra si plus tôt s'ouvre"
                              : "We'll notify you if anything opens up earlier"
                          );
                        } catch (e) {
                          setEarlierAlertError(
                            isFr ? "Échec — réessayez" : "Couldn't save — try again"
                          );
                          toast.error(
                            isFr ? "Échec — réessayez" : "Couldn't save — try again"
                          );
                        } finally {
                          setEarlierAlertSaving(false);
                        }
                      }}
                      disabled={earlierAlertSaving}
                      className="w-full bg-amber-600 hover:bg-amber-500 text-white text-sm font-medium"
                      data-testid="earlier-alert-yes-btn"
                    >
                      {earlierAlertSaving ? (
                        <>
                          <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                          {isFr ? "Activation…" : "Saving…"}
                        </>
                      ) : (
                        <>
                          <Bell className="w-3.5 h-3.5 mr-1.5" />
                          {isFr ? "Oui, prévenez-moi · +25 pts" : "Yes, notify me · +25 pts"}
                        </>
                      )}
                    </Button>
                  )}
                  {earlierAlertError && !earlierAlertSaved && (
                    <p
                      className="text-rose-300 text-[11px] mt-1.5"
                      data-testid="earlier-alert-error"
                    >
                      {earlierAlertError}
                    </p>
                  )}
                  <p className="text-zinc-500 text-[10px] mt-2 leading-relaxed">
                    {isFr
                      ? "Aucune action requise si vous gardez votre heure actuelle."
                      : "No action needed if you're happy with your booked time."}
                  </p>
                </div>
              )}

              <p className="text-zinc-500 text-xs mt-4">
                {booking.success 
                  ? (isFr ? "Un courriel de confirmation a été envoyé." : "A confirmation email has been sent.")
                  : (isFr ? "Vous recevrez un courriel une fois confirmé." : "You will receive an email once confirmed.")}
              </p>

              {/* Afterpay 1-click opt-in — available on EVERY treatment.
                  Owner update (Jun 22, 2026): previously gated to total
                  ≥ $400; now always shown so clients can split any
                  treatment into 4 instalments via Square's Afterpay
                  integration. Only hide if total is somehow $0 (free
                  consult). */}
              {(() => {
                const priceNum = totalPriceCents / 100;
                if (priceNum < 1) return null;
                const perPay = (priceNum / 4).toFixed(2);
                if (afterpayOptIn === "done") {
                  return (
                    <div className="mt-5 mx-auto max-w-md bg-gradient-to-br from-emerald-900/60 to-emerald-950 border border-emerald-500/60 rounded-xl px-4 py-3 text-left" data-testid="afterpay-opt-in-done">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-emerald-300 text-lg">✓</span>
                        <span className="text-[10px] font-bold tracking-[3px] text-emerald-300">AFTERPAY SELECTED</span>
                      </div>
                      <p className="text-white text-sm leading-snug">
                        {isFr
                          ? <>C&apos;est noté. Karine vous enverra par courriel votre facture Square de <strong className="text-emerald-300">${priceNum.toFixed(2)}</strong> (4 versements de <strong className="text-emerald-300">${perPay}</strong>). Activez-la le jour de votre traitement.</>
                          : <>Got it. Karine will email you your Square invoice for <strong className="text-emerald-300">${priceNum.toFixed(2)}</strong> (4 instalments of <strong className="text-emerald-300">${perPay}</strong>). You&apos;ll activate it on the day of your treatment.</>}
                      </p>
                    </div>
                  );
                }
                return (
                  <button
                    type="button"
                    onClick={async () => {
                      if (afterpayOptIn === "busy") return;
                      const msg = isFr
                        ? `Souhaitez-vous procéder avec Afterpay comme mode de paiement ? Nous vous enverrons une facture par courriel et elle pourra être activée le jour de votre traitement.`
                        : `Would you like to proceed with Afterpay as your choice of payment? We'll email you the invoice and you'll activate it on the day of your treatment.`;
                      if (!window.confirm(msg)) return;
                      // Aug 18 2026 — Tina Hawley reported "there was an
                      // error" when opting into Afterpay. Root cause: the
                      // client's email/name state could be blank at this
                      // step (e.g. rebook flow or state reset after
                      // booking), causing HTTP 422 from the backend. Fix:
                      // pull from booking response as a safety fallback,
                      // and show an actionable message if we still can't
                      // find an email.
                      const emailOk = (customerInfo.email || booking?.customer?.email || "").trim();
                      if (!emailOk || !emailOk.includes("@")) {
                        toast.error(
                          isFr
                            ? "Nous n'avons pas votre courriel. Écrivez-nous au (613) 850-0805 et nous configurerons Afterpay pour vous."
                            : "We don't have your email on file. Text us at (613) 850-0805 and we'll set up Afterpay for you."
                        );
                        return;
                      }
                      setAfterpayOptIn("busy");
                      try {
                        const body = {
                          first_name: (customerInfo.first_name || booking?.customer?.first_name || "").trim() || "Client",
                          last_name: (customerInfo.last_name || booking?.customer?.last_name || "").trim() || "—",
                          email: emailOk,
                          phone: (customerInfo.phone || booking?.customer?.phone || "").trim() || "—",
                          treatment: (selectedServices || []).map((s) => s.name).join(" + ") || "Booked treatment",
                          price: priceNum,
                          location_id: String(selectedLocation?.id || "3"),
                          preferred_appointment_date: selectedDate?.date || selectedDate || "",
                          notes: `Auto-opted in from booking confirmation · time ${selectedTime || "?"} · booking_id ${booking?.booking_id || "?"}`,
                        };
                        const r = await fetch(`${API}/afterpay/request`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          body: JSON.stringify(body),
                        });
                        if (!r.ok) {
                          // Read the specific error so we can help the
                          // client instead of the old generic "couldn't save".
                          let detail = "";
                          try {
                            const err = await r.json();
                            detail = Array.isArray(err?.detail) ? err.detail.map(d => d.msg).join(", ") : (err?.detail || err?.message || "");
                          } catch { /* ignore */ }
                          throw new Error(detail || `HTTP ${r.status}`);
                        }
                        setAfterpayOptIn("done");
                        toast.success(isFr ? "Afterpay sélectionné — facture en route" : "Afterpay selected — invoice on its way");
                      } catch (e) {
                        setAfterpayOptIn(null);
                        // Show the actual error message so clients know
                        // what went wrong (e.g. missing email) instead of
                        // the old generic toast.
                        const errMsg = (e && e.message) ? e.message : "";
                        toast.error(
                          isFr
                            ? `Afterpay indisponible pour l'instant. Textez-nous au (613) 850-0805 et nous vous configurerons.${errMsg ? " · " + errMsg : ""}`
                            : `Afterpay isn't available right now. Text us at (613) 850-0805 and we'll set you up.${errMsg ? " · " + errMsg : ""}`
                        );
                      }
                    }}
                    className="mt-5 mx-auto max-w-md w-full block bg-gradient-to-br from-emerald-950/40 to-zinc-900 hover:from-emerald-900/60 hover:to-zinc-800 border border-emerald-700/40 hover:border-emerald-500/70 rounded-xl px-4 py-3 text-left transition-all cursor-pointer disabled:opacity-60"
                    disabled={afterpayOptIn === "busy"}
                    data-testid="afterpay-opt-in-button"
                  >
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[9px] font-bold tracking-[3px] text-emerald-400">PAY OVER 4 INSTALMENTS</span>
                        <span className="text-emerald-400">·</span>
                        <span className="text-[9px] font-bold tracking-[2px] text-emerald-300">AFTERPAY</span>
                      </div>
                      <span className="text-emerald-300 text-[10px] font-bold whitespace-nowrap">
                        {afterpayOptIn === "busy" ? (isFr ? "…" : "…") : (isFr ? "Choisir →" : "Choose →")}
                      </span>
                    </div>
                    <p className="text-white text-sm leading-snug">
                      {isFr
                        ? <>Payez en <strong className="text-emerald-300">4 versements</strong> de <strong className="text-emerald-300">${perPay}</strong> sans intérêt — directement avec Square.</>
                        : <>Pay in <strong className="text-emerald-300">4 instalments</strong> of <strong className="text-emerald-300">${perPay}</strong> — interest-free, directly through Square.</>}
                    </p>
                    <p className="text-emerald-300/80 text-[11px] mt-1.5 font-medium">
                      {isFr
                        ? "👆 Touchez pour choisir Afterpay — nous vous facturerons et vous pourrez l'activer le jour de votre traitement."
                        : "👆 Tap to choose Afterpay — we'll invoice you and you'll activate it on the day of your treatment."}
                    </p>
                  </button>
                );
              })()}

              <Button onClick={handleClose} className="mt-4 bg-amber-600 hover:bg-amber-500 text-white" data-testid="close-confirmation">
                {isFr ? "Fermer" : "Done"}
              </Button>

              {/* Support footer — gives the client a clear help path post-booking */}
              <div className="mt-5 mx-auto max-w-md bg-zinc-800/50 border border-zinc-700 rounded-xl p-3 text-left" data-testid="booking-support-footer">
                <p className="text-[10px] uppercase tracking-widest text-amber-400/80 font-bold mb-1.5">
                  {isFr ? "Besoin d'aide ?" : "Need help?"}
                </p>
                <p className="text-zinc-300 text-[11px] leading-relaxed mb-2">
                  {isFr
                    ? "Une question sur votre rendez-vous ? Écrivez-nous — nous répondons en moins d'une heure."
                    : "Question about your appointment? Email or message us — we typically reply within an hour."}
                </p>
                <div className="flex flex-wrap gap-2">
                  <SupportEmailButton
                    email="bookings@karinelauzon.com"
                    subject={isFr ? "Aide avec mon rendez-vous" : "Help with my appointment"}
                    data-testid="booking-support-email"
                    variant="amber"
                    className="px-2.5 py-1.5 rounded-lg text-[11px]"
                  />
                  <a
                    href="https://m.me/PreciseRNInjectionist" onClick={(e) => { e.preventDefault(); openContactModal({ source: "m-me-link" }); }}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-[#0084FF]/10 hover:bg-[#0084FF]/20 border border-[#0084FF]/30 text-[#67b6ff] text-[11px] font-semibold transition-colors"
                    data-testid="booking-support-messenger"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> Messenger
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Persistent Book-Direct strip — visible on every step so clients always
            have an escape hatch if our internal flow is slow or unavailable.
            Auto-expands when:
              • the dates fetch has been loading > 5s
              • OR there's an error
            so clients instantly see the Square backup buttons without having
            to find/tap the small toggle. Highlights the currently-selected
            clinic with a bright primary button so the most likely choice is
            one tap away. */}
        {step < 6 && (() => {
          // Auto-expand the green "Book on Square direct" panel only as
          // a true last-resort — at 28s, just before the 30s automatic
          // page-reload fires. With auto-recovery (snapshot reseed at 6s,
          // refresh at 12s, cache-bust at 22s, hard reload at 30s) running
          // silently in the background, this should almost never appear.
          const autoExpand = !!error || (loading && step <= 2 && loadingElapsed >= 28);
          const isExpanded = autoExpand || showFooterDirect;
          const stripClass = autoExpand
            ? "px-3 sm:px-4 pt-3 pb-3 border-t border-emerald-500/40 bg-gradient-to-r from-emerald-950/60 via-emerald-900/30 to-zinc-900/60"
            : "px-3 sm:px-4 pt-2.5 pb-1 border-t border-zinc-800 bg-zinc-900/60";
          const ctxLoc = selectedLocation?.id;
          return (
            <div className={stripClass} data-testid="book-direct-footer-strip">
              <button
                type="button"
                onClick={() => setShowFooterDirect(v => !v)}
                className="w-full flex items-center justify-between gap-2 text-left group"
                data-testid="book-direct-footer-toggle"
                aria-expanded={isExpanded}
              >
                <span className={`leading-snug ${autoExpand ? "text-sm sm:text-[15px] text-emerald-100" : "text-[11px] sm:text-xs text-zinc-400"}`}>
                  {autoExpand ? (
                    <>
                      <span className="text-emerald-300 font-bold block mb-0.5">
                        {isFr ? "Réservation immédiate" : "For immediate booking"}
                      </span>
                      <span className="text-emerald-100 font-medium text-xs sm:text-sm">
                        {isFr
                          ? "Essayez notre réservation Square directe ↓"
                          : "Try our Square booking direct ↓"}
                      </span>
                    </>
                  ) : (
                    <>
                      {isFr
                        ? "Vous rencontrez des problèmes ou ne trouvez pas d'heure disponible ?"
                        : "Having issues or can't find an available time?"}
                      {" "}
                      <span className="text-emerald-300 font-semibold group-hover:text-emerald-200 underline underline-offset-2 decoration-emerald-500/40">
                        {isFr ? "Réservez directement sur Square" : "Book direct on Square"}
                      </span>
                      {" "}
                      <ChevronRight className={`inline w-3 h-3 text-emerald-400 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                    </>
                  )}
                </span>
              </button>
              {isExpanded && (
                <div
                  className={autoExpand ? "mt-3 space-y-2" : "mt-2 grid grid-cols-3 gap-1.5"}
                  data-testid="book-direct-footer-links"
                >
                  {autoExpand ? (
                    // LARGE prominent buttons — one big primary for the selected clinic
                    // + two compact secondaries for the others.
                    <>
                      {ctxLoc && SQUARE_DIRECT_URLS[ctxLoc] && (
                        <a
                          href={SQUARE_DIRECT_URLS[ctxLoc]}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between gap-3 px-4 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-green-600 hover:from-emerald-400 hover:to-green-500 text-white font-bold text-base shadow-lg transition-all"
                          data-testid={`book-direct-footer-primary-${ctxLoc}`}
                        >
                          <span className="flex items-center gap-2">
                            <Calendar className="w-5 h-5" />
                            {isFr ? `Réserver sur Square — ${LOCATION_NAMES[ctxLoc]}` : `Book on Square — ${LOCATION_NAMES[ctxLoc]}`}
                          </span>
                          <ExternalLink className="w-5 h-5 flex-shrink-0" />
                        </a>
                      )}
                      <div className="grid grid-cols-3 gap-2">
                        {["1", "2", "3"].map((id) => (
                          <a
                            key={id}
                            href={SQUARE_DIRECT_URLS[id]}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center justify-center gap-1.5 px-2 py-2.5 rounded-lg border text-xs font-semibold transition-all ${
                              ctxLoc === id
                                ? "bg-emerald-500/10 border-emerald-500/60 text-emerald-200"
                                : "bg-zinc-800 hover:bg-zinc-700 border-zinc-700 hover:border-emerald-500/50 text-white"
                            }`}
                            data-testid={`book-direct-footer-${id}`}
                          >
                            <span className="truncate">{LOCATION_NAMES[id]}</span>
                            <ExternalLink className="w-3 h-3 text-emerald-400 flex-shrink-0" />
                          </a>
                        ))}
                      </div>
                    </>
                  ) : (
                    ["1", "2", "3"].map((id) => (
                      <a
                        key={id}
                        href={SQUARE_DIRECT_URLS[id]}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between gap-1.5 px-2 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 hover:border-amber-500/50 transition-all text-[10px] sm:text-[11px] text-white"
                        data-testid={`book-direct-footer-${id}`}
                      >
                        <span className="font-medium truncate">{LOCATION_NAMES[id]}</span>
                        <ExternalLink className="w-3 h-3 text-amber-400 flex-shrink-0" />
                      </a>
                    ))
                  )}
              </div>
            )}
          </div>
          );
        })()}

        {/* Footer Navigation */}
        {step < 6 && (
          <div className="flex items-center justify-between p-3 sm:p-4 border-t border-zinc-700 flex-shrink-0 bg-zinc-900 gap-2">
            <Button
              variant="ghost"
              onClick={() => step === 0 ? handleClose() : setStep(s => s - 1)}
              className="text-zinc-400 hover:text-white flex-shrink-0"
              data-testid="booking-back-btn"
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              {step === 0 ? (isFr ? "Annuler" : "Cancel") : (isFr ? "Retour" : "Back")}
            </Button>
            {/* Iter 453 · Feb 2026 · Karine ask (novice-friendly) — selection
                summary so users always know what will happen when they tap
                Next. Empty on step 1 shows a prompt; once services are
                picked, echoes the count + total duration + price. */}
            {step === 1 && (
              <div
                className="flex-1 min-w-0 text-center text-[11px] sm:text-xs leading-tight"
                data-testid="booking-selection-summary"
              >
                {selectedServices.length === 0 ? (
                  <span className="text-zinc-500">
                    {isFr ? "Sélectionnez un traitement pour continuer" : "Tap a service to continue"}
                  </span>
                ) : (
                  <>
                    <span className="text-amber-300 font-semibold">
                      {selectedServices.length === 1
                        ? (isFr ? "1 traitement" : "1 service")
                        : (isFr ? `${selectedServices.length} traitements` : `${selectedServices.length} services`)}
                    </span>
                    <span className="text-zinc-500"> · </span>
                    <span className="text-zinc-300">{formatDuration(totalDurationMinutes)}</span>
                  </>
                )}
              </div>
            )}
            <Button
              onClick={goNext}
              disabled={!canProceed() || loading}
              className="bg-amber-600 hover:bg-amber-500 text-white disabled:opacity-40 flex-shrink-0"
              data-testid="booking-next-btn"
            >
              {loading ? (
                <><Loader2 className="w-4 h-4 mr-1 animate-spin" /> {step === 5 ? (isFr ? "Réservation..." : "Booking...") : (isFr ? "Chargement..." : "Loading...")}</>
              ) : step === 5 ? (
                // Iter 409 · Layer 3 — location echoed into the button
                // so users glance at it one more time. Reads e.g.
                // "Book at Ottawa →" instead of just "Book Appointment".
                <>
                  {(() => {
                    const name = LOCATION_NAMES[String(selectedLocation?.id ?? selectedLocation ?? "")] || selectedLocation?.name || (isFr ? "cette clinique" : "this clinic");
                    return isFr ? `Réserver à ${name}` : `Book at ${name}`;
                  })()} <Check className="w-4 h-4 ml-1" />
                </>
              ) : step === 4 ? (
                <>{isFr ? "Vérifier" : "Review"} <ChevronRight className="w-4 h-4 ml-1" /></>
              ) : (
                <>{isFr ? "Suivant" : "Next"} <ChevronRight className="w-4 h-4 ml-1" /></>
              )}
            </Button>
          </div>
        )}

        {/* Iter 409 · Layer-1 Location Confirmation Modal — Marc's request
            to make wrong-clinic bookings almost impossible. Full-screen
            takeover so users can't scroll past or press Enter through. */}
        {showLocationConfirm && (() => { const _confirmLocId = String(selectedLocation?.id ?? selectedLocation ?? ""); return (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
            data-testid="booking-confirm-location-modal"
          >
            <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border-2 border-amber-500 overflow-hidden">
              <div className="bg-gradient-to-br from-amber-500 via-amber-400 to-amber-500 px-6 py-4 text-center">
                <p className="text-[10px] uppercase tracking-[0.3em] text-amber-900 font-bold">
                  {isFr ? "Confirmez avant de réserver" : "Confirm Before Booking"}
                </p>
                <p className="font-serif text-lg text-neutral-900 mt-1">
                  {isFr ? "Est-ce la bonne clinique ?" : "Is this the right clinic?"}
                </p>
              </div>
              <div className="p-6 text-center">
                <div className="w-14 h-14 rounded-full bg-amber-100 border-2 border-amber-400 flex items-center justify-center mx-auto mb-4">
                  <MapPin className="w-7 h-7 text-amber-700" />
                </div>
                <p className="text-[11px] uppercase tracking-[0.25em] text-amber-700 font-bold mb-1">
                  {isFr ? "Vous réservez à" : "You are booking at"}
                </p>
                <p
                  className="font-serif text-3xl text-neutral-900 mb-1"
                  data-testid="booking-confirm-location-name"
                >
                  {LOCATION_NAMES[_confirmLocId] || selectedLocation?.name || "—"}
                </p>
                <p className="text-sm text-neutral-500 leading-snug mb-5">
                  {LOCATION_ADDRESSES[_confirmLocId] || ""}
                </p>
                {selectedDate && selectedTime && (
                  <div className="bg-neutral-50 rounded-lg border border-neutral-200 py-3 px-4 mb-5 text-left">
                    <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 font-semibold mb-1">
                      {isFr ? "Créneau" : "Appointment"}
                    </p>
                    <p className="text-sm text-neutral-900 font-medium" data-testid="booking-confirm-slot">
                      {new Date(String(selectedDate?.date || selectedDate).slice(0, 10) + "T00:00:00").toLocaleDateString(isFr ? "fr-CA" : "en-CA", { weekday: "long", month: "long", day: "numeric" })}
                      &nbsp;·&nbsp;{selectedTime}
                    </p>
                  </div>
                )}
                <button
                  onClick={() => { setShowLocationConfirm(false); handleBook(); }}
                  disabled={loading}
                  data-testid="booking-confirm-yes-btn"
                  className="w-full py-3.5 rounded-xl bg-neutral-900 hover:bg-neutral-800 text-white font-bold text-sm tracking-wide disabled:opacity-50 mb-3"
                >
                  {loading
                    ? (isFr ? "Réservation…" : "Booking…")
                    : (isFr ? `Oui, réserver à ${LOCATION_NAMES[_confirmLocId] || selectedLocation?.name || ""}` : `Yes, book at ${LOCATION_NAMES[_confirmLocId] || selectedLocation?.name || ""}`)}
                </button>
                <button
                  onClick={() => { setShowLocationConfirm(false); setStep(0); }}
                  disabled={loading}
                  data-testid="booking-confirm-change-btn"
                  className="w-full py-3 rounded-xl bg-white text-neutral-700 border border-neutral-300 hover:bg-neutral-50 font-semibold text-sm"
                >
                  ← {isFr ? "Changer de clinique" : "Change location"}
                </button>
                <p className="text-[11px] text-neutral-400 mt-4 leading-snug">
                  {isFr
                    ? "Un rendez-vous réservé dans la mauvaise clinique doit être re-réservé — nous ne pouvons pas déplacer un rendez-vous d'une clinique à une autre."
                    : "A booking at the wrong clinic must be re-booked — we can't move an appointment between clinics."}
                </p>
              </div>
            </div>
          </div>
        ); })()}
      </div>
    </div>
  );
};

export default InlineBookingFlow;
